import "../styles.css"
import "@phosphor-icons/web/regular"
import "maplibre-gl/dist/maplibre-gl.css"
import "./map.css"
import maplibregl from "maplibre-gl"
import van from "vanjs-core"
import {
	build_print_html,
	type PrintLegendItem,
	escape_html,
	legend_kind,
	nice_scale,
	shade_color,
	swatch_img,
	swatch_svg,
	hatch_paths,
} from "../lib/print"
import { distance_m } from "../lib/geodesy"
import { public_geojson_url, use_vector_tiles } from "../lib/layer_transport"
// 1.5 coordonate Stereo 70 (lib PURĂ, contract plan/contracts/stereo70.md — conversia se face în browser)
import { wgs84_to_stereo70, format_stereo70, stereo70_in_bounds } from "../lib/stereo70"
// 1.3 fundaluri configurabile (contract basemaps.md), 2.2 filtre salvate (filtre_salvate.md),
// 1.6 liste de valori (liste_valori.md) — toate lib-uri PURE partajate server↔client (anti-drift).
import { basemap_tile_url, type Basemap } from "../lib/basemaps"
import { filter_matches, saved_filter_label, type SavedFilter } from "../lib/filters"
import { GeometryCache, index_by_id } from "../lib/snap_geometry"
import { allowed_values, value_list_problem, type ListValue } from "../lib/attribute_schema"
import * as vanX from "vanjs-ext"
import { get_hash_query_params } from "../lib/router.client"
import { set_workspace, workspace } from "../lib/workspace.client"
import { api_delete, api_get, api_patch, api_post, api_upload } from "../lib/api.client"
import { SYSTEM_FEATURE_KEYS, PHOTOS_KEY, parse_photos, photo_uuid } from "../lib/collect"
import { slugify, slug_key } from "../lib/slug"
import {
	effective_style,
	is_hidden_symbol,
	hatch_layer_spec,
	hatch_angle_q,
	hatch_tile_px,
	type Hatch,
	layer_color,
	LAYER_COLORS,
	LABEL_HALO_COLOR,
	LABEL_HALO_PX,
	POINT_RADIUS_PX,
	DASH_ARRAY,
	AREA_KEY,
	area_sort_key,
	label_min_zoom,
	paint_values,
	type EffectiveCategory,
} from "../lib/layer_style"
// Editare pe hartă (F13-T4): manager Terra Draw vendorat din vlib — doar intern, editor+.
import {
	create_draw_manager,
	draw_modes_for_geometry,
	rewrap_geometry,
	unwrap_for_edit,
	wrap_geometry_for_layer,
	type DrawManager,
	type DrawMode,
	type DrawnFeature,
	type SnapQuery,
} from "./draw.client"
import { is_dark } from "../lib/theme.client"
import {
	install_available,
	prompt_install,
	PLAY_STORE_URL,
	is_android,
	is_standalone,
} from "../lib/pwa.client"
import { fuzzy_match } from "../lib/fuzzy"
import { mark_perf } from "../lib/observe.client"
import {
	session,
	session_loaded,
	session_org_feature,
	load_session,
	user_label,
	user_initials,
	logout,
} from "../lib/session.client"
import { path_length_m, polygon_area_m2, format_distance, format_area } from "../lib/geodesy"
import {
	parse_style3d,
	compute_heights3d,
	prepare_utr_index,
	rh_rows_from_metadata,
	DEFAULT_HEIGHT_M,
	type Style3d,
	type UtrIndexEntry,
} from "../lib/style3d"
import { sun_position, sun_to_light_position } from "../lib/sun"
import { author_label, is_mcp_agent } from "../lib/mcp_agent"
import pkg from "../../package.json"
import type { HistoryEvent } from "../features"
import type { SearchMatch, IdentifyResult } from "../search"
import type { Regim } from "../regim"
import type { PublicCaps, FeatureFlag } from "../lib/entitlements"
import { parse_api_links, expand_url_template, anchor_point, type ApiLink } from "../lib/api_links"
import type { GeocodeHit } from "../geocode"

const t = van.tags

/** Icon Phosphor inline — <i class="ph ph-<nume>"> (fontul vine din @phosphor-icons/web). */
function icon(name: string, cls = "") {
	return t.i({ class: `ph ph-${name} ${cls}` })
}

// ─────────────────────────────────────────────────────────────
// Context de montare: aceleași componente servesc harta INTERNĂ (back-office) și cea PUBLICĂ (embed).
// `is_public` comută sursa de date pe /api/public/* (token în loc de gardă de tenant); `caps` ascund
// editarea, generarea de documente, selectorul de hartă. Diferența publică = DOAR edit + documente.
// ─────────────────────────────────────────────────────────────
interface MapCaps {
	documents: boolean // secțiunea Documente + butoane de generare (doar intern)
	workspace: boolean // MapSelector (alegere hartă); public = o singură hartă (din token)
	as_of: boolean // input datetime „as-of" (istoric)
	search: boolean // căutare după atribut + identify
	ticket: boolean // CTA „Fă o solicitare" + pin/GPS + „Sesizare despre acest element" (doar public)
	regim: boolean // mod urbanism (F5-T2): click → regim UTR; public prin /api/public/regim (F6-T2)
	// Instrumente restrângibile per EMBED (F7-T3) — intern mereu true; pe un token `emb_` vin
	// din config-ul embed-ului (via `PublicMapView.tools`, aplicat în mount_public_map).
	measure: boolean // măsurare distanță/suprafață + desen liber
	legend: boolean // legenda straturilor vizibile
	basemap: boolean // comutarea Hartă/Satelit
}
interface MapCtx {
	is_public: boolean
	token: string // tokenul public (?k=) — gol pentru intern
	caps: MapCaps
	ticket_org: string // slug-ul primăriei (pentru butoanele de sesizare)
	ticket_map: string // id-ul hărții (pentru sesizare)
	// F7-T3: blocarea navigării la un extent ([w,s,e,n] → maxBounds), doar pe embed-uri cu bbox_lock.
	bbox_lock: [number, number, number, number] | null
}
const CTX: MapCtx = {
	is_public: false,
	token: "",
	ticket_org: "",
	ticket_map: "",
	bbox_lock: null,
	caps: {
		documents: true,
		workspace: true,
		as_of: true,
		search: true,
		ticket: false,
		regim: true,
		measure: true,
		legend: true,
		basemap: true,
	},
}

// Forma întoarsă de GET /api/public/map (adaptată la MapDetail în load_map pentru modul public).
// ⚠️ Adaptorul din load_map copiază CÂMP CU CÂMP — un câmp nou pe server care nu e preluat
// acolo dispare TĂCUT în embed (așa s-au pierdut inițial terrain + utr_rh la F10-T5).
interface PublicMapView {
	slug: string
	name: string
	extent: [number, number, number, number] | null
	layers: LayerSummary[]
	org_slug: string
	org_name: string
	map_id: string
	stema_url: string // branding primărie (F6-T1): "" = fără stemă → brandul platformei
	terrain?: { url: string; encoding: string } // sursa DEM a instanței (F10-T3)
	utr_rh?: Record<string, string> // cod UTR → rh_max, fail-closed pe server (F10-T5)
	// Capabilitățile viewerului din contractul org-ului (feature gating) — absent pe
	// răspunsuri vechi/cache → tratat permisiv (serverul e poarta reală).
	caps?: Partial<PublicCaps>
	// F7-T3: instrumentele embed-ului (absent = toate pornite) + blocarea la extent. Consumate în
	// mount_public_map (embed.client le pasează din pre-fetch, ÎNAINTE de montarea toolbar-ului) —
	// NU prin adaptorul din load_map (toolbar-ul e deja randat atunci).
	tools?: Partial<Record<string, boolean>>
	bbox_lock?: [number, number, number, number] | null
	// F7-T4: linkuri externe cu placeholders (butoane în FeaturePanel) — expuse țintit de server.
	api_links?: ApiLink[]
	// 1.3: fundalurile configurate pe hartă (paritate intern ↔ /embed; contract basemaps.md)
	basemaps?: Basemap[]
}

// ── Ticketing public (IT-PORTAL-57): captură ANONIMĂ în embed → depunere AUTENTIFICATĂ în app.
// Podul e URL-ul: harta + opțional feature_ref + pin (lng/lat) trec prin query, iar formularul
// autentificat le pre-completează (revalidate pe server). Doar în modul public (caps.ticket).
let pin_marker: maplibregl.Marker | null = null
let pin_source = "pin" // sursa locației pinului în așteptare (pin/gps) — trece în ticket la confirmare
// Context suplimentar purtat de pinul în așteptare (F6-T6: observația „cu locație pe hartă"
// pornită din bannerul de consultare) — golit la confirmare/anulare.
let pin_extra: { tip_act?: string; subject?: string; category?: string } = {}

/** Activează/dezactivează modul pin de sesizare (cursor + stare reactivă). */
function set_pin_mode(on: boolean): void {
	STATE.pin_active = on
	// Reset DOAR la intrare: pinul obișnuit din TicketBar pornește curat, iar bannerul de consultare
	// își setează preset-ul DUPĂ apel. La IEȘIRE nu ștergem nimic — clickul pe hartă face
	// set_pin_mode(false) + place_pending_pin, iar preset-ul trebuie să supraviețuiască până la
	// confirm_pin (altfel observația „cu locație" ar pleca drept sesizare simplă).
	if (on) {
		pin_extra = {}
	}
	if (on && STATE.measure_mode) {
		set_measure_mode("") // modurile se exclud: un singur consumator de click pe hartă
	}
	if (on && STATE.regim_mode) {
		set_regim_mode(false)
	}
	if (on && STATE.edit_mode) {
		set_edit_mode(false)
	}
	if (MAP) {
		MAP.getCanvas().style.cursor = on ? "crosshair" : ""
	}
}

/**
 * Plasează un pin TRAS-ABIL la (lng,lat) și intră în starea „așteaptă confirmarea locației" — persoana
 * poate trage pinul ca să ajusteze, apoi confirmă. La confirmare deschide formularul cu coordonata FINALĂ.
 */
function place_pending_pin(lng: number, lat: number, source: string): void {
	if (!MAP) {
		return
	}
	pin_source = source
	pin_marker?.remove()
	pin_marker = new maplibregl.Marker({ color: "#0e7490", draggable: true })
		.setLngLat([lng, lat])
		.addTo(MAP)
	STATE.pending_pin = true
}

/** Confirmă locația pinului → deschide formularul de sesizare cu coordonata curentă a pinului. */
function confirm_pin(): void {
	if (!pin_marker) {
		return
	}
	const ll = pin_marker.getLngLat()
	STATE.pending_pin = false
	pin_marker.remove()
	pin_marker = null
	const extra = pin_extra
	pin_extra = {}
	open_ticket({ lng: ll.lng, lat: ll.lat, source: pin_source, ...extra })
}

/** Anulează pinul în așteptare (îl scoate de pe hartă). */
function cancel_pin(): void {
	pin_marker?.remove()
	pin_marker = null
	pin_extra = {}
	STATE.pending_pin = false
}

/** Deschide formularul de solicitare din app (necesită cont), pre-completat cu harta + feature/pin. */
function open_ticket(extra: {
	feature_ref?: string
	lng?: number
	lat?: number
	source?: string
	tip_act?: string // preset (ex. observatie_consultare din bannerul de consultare, F6-T6)
	subject?: string // preset editabil (ex. numele consultării țintă)
	category?: string // preset mașinal-filtrabil (ex. `consultare:<layer_slug>`)
}): void {
	if (!CTX.ticket_org) {
		return
	}
	const p = new URLSearchParams({ new: "1", org: CTX.ticket_org, map_id: CTX.ticket_map })
	if (extra.feature_ref) {
		p.set("feature_ref", extra.feature_ref)
	}
	if (extra.tip_act) {
		p.set("tip_act", extra.tip_act)
	}
	if (extra.subject) {
		p.set("subject", extra.subject)
	}
	if (extra.category) {
		p.set("category", extra.category)
	}
	if (typeof extra.lng === "number" && typeof extra.lat === "number") {
		p.set("lng", extra.lng.toFixed(6))
		p.set("lat", extra.lat.toFixed(6))
		p.set("location_source", extra.source ?? "pin")
	}
	window.open(`${location.origin}/#/solicitari?${p.toString()}`, "_blank", "noopener")
}

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface MapSummary {
	id: string
	slug: string
	name: string
	description: string
}

interface LayerSummary {
	slug: string
	name: string
	geometry_type: string
	feature_count: number
	geom_bytes?: number // octeți de geometrie pe disc, doar pe straturile mici (lib/layer_transport)
	// 09.10: + dash, label_size, label_color, categories (stilul pe valoare) — citite prin effective_style
	style?: {
		color?: string
		opacity?: number
		stroke?: string
		width?: number
		label?: string
		dash?: boolean
		label_size?: number
		label_color?: string
		categories?: unknown
		sort?: string // "area" = poligoanele mari dedesubt (ordinea de desenare din QGIS)
		label_scale_max?: number // eticheta doar sub scara 1:N (QGIS scaleMax)
	}
	style3d?: Record<string, unknown> // config extrudare 3D (F10-T1) — normalizat de server
	role?: string // 'utr' | 'derogare' | "" — legenda de urbanism (REQ-URB-001)
	allowed_templates?: string[] // șabloane permise pe features-ul stratului (F13)
	consultare?: boolean // strat în consultare publică (F6-T6) — banner + observații în /embed
	consultare_pana_la?: string // YYYY-MM-DD; "" = fără termen
	// 2.2: filtrele salvate ale stratului (rânduri „strat derivat"); public = doar cele compatibile F10
	saved_filters?: SavedFilter[]
	// 1.4: iconița stratului punctual ("" = simbolul implicit, cerc) — URL public, fără token
	icon_url?: string
	// 18.09: poziția ISTORICĂ a stratului (ordinea de creare), din care se alege culoarea implicită —
	// ca reordonarea făcută de primărie să NU repicteze harta. Opțional: un răspuns vechi din cache-ul
	// PWA n-o are, iar atunci `layer_color` cade pe poziția din listă, ca înainte.
	color_index?: number
	// 18.09: starea implicită la deschiderea hărții (absent = aprins).
	visible_default?: boolean
}

interface MapDetail {
	id: string
	slug: string
	org_slug?: string // primăria hărții ("" = neasignată) — puntea regim → solicitare (F5-T5)
	config: Record<string, unknown>
	metadata: Record<string, unknown>
	extent: [number, number, number, number] | null // bbox 4326 al datelor (autozoom)
	layers: LayerSummary[]
	// Teren 3D (F10-T3): sursa DEM a instanței (env server); url gol/absent = fără teren.
	// Prezent și în răspunsul public (/api/public/map) — paritate intern/embed.
	terrain?: { url: string; encoding: string }
	// Doar pe calea PUBLICĂ (F10-T5): lookup cod UTR → rh_max (fail-closed pe server — populat
	// doar cu stratul UTR publicat). Intern, sursa e metadata.rlu întreagă.
	utr_rh?: Record<string, string>
	// Doar pe calea PUBLICĂ: capabilitățile viewerului din contractul org-ului (public.ts `caps`)
	// — butoanele negate aici NU se montează; refuzul real rămâne pe server (404).
	public_caps?: Partial<PublicCaps>
	// Intern: EAV-ul hărții (get_map îl întoarce) — sursa `api_links` (F7-T4).
	attributes?: Record<string, unknown>
	// Public: linkurile externe gata parsate de server (EAV-ul hărții NU se expune întreg).
	api_links?: ApiLink[]
	// 1.3: fundalurile configurate (gol/absent = perechea implicită Hartă/Satelit)
	basemaps?: Basemap[]
}

interface FeatureDetail {
	type: "Feature"
	id: string
	geometry: GeoJSON.Geometry
	properties: Record<string, unknown>
	metadata: Record<string, unknown>
	version: number
	layer_slug?: string // stratul părinte (calea publică) — pentru feature_ref-ul sesizării
}

// Documentele legate de entitatea selectată (subset din DocumentSummary).
interface FeatureDocument {
	id: string
	name: string
	filename: string
	registry_code: string
	status: "emis" | "anulat"
	created_at: string
}

// Șabloanele disponibile — butoane de generare pe feature-ul selectat.
interface TemplateRef {
	slug: string
	name: string
}

// Tipul evenimentelor de istoric vine de pe server (uniune discriminată pe `kind`:
// versiuni de geometrie + schimbări de atribute). DOAR `import type` — se șterge la
// build (verbatimModuleSyntax), deci Elysia nu ajunge în bundle-ul de browser.

// ─────────────────────────────────────────────────────────────
// Global Reactive State
// ─────────────────────────────────────────────────────────────

const STATE = vanX.reactive({
	// maps list
	maps: [] as MapSummary[],
	maps_loading: false,
	maps_error: "",

	// selected map
	selected_map_slug: "",
	map_detail: null as MapDetail | null,
	map_loading: false,
	map_error: "",

	// layer visibility: keyed by layer slug -> boolean
	layer_visible: {} as Record<string, boolean>,

	// selected feature
	selected_feature_id: "",
	feature_detail: null as FeatureDetail | null,
	feature_loading: false,
	feature_error: "",
	feature_history: [] as HistoryEvent[],
	feature_history_loading: false,
	feature_documents: [] as FeatureDocument[],
	feature_documents_loading: false,
	templates: [] as TemplateRef[], // pentru butoanele „generează document" din panou

	// fundal: "harta" (OSM) | "satelit" (Esri) | "bm:<i>" = al i-lea fundal CONFIGURAT pe hartă (1.3);
	// când harta are `basemaps`, butoanele configurate înlocuiesc perechea fixă (primul = implicit)
	basemap: "harta" as string,
	// 1.5 coordonate: poziția afișată în bara de coordonate (cursorul; pe touch = centrul hărții)
	coord_lng: Number.NaN,
	coord_lat: Number.NaN,
	// 3.4 proximitate: raza (m) pentru identify/căutare; 0 = oprit (intersecție strictă)
	radius_m: 0,
	// 1.2 zonificare actuală: stratul VIRTUAL (PUG decupat de PUZ-uri) — încărcat la aprindere
	zonificare_visible: false,
	zonificare_loading: false,
	zonificare_error: "",
	zonificare_derogari: -1, // nr. de derogări din `sources` (-1 = neîncărcat încă)
	zonificare_utr: "", // slug-ul stratului UTR sursă (din `sources.utr`)
	// 2.2 filtre salvate: vizibilitatea rândurilor „strat derivat", cheie `<strat>::<filtru>`
	filter_visible: {} as Record<string, boolean>,
	// 4.5 snapping (contract snapping.md): lipirea la obiecte în editor + „și la straturile vizibile"
	edit_snapping: true,
	edit_snap_all: false,
	// vedere 3D: extrudarea straturilor de clădiri (F10-T1) — per sesiune de hartă
	mode_3d: false,
	// teren 3D (F10-T3): relief din tile-uri DEM — doar când instanța are TERRAIN_TILES_URL
	terrain_on: false,
	// însorire (F10-T4, REQ-M3D-009): soarele la (dată, oră) mișcă umbrirea fațadelor (light.position)
	sun_active: false,
	sun_date: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`,
	sun_hour: 12, // ora locală, fracționar (12.5 = 12:30)
	sun_readout: "",
	// măsurare/desen (REQ-PEX-004): "" = oprit; „draw" = desen liber TEMPORAR (formele se adună
	// în `drawn_features`, client-only — nimic pe server); punctele stau în `measure_points`,
	// aici doar ce se randează — modul activ + rezultatul formatat.
	measure_mode: "" as "" | "distance" | "area" | "draw",
	measure_readout: "",
	sidebar_open:
		typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true,

	// as-of
	as_of: "",

	// ticketing public: numele primăriei + disponibilitatea butoanelor (setate după load_map public)
	ticket_available: false,
	// bannerul de consultare: colapsat manual la un cip (per sesiune de pagină — la o vizită
	// nouă reapare expandat, vizibilitatea consultării rămâne regula)
	consultare_collapsed: false,
	org_name: "",
	org_stema: "", // URL-ul stemei primăriei (branding /embed, F6-T1); "" = brandul platformei
	public_docs: [] as PublicDoc[], // documentația PUG publică (F6) — doar în /embed
	pin_active: false, // modul „alege un punct pe hartă pentru sesizare"
	pending_pin: false, // un pin (tras-abil) e plasat și așteaptă confirmarea locației

	// căutare după atribute (nr. cadastral) + interogare spațială (identify)
	// căutare după adresă (F6-T1, REQ-PEX-001): Enter → /api/geocode → listă → zoom + marker.
	// Agnostic de provider (serverul decide Nominatim/PositionStack).
	addr_q: "",
	addr_loading: false,
	addr_error: "",
	addr_results: [] as GeocodeHit[],

	search_q: "",
	search_loading: false,
	search_results: [] as SearchMatch[],
	search_error: "",
	identify_loading: false,
	identify_result: null as IdentifyResult | null,

	// Filtru pe NUME de strat (F13-T2): filtrează DOAR lista din sidebar, nu vizibilitatea
	// pe hartă. Fuzzy pe subsecvențe + diacritice normalizate (lib/fuzzy).
	layer_q: "",
	// Drawer de straturi (F13 redesign): COLLAPSED = straturile ca iconițe; expanded = listă completă.
	sidebar_collapsed:
		typeof localStorage !== "undefined" &&
		localStorage.getItem("urbanism_sidebar_collapsed") === "1",
	// Căutare UNIFICATĂ (deschisă în drawer-ul extins): modul + opțiuni avansate.
	search_open: false,
	search_mode: "both" as "both" | "attr" | "addr",
	search_advanced: false,
	// Legendă pe hartă (F13): afișează straturile VIZIBILE cu simbol + nume.
	legend_open: false,

	// regim urbanistic (F5-T2): mod on-click → GET /regim → panoul lateral de reglementări
	regim_mode: false,
	regim_loading: false,
	regim: null as Regim | null,
	regim_error: "", // mesajul serverului la fail (public: „indisponibil" e o stare legitimă, F6-T2)

	// overlay „acte" (F5-T9 → N3): stratul virtual al documentelor hărții, colorat pe STARE (doar intern)
	acte_visible: false,
	// overlay „solicitări" (F13): TOATE cererile cu locație, tematic pe STATUS (doar intern)
	solicitari_visible: false,
	identify_mode: false, // click pe hartă = identify (toate layerele de sub punct)

	// editare pe hartă (F13-T4): mod DOAR intern, editor+ pe org-ul hărții. Geometria în lucru
	// stă în managerul Terra Draw (modul draw.client) — în STATE doar ce se randează.
	edit_mode: false, // panoul de editare e deschis
	edit_layer_slug: "", // stratul țintă pentru elemente noi
	edit_draw_active: "" as "" | DrawMode, // modul de desen activ ("" = niciunul)
	edit_schema: [] as { key: string; type: string; values: ListValue[] | null }[], // attribute_schema a stratului țintă (+ lista de valori, 1.6)
	edit_schema_loading: false,
	edit_pending: false, // o schiță e terminată → formularul de atribute e deschis
	edit_geom_feature: "", // id-ul feature-ului EXISTENT a cărui geometrie e în editor
	edit_geom_dirty: false, // geometria din editor diferă de cea salvată
	edit_saving: false,
	edit_error: "",
	edit_notice: "",
	// Notiță TRANZITORIE pe hartă (ex. după ștergerea unui obiect cu acte ancorate): panoul obiectului
	// se închide la ștergere, deci mesajul n-are unde apărea altfel.
	map_notice: "",
	// captură GPS din teren (F9-T2): schița curentă vine de la buton, cu precizia raportată
	edit_gps_pending: false, // „Se localizează…"
	edit_gps_accuracy: 0, // ± metri (0 = schița NU e din GPS → fără amprente la salvare)
	// F9-T5: formularul „Strat nou de colectare" din panoul de editare
	edit_new_layer_open: false,
	edit_new_layer_name: "", // citit DOAR de preview-ul de slug (nested) — nu re-randează formularul
	edit_new_layer_geom: "Point",
	edit_new_layer_rows: 1, // câte rânduri de câmpuri se randează (valorile stau în modul)
	edit_new_layer_busy: false,
	edit_new_layer_error: "",
	// poze de teren (F9-T3) pe feature-ul selectat
	feature_photo_busy: false,
	feature_photo_error: "",
	// ștergerea e în spatele unui mod explicit („Gestionează") — ✕ pe thumbnail era prea ușor
	// de atins din greșeală pe mobil (constatare 26.08)
	feature_photo_manage: false,
	// FeaturePanel: editarea atributelor entității selectate (formular pe cheile existente)
	feature_edit_open: false,
	feature_edit_saving: false,
	feature_edit_error: "",
	feature_edit_rev: 0, // bumped la „Adaugă atribut" — valorile stau în modul, re-randarea vine de aici

	// tabel de atribute (drawer jos)
	table_layer_slug: "",
	// Stratul feature-ului selectat (F13) — pentru a filtra șabloanele de generat pe strat.
	selected_feature_layer: "",
	table_layer_name: "",
	// Rândurile tabelului: DOAR atribute (fără contur — vezi load_table/attributes_only).
	table_features: [] as { id: string; properties: Record<string, unknown> }[],
	table_columns: [] as string[],
	table_loading: false,
	table_error: "",
	table_truncated: false,
	// Straturi desenate PARȚIAL (serverul a semnalat `truncated`) — se arată în listă, ca să nu
	// existe niciodată date trunchiate care par complete.
	layer_truncated: {} as Record<string, boolean>,
})

// Inițializarea `sidebar_open` de mai sus e un snapshot la ÎNCĂRCAREA modulului — rotirea
// telefonului sau redimensionarea peste/sub 768px n-o re-evalua niciodată. Listener-ul trăiește
// cât pagina (modulul se încarcă o dată), la fel ca cei de document din UserMenu.
// După ce userul a umblat EXPLICIT la sidebar (butonul „Straturi"), trecerea de breakpoint
// doar ÎNCHIDE la intrarea pe mobil (overlay-ul peste hartă nu are ce căuta deschis nechemat);
// nu mai forțează redeschiderea pe desktop peste alegerea lui (constatare review 24.08).
let sidebar_user_toggled = false
function toggle_sidebar_open(): void {
	sidebar_user_toggled = true
	STATE.sidebar_open = !STATE.sidebar_open
}
if (typeof window !== "undefined") {
	window.matchMedia("(min-width: 768px)").addEventListener("change", (e) => {
		if (!e.matches) {
			STATE.sidebar_open = false
		} else if (!sidebar_user_toggled) {
			STATE.sidebar_open = true
		}
	})
}

const TABLE_LIMIT = 2000
// GeoJSON integral sau tile-uri vectoriale? Regula e în lib/layer_transport (`use_vector_tiles`),
// PARTAJATĂ cu serverul: peste MVT_THRESHOLD obiecte → tile-uri pe AMBELE hărți (regula istorică);
// peste MVT_GEOM_BYTES de geometrie pe disc → tile-uri DOAR pe harta publică (vezi comentariul din
// lib/layer_transport: tile-urile cuantizează geometria, iar funcționarul se lipește de ea când
// desenează — cetățeanul nu editează niciodată). Cu `as_of` rămânem pe GeoJSON: ruta de tile-uri
// n-are suport temporal. Straturile MVT primesc înălțimile 3D rezolvate de server (heights3d) și
// filtrele prin `?filter=`, iar editarea geometriei unui element o ia din API, nu din tile.

// ─────────────────────────────────────────────────────────────
// Vedere 3D (F10-T1): extrudare dinamică din date 2D, configurată per layer
// prin `config.style3d` (lib/style3d — parser regim, rezoluție înălțime, join UTR).
// ─────────────────────────────────────────────────────────────

/** Config-ul 3D al stratului (normalizat de server; parse din nou = ieftin și defensiv). */
function layer_style3d(layer: LayerSummary): Style3d {
	return parse_style3d(layer.style3d)
}

/**
 * Nucleul gating-ului de UI pe contract (PLAN_FEATURES_CONTRACT.md): modulul `flag` e pornit pe
 * org-ul hărții curente? Public: semnalul explicit din `public_caps[cap]` (absent pe răspunsuri
 * vechi → permisiv); `cap` nedeclarat = modulul nu există public deloc (ex. colectarea). Intern:
 * flag-ul org-ului din sesiune (org străin/necunoscut → permisiv — ascundem doar ce ȘTIM oprit;
 * refuzul real e pe server).
 */
function org_module_available(flag: FeatureFlag, cap?: keyof PublicCaps): boolean {
	const d = STATE.map_detail
	if (!d) {
		return false
	}
	if (CTX.is_public) {
		return cap !== undefined && d.public_caps?.[cap] !== false
	}
	return !d.org_slug || session_org_feature(d.org_slug, flag)
}

/** 3D-ul e disponibil pe harta curentă? */
function map_3d_available(): boolean {
	return org_module_available("model_3d", "view3d")
}

/** Faza 2: modulul de regim (urb_regim) e în contractul org-ului hărții? */
function regim_available(): boolean {
	return CTX.caps.regim && org_module_available("urb_regim", "regim")
}

/** Faza 2: colectarea din teren (app_mobila) — DOAR intern (fără cap public). */
function teren_available(): boolean {
	return org_module_available("app_mobila")
}

/** Faza 2: rapoartele (stratul virtual „acte") sunt în contractul org-ului hărții? */
function rapoarte_available(): boolean {
	return org_module_available("rapoarte")
}

/**
 * Stratul se extrudează în modul 3D? Config-ul explicit câștigă (`style3d.enabled`);
 * fără config → heuristica istorică: doar straturile de clădiri (nu zonificări/parcele).
 */
function extrudes_3d(layer: LayerSummary): boolean {
	const enabled = layer_style3d(layer).enabled
	if (typeof enabled === "boolean") {
		return enabled
	}
	const gt = layer.geometry_type?.toLowerCase() ?? ""
	return gt.includes("polygon") && /cladir|fond_construit|building/.test(layer.slug)
}

/**
 * Contextul join-ului dinamic clădire→UTR (REQ-M3D-001): poligoanele UTR indexate + rândurile
 * de indicatori din metadata.rlu. Se (re)construiește la fiecare intrare în modul 3D — vezi
 * ensure_utr_ctx_3d; null = harta n-are strat UTR / RLU (join-ul degradează la fallback).
 */
interface UtrCtx3d {
	index: UtrIndexEntry[]
	rows: Record<string, unknown>[]
}
let utr_ctx_3d: UtrCtx3d | null = null

/**
 * Încarcă (dacă e nevoie) stratul UTR + indicatorii pentru join-ul 3D. Folosește cache-ul
 * GeoJSON al hărții când stratul e deja montat; altfel îl aduce țintit (stratul UTR real are
 * sute de poligoane — sub pragul MVT). În /embed fără metadata publică → rows gol → context null
 * (fail-closed prin construcție: nu inventăm regim unde nu avem regulament).
 */
async function ensure_utr_ctx_3d(map_slug: string): Promise<UtrCtx3d | null> {
	if (utr_ctx_3d) {
		return utr_ctx_3d
	}
	const detail = STATE.map_detail
	if (!detail) {
		return null
	}
	let rows = rh_rows_from_metadata(detail.metadata)
	if (rows.length === 0 && detail.utr_rh) {
		// Calea publică (F10-T5): serverul nu expune metadata.rlu, ci lookup-ul compact
		// cod→rh_max (fail-closed) — îl aducem la aceeași formă de rânduri.
		rows = Object.entries(detail.utr_rh).map(([utr, rh_max]) => ({ utr, rh_max }))
	}
	const utr_layer = detail.layers.find((l) => l.role === "utr")
	if (rows.length === 0 || !utr_layer) {
		return null
	}
	const sid = source_id(map_slug, utr_layer.slug)
	let fc = geojson_cache.get(sid)
	if (!fc) {
		const r = await api_get<GeoJSON.FeatureCollection>(geojson_url(map_slug, utr_layer.slug, ""))
		if (!r.ok) {
			return null
		}
		fc = r.data
	}
	const index = prepare_utr_index(
		fc.features as {
			geometry?: { type?: string; coordinates?: unknown } | null
			properties?: Record<string, unknown> | null
		}[],
	)
	if (index.length === 0) {
		return null
	}
	utr_ctx_3d = { index, rows }
	return utr_ctx_3d
}

// ─────────────────────────────────────────────────────────────
// Culoarea și stilul efectiv al straturilor: lib/layer_style (PUR, partajat cu serverul) — paleta,
// implicitele de desenare și `layer_color(layer, idx)` trăiesc ACOLO, ca harta, legenda, planșa
// tipărită și exportul (simbologia QGIS) să nu poată diferi (BD-007 / LIV-041).
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────

function geojson_url(map_slug: string, layer_slug: string, as_of: string): string {
	if (CTX.is_public) {
		// FĂRĂ `limit`: desenarea vrea stratul ÎNTREG, iar ruta îl întoarce întreg — ca înainte.
		return public_geojson_url(CTX.token, layer_slug, { as_of })
	}
	const base = `/api/maps/${map_slug}/layers/${layer_slug}/geojson`
	if (as_of) {
		return `${base}?as_of=${encodeURIComponent(as_of)}`
	}
	return base
}

function heights3d_url(map_slug: string, layer_slug: string): string {
	if (CTX.is_public) {
		return `/api/public/layers/${encodeURIComponent(layer_slug)}/heights3d?k=${encodeURIComponent(CTX.token)}`
	}
	return `/api/maps/${map_slug}/layers/${layer_slug}/heights3d`
}

function feature_url(id: string, as_of: string): string {
	if (CTX.is_public) {
		const b = `/api/public/feature/${encodeURIComponent(id)}?k=${encodeURIComponent(CTX.token)}`
		return as_of ? `${b}&as_of=${encodeURIComponent(as_of)}` : b
	}
	const base = `/api/features/${id}`
	if (as_of) {
		return `${base}?as_of=${encodeURIComponent(as_of)}`
	}
	return base
}

function tiles_url(map_slug: string, layer_slug: string): string {
	// URL absolut — MapLibre nu rezolvă fiabil template-urile relative de tiles
	if (CTX.is_public) {
		return `${location.origin}/api/public/tiles/${encodeURIComponent(layer_slug)}/{z}/{x}/{y}?k=${encodeURIComponent(CTX.token)}`
	}
	return `${location.origin}/tiles/${map_slug}/${layer_slug}/{z}/{x}/{y}.pbf`
}

// ─────────────────────────────────────────────────────────────
// 2.2 FILTRE SALVATE ca straturi derivate (contract filtre_salvate.md): sub fiecare strat cu
// `saved_filters`, câte un rând „strat derivat" cu toggle propriu, culoare DERIVATĂ din a părintelui
// (shade_color) și sursă filtrată: GeoJSON (sub prag) = filtrare LOCALĂ pe datele deja descărcate
// (`filter_matches`, zero cereri în plus); MVT (peste prag) = sursă separată cu `?filter=<slug>`.
// Public: `saved_filters` vin deja doar cele compatibile F10.
// ─────────────────────────────────────────────────────────────

function filter_key(layer_slug: string, filter_slug: string): string {
	return `${layer_slug}::${filter_slug}`
}
function filter_layer_id(map_slug: string, layer_slug: string, filter_slug: string): string {
	return layer_id_for(map_slug, layer_slug) + "__f_" + filter_slug
}
function filter_source_id(map_slug: string, layer_slug: string, filter_slug: string): string {
	return source_id(map_slug, layer_slug) + "__f_" + filter_slug
}
/** Culoarea rândului derivat: aceeași familie ca părintele, alternând închis/deschis per index. */
const FILTER_SHADES = [0.62, 1.45, 0.4, 1.7, 0.8]
function filter_color(parent_color: string, fi: number): string {
	return shade_color(parent_color, FILTER_SHADES[fi % FILTER_SHADES.length])
}

/** Scoate de pe hartă rândurile derivate ale unui strat (toate filtrele lui). */
function remove_filter_layers(map_slug: string, layer: LayerSummary): void {
	if (!MAP) {
		return
	}
	for (const f of layer.saved_filters ?? []) {
		const lid = filter_layer_id(map_slug, layer.slug, f.slug)
		const sid = filter_source_id(map_slug, layer.slug, f.slug)
		for (const suffix of ["__outline", ""]) {
			const xid = lid + suffix
			if (added_layers.has(xid) || MAP.getLayer(xid)) {
				try {
					MAP.removeLayer(xid)
				} catch (_) {
					/* ignore */
				}
				added_layers.delete(xid)
			}
		}
		if (added_sources.has(sid) || MAP.getSource(sid)) {
			try {
				MAP.removeSource(sid)
			} catch (_) {
				/* ignore */
			}
			added_sources.delete(sid)
		}
	}
}

/** Adaugă pe hartă rândul derivat (sursă filtrată + stil în familia părintelui). */
async function add_filter_layer(
	map_slug: string,
	layer: LayerSummary,
	idx: number,
	fi: number,
	f: SavedFilter,
): Promise<void> {
	if (!MAP) {
		return
	}
	const sid = filter_source_id(map_slug, layer.slug, f.slug)
	const lid = filter_layer_id(map_slug, layer.slug, f.slug)
	const key = filter_key(layer.slug, f.slug)
	const use_mvt = use_vector_tiles(layer, STATE.as_of, CTX.is_public)
	if (use_mvt) {
		if (!added_sources.has(sid)) {
			const base = tiles_url(map_slug, layer.slug)
			MAP.addSource(sid, {
				type: "vector",
				tiles: [`${base}${base.includes("?") ? "&" : "?"}filter=${encodeURIComponent(f.slug)}`],
				promoteId: "feature_id",
			})
			added_sources.add(sid)
		}
	} else {
		// Filtrare LOCALĂ pe GeoJSON-ul părintelui (din cache; altfel îl aducem o dată).
		let fc = geojson_cache.get(source_id(map_slug, layer.slug))
		if (!fc) {
			const res = await api_get<GeoJSON.FeatureCollection & { truncated?: boolean }>(
				geojson_url(map_slug, layer.slug, STATE.as_of),
			)
			if (!res.ok || !MAP) {
				return
			}
			// Filtrul lucrează pe acest cache: dacă sursa e parțială, și rândul derivat e parțial.
			write_layer_truncated(layer.slug, res.data.truncated === true)
			fc = res.data
		}
		const features = fc.features.filter((x) =>
			filter_matches((x.properties ?? {}) as Record<string, unknown>, f.filter),
		)
		if (!added_sources.has(sid)) {
			MAP.addSource(sid, { type: "geojson", data: { type: "FeatureCollection", features } })
			added_sources.add(sid)
		}
	}
	const src_layer = use_mvt ? { "source-layer": layer.slug } : {}
	const color = filter_color(layer_color(layer, idx), fi)
	const dark = shade_color(color, 0.7)
	const gt = layer.geometry_type?.toLowerCase() ?? ""
	if (gt.includes("polygon")) {
		if (!added_layers.has(lid)) {
			MAP.addLayer({
				id: lid,
				type: "fill",
				source: sid,
				...src_layer,
				paint: { "fill-color": color, "fill-opacity": 0.75 },
			} as maplibregl.LayerSpecification)
			added_layers.add(lid)
		}
		const oid = lid + "__outline"
		if (!added_layers.has(oid)) {
			MAP.addLayer({
				id: oid,
				type: "line",
				source: sid,
				...src_layer,
				paint: { "line-color": dark, "line-width": 1.5 },
			} as maplibregl.LayerSpecification)
			added_layers.add(oid)
		}
	} else if (gt.includes("line") || gt.includes("string") || gt.includes("curve")) {
		if (!added_layers.has(lid)) {
			MAP.addLayer({
				id: lid,
				type: "line",
				source: sid,
				...src_layer,
				paint: { "line-color": color, "line-width": 3.5 },
			} as maplibregl.LayerSpecification)
			added_layers.add(lid)
		}
	} else if (!added_layers.has(lid)) {
		MAP.addLayer({
			id: lid,
			type: "circle",
			source: sid,
			...src_layer,
			paint: {
				"circle-color": color,
				"circle-radius": 6,
				"circle-stroke-color": dark,
				"circle-stroke-width": 1.5,
			},
		} as maplibregl.LayerSpecification)
		added_layers.add(lid)
	}
	const visible = STATE.filter_visible[key] === true
	for (const suffix of ["", "__outline"]) {
		if (MAP.getLayer(lid + suffix)) {
			MAP.setLayoutProperty(lid + suffix, "visibility", visible ? "visible" : "none")
		}
	}
	bind_layer_click(lid, layer.slug)
	restack_layers()
}

/** Scrie marcajul de trunchiere al unui strat (aceeași regulă VanX ca `write_filter_visible`). */
function write_layer_truncated(slug: string, on: boolean): void {
	// `hasOwn`, nu `=== undefined`: pentru „constructor” proxy-ul întoarce Object.prototype.constructor.
	if (!Object.hasOwn(STATE.layer_truncated, slug)) {
		STATE.layer_truncated = { ...STATE.layer_truncated, [slug]: on }
	} else {
		STATE.layer_truncated[slug] = on
	}
}

/**
 * Scrie vizibilitatea unui rând derivat. Cheile se PRE-POPULEAZĂ în load_map (ca layer_visible):
 * VanX nu înregistrează dependență pe o cheie ABSENTĂ la prima citire, deci butonul „vizibil/ascuns"
 * și legenda nu s-ar mai re-randa niciodată dacă cheia ar apărea abia la primul toggle. Dacă totuși
 * lipsește (strat apărut după load_map), reasignăm containerul — asta re-rulează cititorii.
 */
function write_filter_visible(key: string, on: boolean): void {
	if (STATE.filter_visible[key] === undefined) {
		STATE.filter_visible = { ...STATE.filter_visible, [key]: on }
	} else {
		STATE.filter_visible[key] = on
	}
}

/** Toggle-ul unui rând derivat: aprinde (lazy, la prima aprindere) / stinge. */
function set_filter_visible(
	layer: LayerSummary,
	idx: number,
	fi: number,
	f: SavedFilter,
	on: boolean,
): void {
	const map_slug = STATE.selected_map_slug
	write_filter_visible(filter_key(layer.slug, f.slug), on)
	if (!MAP || !map_slug) {
		return
	}
	const lid = filter_layer_id(map_slug, layer.slug, f.slug)
	if (on && !added_layers.has(lid)) {
		void add_filter_layer(map_slug, layer, idx, fi, f)
		return
	}
	for (const suffix of ["", "__outline"]) {
		if (MAP.getLayer(lid + suffix)) {
			MAP.setLayoutProperty(lid + suffix, "visibility", on ? "visible" : "none")
		}
	}
}

/** Rândurile derivate VIZIBILE ale hărții (legendă pe hartă + tipărire). */
function visible_filter_rows(): { layer: LayerSummary; idx: number; fi: number; f: SavedFilter }[] {
	const d = STATE.map_detail
	if (!d) {
		return []
	}
	const out: { layer: LayerSummary; idx: number; fi: number; f: SavedFilter }[] = []
	d.layers.forEach((layer, idx) => {
		;(layer.saved_filters ?? []).forEach((f, fi) => {
			if (STATE.filter_visible[filter_key(layer.slug, f.slug)]) {
				out.push({ layer, idx, fi, f })
			}
		})
	})
	return out
}

// ─────────────────────────────────────────────────────────────
// 1.2 ZONIFICARE ACTUALĂ (contract zonificare_actuala.md): stratul VIRTUAL `zonificare_actuala` =
// UTR-urile PUG decupate de derogările aprobate + PUZ-urile deasupra (sursa „PUZ"). Intern pe ruta
// gardată, public pe /api/public/... fail-closed (doar cu stratul UTR publicat). Rândul apare doar
// când harta are strat cu rol `utr` (+ modulul de regim); pe public, un 404 la aprindere se afișează
// în rând („indisponibil"), nu se sondează dinainte (o cerere în plus la fiecare vizită).
// ─────────────────────────────────────────────────────────────

const ZONIFICARE_SRC = "__zonificare"
const ZONIFICARE_PUZ_COLOR = "#d97706"
const ZONIFICARE_PUG_LINE = "#334155"
const ZONIFICARE_IDS = ["_fill", "_line", "_puz", "_label"].map((x) => ZONIFICARE_SRC + x)

interface ZonificareCollection extends GeoJSON.FeatureCollection {
	sources?: { utr: string; derogari: string[] }
}

/**
 * Rândul „Zonificare actuală" are sens pe harta curentă? Intern: strat UTR + modulul de regim în
 * contract. Public: DOAR stratul UTR publicat (`layers` = cele publicate, fail-closed prin
 * construcție) — stratul virtual nu e legat de instrumentul „regim" al embed-ului, iar `caps.regim`
 * îl poartă și pe acela; poarta reală rămâne serverul (un 404 se afișează în rând la aprindere).
 */
function zonificare_supported(): boolean {
	const d = STATE.map_detail
	if (!d || !d.layers.some((l) => l.role === "utr")) {
		return false
	}
	return CTX.is_public || regim_available()
}

async function set_zonificare_visible(on: boolean): Promise<void> {
	STATE.zonificare_visible = on
	STATE.zonificare_error = ""
	if (!MAP) {
		return
	}
	if (!on) {
		for (const id of ZONIFICARE_IDS) {
			if (MAP.getLayer(id)) {
				MAP.setLayoutProperty(id, "visibility", "none")
			}
		}
		return
	}
	const slug = STATE.selected_map_slug
	if (!slug) {
		return
	}
	STATE.zonificare_loading = true
	// Cache-Control: no-cache pe server (cache de 30 s pe rezultat) → re-aducem la fiecare aprindere.
	const url = CTX.is_public
		? `/api/public/layers/zonificare_actuala/geojson?k=${encodeURIComponent(CTX.token)}`
		: `/api/maps/${encodeURIComponent(slug)}/layers/zonificare_actuala/geojson`
	const r = await api_get<ZonificareCollection>(url)
	STATE.zonificare_loading = false
	if (!r.ok || !MAP) {
		STATE.zonificare_visible = false
		STATE.zonificare_error = r.ok
			? ""
			: (r.err?.message ?? "Zonificarea actuală nu este disponibilă pentru această hartă.")
		return
	}
	const data = r.data
	STATE.zonificare_derogari = data.sources?.derogari?.length ?? 0
	STATE.zonificare_utr = data.sources?.utr ?? ""
	// PUG: culoare per COD (paleta tematică, deterministă în ordinea codurilor); PUZ: amber.
	const codes = [
		...new Set(data.features.map((f) => String(f.properties?.cod ?? "")).filter((c) => c !== "")),
	].toSorted()
	// `match` cu perechi DINAMICE (cod → culoare): tipurile-tuplu ale MapLibre nu pot verifica un
	// spread, de-aici conversia explicită; forma e cea din spec (input, ...perechi, fallback).
	const by_code = (codes.length > 0
		? [
				"match",
				["get", "cod"],
				...codes.flatMap((c, i) => [c, LAYER_COLORS[i % LAYER_COLORS.length]]),
				"#94a3b8",
			]
		: ["literal", "#94a3b8"]) as unknown as maplibregl.ExpressionSpecification
	const is_puz: maplibregl.ExpressionSpecification = ["==", ["get", "sursa"], "PUZ"]
	const fill_color: maplibregl.ExpressionSpecification = [
		"case",
		is_puz,
		ZONIFICARE_PUZ_COLOR,
		by_code,
	]
	const fill_opacity: maplibregl.ExpressionSpecification = ["case", is_puz, 0.35, 0.45]
	const label_text: maplibregl.ExpressionSpecification = [
		"case",
		is_puz,
		["concat", ["get", "cod"], " · ", ["get", "act"]],
		["get", "cod"],
	]
	const existing = MAP.getSource(ZONIFICARE_SRC) as maplibregl.GeoJSONSource | undefined
	if (existing) {
		existing.setData(data)
		MAP.setPaintProperty(ZONIFICARE_SRC + "_fill", "fill-color", fill_color)
		for (const id of ZONIFICARE_IDS) {
			if (MAP.getLayer(id)) {
				MAP.setLayoutProperty(id, "visibility", "visible")
			}
		}
		restack_layers()
		return
	}
	MAP.addSource(ZONIFICARE_SRC, { type: "geojson", data })
	MAP.addLayer({
		id: ZONIFICARE_SRC + "_fill",
		type: "fill",
		source: ZONIFICARE_SRC,
		paint: { "fill-color": fill_color, "fill-opacity": fill_opacity },
	} as maplibregl.LayerSpecification)
	MAP.addLayer({
		id: ZONIFICARE_SRC + "_line",
		type: "line",
		source: ZONIFICARE_SRC,
		filter: ["!=", ["get", "sursa"], "PUZ"],
		paint: { "line-color": ZONIFICARE_PUG_LINE, "line-width": 1 },
	} as maplibregl.LayerSpecification)
	// PUZ-urile: contur gros punctat (MapLibre n-are hașură fără imagine) — deasupra PUG-ului.
	MAP.addLayer({
		id: ZONIFICARE_SRC + "_puz",
		type: "line",
		source: ZONIFICARE_SRC,
		filter: ["==", ["get", "sursa"], "PUZ"],
		paint: { "line-color": ZONIFICARE_PUZ_COLOR, "line-width": 3, "line-dasharray": [2, 1] },
	} as maplibregl.LayerSpecification)
	MAP.addLayer({
		id: ZONIFICARE_SRC + "_label",
		type: "symbol",
		source: ZONIFICARE_SRC,
		layout: {
			"text-field": label_text,
			"text-font": ["Noto Sans Regular"],
			"text-size": 11,
			"text-anchor": "center",
		},
		paint: { "text-color": "#1a1a1a", "text-halo-color": "#ffffff", "text-halo-width": 1.5 },
	} as maplibregl.LayerSpecification)
	restack_layers()
	// Click pe o zonă → popup cu cod/denumire/sursă/act + selecția poligonului-SURSĂ (feature_id +
	// layer_slug din contract: MapLibre nu garantează Feature.id string).
	const fill_id = ZONIFICARE_SRC + "_fill"
	if (!bound_click_layers.has(fill_id)) {
		bound_click_layers.add(fill_id)
		MAP.on("click", fill_id, (e) => {
			if (map_click_consumed()) {
				return
			}
			const props = (e.features?.[0]?.properties ?? {}) as Record<string, unknown>
			const shown: Record<string, unknown> = {
				cod: props.cod,
				denumire: props.denumire,
				sursa: props.sursa,
			}
			if (props.sursa === "PUZ") {
				shown.act = props.act
				shown.hcl = props.hcl
			}
			show_feature_popup(e.lngLat, shown)
			const uuid_re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
			const fid = String(props.feature_id ?? "")
			if (uuid_re.test(fid)) {
				STATE.selected_feature_id = fid
				STATE.selected_feature_layer = String(props.layer_slug ?? "")
				load_feature(fid)
			}
		})
	}
}

// ─────────────────────────────────────────────────────────────
// Data loading functions (update STATE, never called in components)
// ─────────────────────────────────────────────────────────────

async function load_maps(): Promise<void> {
	STATE.maps_loading = true
	STATE.maps_error = ""
	const res = await api_get<MapSummary[]>("/api/maps")
	STATE.maps_loading = false
	if (res.ok) {
		STATE.maps = res.data
	} else {
		STATE.maps_error = res.err?.message ?? ""
	}
}

async function load_map(slug: string, opts: { fit?: boolean } = {}): Promise<void> {
	if (!slug && !CTX.is_public) {
		return
	}
	STATE.map_loading = true
	STATE.map_error = ""
	STATE.map_detail = null

	// Sursa detaliului: intern /api/maps/:slug (MapDetail) vs public /api/public/map?k= (adaptat).
	let detail: MapDetail | null = null
	if (CTX.is_public) {
		const res = await api_get<PublicMapView>(`/api/public/map?k=${encodeURIComponent(CTX.token)}`)
		STATE.map_loading = false
		if (!res.ok) {
			STATE.map_error = res.err?.message ?? ""
			return
		}
		const pv = res.data
		detail = {
			id: pv.map_id,
			slug: pv.slug,
			config: { name: pv.name },
			metadata: {},
			extent: pv.extent,
			layers: pv.layers,
			// F10: fără astea, embed-ul pierde butonul „Teren" și înălțimile din regim (utr_rh).
			terrain: pv.terrain,
			utr_rh: pv.utr_rh,
			public_caps: pv.caps,
			api_links: pv.api_links, // F7-T4: butoanele de interogare externă din FeaturePanel
			basemaps: pv.basemaps, // 1.3: fundalurile configurate — paritate cu harta internă
		}
		CTX.ticket_org = pv.org_slug
		CTX.ticket_map = pv.map_id
		// Traficul per primărie în Umami (embed): event custom cu slug-ul hărții/org-ului —
		// URL-ul nu se poate folosi (query-ul cu tokenul e exclus). No-op fără script/blocat.
		;(
			window as unknown as {
				umami?: { track: (n: string, d?: Record<string, unknown>) => void }
			}
		).umami?.track("harta_publica", { harta: pv.slug, primarie: pv.org_slug })
		// Sesizările cer și flag-ul de contract al org-ului (caps.sesizari), nu doar org asignat.
		STATE.ticket_available = !!pv.org_slug && pv.caps?.sesizari !== false
		STATE.org_name = pv.org_name
		STATE.org_stema = pv.stema_url
		// Documentația PUG publică (F6) — best-effort, în paralel cu straturile (secțiunea
		// din sidebar rămâne ascunsă cât e goală). Fără flag-ul de contract nu chemăm deloc
		// (serverul ar răspunde oricum 404 indistinct).
		if (pv.caps?.documente !== false) {
			void api_get<PublicDoc[]>(`/api/public/documents?k=${encodeURIComponent(CTX.token)}`).then(
				(r) => {
					STATE.public_docs = r.ok ? r.data : []
				},
			)
		}
	} else {
		const res = await api_get<MapDetail>(`/api/maps/${slug}`)
		STATE.map_loading = false
		if (!res.ok) {
			STATE.map_error = res.err?.message ?? ""
			return
		}
		detail = res.data
	}

	STATE.map_detail = detail
	const map_slug = detail.slug
	// 1.3: fundalurile configurate ale hărții (gol = perechea fixă Hartă/Satelit)
	apply_basemaps(detail.basemaps ?? [])
	// Vizibilitatea inițială: comutarea EXPLICITĂ a utilizatorului din sesiune dacă există
	// (`explicit_visibility`), altfel implicitul stratului (`visible_default`, 18.09; absent = aprins,
	// deci hărțile existente nu se schimbă). Un strat doar „văzut aprins" la montarea anterioară NU
	// e o alegere — ia implicitul NOU, ca bifa din #/admin să se vadă și fără reload.
	// ⚠️ Starea se păstrează pe SLUG, iar slug-urile Ordinului 904 se repetă între comune: fără
	// resetul la schimbarea hărții, un strat stins manual la o primărie ar ține stins omonimul de la
	// alta, iar implicitul configurat ar părea „bifat, dar nu ține". Re-montarea pe ACEEAȘI hartă
	// (schimbarea lui `as_of`) păstrează alegerile explicite, ca până acum.
	const same_map = last_visibility_map === map_slug
	if (!same_map) {
		explicit_visibility.clear()
	}
	last_visibility_map = map_slug
	const vis: Record<string, boolean> = {}
	for (const l of detail.layers) {
		const chosen =
			same_map && explicit_visibility.has(l.slug) ? STATE.layer_visible[l.slug] : undefined
		vis[l.slug] = chosen !== undefined ? chosen : l.visible_default !== false
	}
	// Deep-link `?feature=` deja rezolvat (pe calea internă zborul nu așteaptă harta): stratul
	// elementului pornește aprins chiar dacă e stins implicit — vezi reveal_feature_layer, care
	// acoperă cazul invers (elementul sosește după inițializare).
	const sel_layer = STATE.selected_feature_id ? (STATE.feature_detail?.layer_slug ?? "") : ""
	if (sel_layer && vis[sel_layer] === false) {
		vis[sel_layer] = true
		explicit_visibility.add(sel_layer)
	}
	STATE.layer_visible = vis
	// Marcajele de trunchiere, PRE-POPULATE din acelaşi motiv ca `layer_visible`: VanX nu înregistrează
	// dependenţă pe o cheie ABSENTĂ, iar crearea ei ulterioară nu re-rulează o legare deja construită
	// (vezi write_filter_visible mai jos) — fără asta eticheta „· afișat parțial" nu s-ar randa NICIODATĂ.
	const trunc: Record<string, boolean> = {}
	for (const l of detail.layers) {
		trunc[l.slug] = false // curat la montare: un marcaj vechi nu supraviețuiește schimbării de hartă
	}
	STATE.layer_truncated = trunc
	// 2.2: cheile rândurilor derivate, PRE-POPULATE ca layer_visible (vezi write_filter_visible —
	// o cheie absentă la prima citire nu e urmărită de VanX); starea deja aleasă se păstrează.
	const vis_f: Record<string, boolean> = {}
	for (const l of detail.layers) {
		for (const f of l.saved_filters ?? []) {
			const key = filter_key(l.slug, f.slug)
			vis_f[key] = STATE.filter_visible[key] === true
		}
	}
	STATE.filter_visible = vis_f
	// load GeoJSON sources onto map
	await load_all_layers(map_slug, detail.layers)
	// Straturile de selecție se creează abia acum (peste tot ce s-a încărcat, inclusiv peste `__3d`
	// dacă modul 3D a supraviețuit re-montării) → încă o restivuire, ca ele să intre în banda lor.
	ensure_highlight_layers()
	restack_layers(map_slug)
	apply_highlight()
	// Autozoom pe extent (acoperă și layerele MVT); fallback pe bounds-urile GeoJSON din cache.
	// Sărit când venim cu deep link pe o parcelă (camera a zburat deja acolo).
	if (opts.fit === false) {
		return
	}
	if (MAP && detail.extent) {
		const [minx, miny, maxx, maxy] = detail.extent
		MAP.fitBounds(
			[
				[minx, miny],
				[maxx, maxy],
			],
			{ padding: 40, duration: 800 },
		)
	} else {
		fit_to_data(map_slug, detail.layers)
	}
}

/**
 * Selecția (deep-link `?feature=`, căutare, identify) pe un strat STINS îl aprinde. De când straturile
 * pot porni stinse implicit (18.09), linkul „Element" al unei sesizări sau un rând din raportul
 * topologic ar fi aterizat pe un contur singuratic — fără strat, fără popup, fără vecini; înainte
 * toate straturile erau aprinse la montare, deci cazul nu exista (review 21.09).
 */
function reveal_feature_layer(slug: string): void {
	const detail = STATE.map_detail
	if (!slug || !detail || STATE.layer_visible[slug] !== false) {
		return
	}
	explicit_visibility.add(slug)
	STATE.layer_visible[slug] = true
	sync_layer_visibility(detail.slug, slug)
}

async function load_feature(id: string): Promise<FeatureDetail | null> {
	if (!id) {
		return null
	}
	STATE.feature_loading = true
	STATE.feature_error = ""
	STATE.feature_detail = null
	STATE.feature_history = []
	// Formularul de editare era al entității ANTERIOARE — se închide la orice (re)încărcare.
	STATE.feature_edit_open = false
	STATE.feature_edit_error = ""
	STATE.feature_photo_manage = false
	STATE.feature_photo_error = ""
	const res = await api_get<FeatureDetail>(feature_url(id, STATE.as_of))
	STATE.feature_loading = false
	const detail = res.ok ? res.data : null
	if (res.ok) {
		STATE.feature_detail = res.data
		// Stratul selecției se ia CENTRAL de aici (serverul îl știe): selecția poate veni din
		// căutare/identify/deep-link, nu doar din click — altfel filtrarea actelor per strat
		// (allowed_templates) ar judeca după stratul ULTIMULUI click, nu al feature-ului curent.
		// Guard pe id: selecția se putea schimba cât a durat fetch-ul.
		if (STATE.selected_feature_id === id) {
			STATE.selected_feature_layer = res.data.layer_slug ?? ""
			reveal_feature_layer(res.data.layer_slug ?? "")
		}
	} else {
		STATE.feature_error = res.err?.message ?? ""
	}
	// load history (+ documents doar în modul intern — public nu expune documente)
	load_feature_history(id)
	if (CTX.caps.documents) {
		load_feature_documents(id)
	}
	// Detaliul se ÎNTOARCE (pe lângă STATE) ca apelantul să poată zbura pe geometrie fără s-o mai
	// ceară o dată — tabelul de atribute nu mai primește contururi în rând (attributes_only).
	return detail
}

async function load_feature_documents(id: string): Promise<void> {
	STATE.feature_documents_loading = true
	STATE.feature_documents = []
	const res = await api_get<FeatureDocument[]>(
		`/api/documents?entity=${encodeURIComponent(id)}&limit=10`,
	)
	STATE.feature_documents_loading = false
	if (res.ok) {
		STATE.feature_documents = res.data
	}
	// Șabloanele pentru butoanele de generare — încărcate o singură dată.
	if (STATE.templates.length === 0) {
		const tpl = await api_get<TemplateRef[]>("/api/templates")
		if (tpl.ok) {
			STATE.templates = tpl.data.map((x) => ({ slug: x.slug, name: x.name }))
		}
	}
}

async function load_feature_history(id: string): Promise<void> {
	STATE.feature_history_loading = true
	const url = CTX.is_public
		? `/api/public/feature/${encodeURIComponent(id)}/history?k=${encodeURIComponent(CTX.token)}`
		: `/api/features/${id}/history`
	const res = await api_get<HistoryEvent[]>(url)
	STATE.feature_history_loading = false
	if (res.ok) {
		STATE.feature_history = res.data
	}
}

// Încarcă tabelul de atribute pentru un layer (toate features, până la TABLE_LIMIT).
async function load_table(layer_slug: string, layer_name: string): Promise<void> {
	STATE.table_layer_slug = layer_slug
	STATE.table_layer_name = layer_name
	STATE.table_loading = true
	STATE.table_error = ""
	STATE.table_features = []
	STATE.table_columns = []
	STATE.table_truncated = false
	const map_slug = STATE.selected_map_slug
	// Public: tabelul vine din geojson-ul public (F10-filtrat, o pagină mărginită); intern din /features.
	// `attributes_only=1`: tabelul afișează COLOANE, nu contururi — pe stratul de ape al Horezului
	// cele 2000 de rânduri scad de la 157 MB la sub 2 MB (măsurat 09.09). Zborul la click pe rând
	// folosește geometria din DETALIUL feature-ului, adus oricum (go_to_feature).
	const url = CTX.is_public
		? public_geojson_url(CTX.token, layer_slug, {
				limit: TABLE_LIMIT,
				as_of: STATE.as_of,
				attributes_only: true,
			})
		: `/api/maps/${map_slug}/layers/${layer_slug}/features?limit=${TABLE_LIMIT}&attributes_only=1` +
			(STATE.as_of ? `&as_of=${encodeURIComponent(STATE.as_of)}` : "")
	const res = await api_get<{
		features: { id: string; properties: Record<string, unknown> }[]
		truncated?: boolean // pus de server când pagina e plină (lib/http.parse_page)
	}>(url)
	STATE.table_loading = false
	if (!res.ok) {
		STATE.table_error = res.err?.message ?? ""
		return
	}
	const feats = res.data.features ?? []
	STATE.table_features = feats
	// Și flag-ul serverului: pe calea publică plafonul poate tăia SUB TABLE_LIMIT, iar atunci numărul
	// singur n-ar mai spune că lipsesc rânduri.
	STATE.table_truncated = feats.length >= TABLE_LIMIT || res.data.truncated === true
	// coloane = reuniunea cheilor (păstrând ordinea din primul feature)
	const cols: string[] = []
	const seen = new Set<string>()
	for (const f of feats) {
		for (const k of Object.keys(f.properties ?? {})) {
			if (!seen.has(k)) {
				seen.add(k)
				cols.push(k)
			}
		}
	}
	STATE.table_columns = cols
}

function close_table(): void {
	STATE.table_layer_slug = ""
	STATE.table_features = []
	STATE.table_columns = []
}

// ─────────────────────────────────────────────────────────────
// Căutare (nr. cadastral / valori de atribute) + identify
// ─────────────────────────────────────────────────────────────

// Markerul rezultatului de adresă (unul singur; înlocuit la fiecare selecție).
let addr_marker: maplibregl.Marker | null = null

/** Caută adresa prin /api/geocode (Enter, NU autocomplete — politica Nominatim), cu bias pe
 *  extent-ul hărții (viewbox): „Strada Principală" există în sute de comune. */
async function run_addr_search(): Promise<void> {
	const q = STATE.addr_q.trim()
	if (!q) {
		return
	}
	STATE.addr_loading = true
	STATE.addr_error = ""
	STATE.addr_results = []
	const ext = STATE.map_detail?.extent
	const bbox = ext ? `&bbox=${ext.join(",")}` : ""
	const res = await api_get<GeocodeHit[]>(`/api/geocode?q=${encodeURIComponent(q)}${bbox}`)
	STATE.addr_loading = false
	if (!res.ok) {
		STATE.addr_error = res.err?.message ?? "Căutarea adresei a eșuat."
		return
	}
	STATE.addr_results = res.data
	if (res.data.length === 0) {
		STATE.addr_error = "Nicio adresă găsită."
	}
	if (res.data.length === 1) {
		select_addr_hit(res.data[0])
	}
}

/** Zboară la adresa aleasă + marker portocaliu (înlocuit la următoarea căutare). */
function select_addr_hit(h: GeocodeHit): void {
	if (!MAP) {
		return
	}
	addr_marker?.remove()
	addr_marker = new maplibregl.Marker({ color: "#ea580c" })
		.setLngLat([h.longitude, h.latitude])
		.addTo(MAP)
	MAP.flyTo({ center: [h.longitude, h.latitude], zoom: 17 })
}

/** Curăță căutarea de adresă (marker + rezultate) — la ștergerea textului sau remontare. */
function clear_addr_search(): void {
	addr_marker?.remove()
	addr_marker = null
	STATE.addr_results = []
	STATE.addr_error = ""
}

async function run_search(): Promise<void> {
	const q = STATE.search_q.trim()
	const map_slug = STATE.selected_map_slug
	if (!q || !map_slug) {
		return
	}
	STATE.search_loading = true
	STATE.search_error = ""
	STATE.search_results = []
	STATE.identify_result = null
	// 3.4: cu rază activă, căutarea devine „în apropiere": lng/lat/radius_m ÎMPREUNĂ (contract),
	// în jurul CENTRULUI hărții (cercul arată unde) — rezultatele vin ordonate pe distanță.
	let near = ""
	if (STATE.radius_m > 0 && MAP) {
		const c = MAP.getCenter()
		near = `&lng=${c.lng.toFixed(6)}&lat=${c.lat.toFixed(6)}&radius_m=${STATE.radius_m}`
		show_radius_circle(c.lng, c.lat, STATE.radius_m)
	} else {
		clear_radius_circle()
	}
	const search_url = CTX.is_public
		? `/api/public/search?k=${encodeURIComponent(CTX.token)}&q=${encodeURIComponent(q)}${near}`
		: `/api/maps/${map_slug}/search?q=${encodeURIComponent(q)}${near}`
	const res = await api_get<SearchMatch[]>(search_url)
	STATE.search_loading = false
	if (!res.ok) {
		STATE.search_error = res.err?.message ?? ""
		return
	}
	STATE.search_results = res.data
	if (res.data.length === 0) {
		STATE.search_error = "Niciun rezultat."
	}
	if (res.data.length === 1) {
		select_search_match(res.data[0])
	}
}

/** `padding` = marginea la încadrare; 120 e implicitul căutării, tabelul cere 60 (ca înainte). */
function fly_to_geometry(geom: unknown, padding = 120): void {
	if (!MAP || !geom || typeof geom !== "object" || !("coordinates" in geom)) {
		return
	}
	const b = new maplibregl.LngLatBounds()
	extend_bounds(b, (geom as { coordinates: unknown }).coordinates)
	if (b.isEmpty()) {
		return
	}
	const ne = b.getNorthEast()
	const sw = b.getSouthWest()
	if (ne.lng === sw.lng && ne.lat === sw.lat) {
		MAP.flyTo({ center: ne, zoom: 17 })
	} else {
		MAP.fitBounds(b, { padding, maxZoom: 18, duration: 600 })
	}
}

async function select_search_match(m: SearchMatch): Promise<void> {
	STATE.search_results = []
	STATE.selected_feature_id = m.feature_id
	load_feature(m.feature_id)
	fly_to_geometry(m.geometry)
	await run_identify({ feature_id: m.feature_id })
}

// ─────────────────────────────────────────────────────────────
// Mod urbanism (F5-T2): click pe hartă → GET /regim → panoul de reglementări.
// Consumă clickul pe hartă → se EXCLUDE reciproc cu măsurarea / pinul / identify
// (prioritatea în handler-ul global: măsurare → pin → regim → identify).
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// Overlay „acte" (F5-T9 → N3, REQ-URB-017/019): stratul VIRTUAL al DOCUMENTELOR hărții (CU, AC,
// fișe, acte înregistrate) — „harta tematică AC/CU cu starea actelor" din caiete. Punctele vin din
// `/layers/acte/geojson`, colorate pe STARE (emis/anulat — asta e cerința), cu TIPUL ca a doua
// dimensiune (conturul); click = popup cu detaliile actului + linkul spre cerere/documente. DOAR
// intern (`caps.documents` + modulul `rapoarte`) — poartă date de solicitări.
// ─────────────────────────────────────────────────────────────

const ACTE_SRC = "__acte"
// Culoarea = STAREA actului — SINGURA tabelă: expresia de paint, legendele (ecran + hârtie) și
// popup-ul citesc toate de aici, altfel o schimbare de culoare le-ar desincroniza.
const ACTE_STATE_COLOR = { emis: "#059669", neemis: "#94a3b8", anulat: "#dc2626" } as const
type ActeState = keyof typeof ACTE_STATE_COLOR
const ACTE_STATE_LABEL: Record<ActeState, string> = {
	emis: "Emis",
	neemis: "Neemis — act pregătit, cererea nu e încă emisă",
	anulat: "Anulat",
}
const ACTE_STATE_COLORS = [
	"emis",
	ACTE_STATE_COLOR.emis,
	"neemis",
	ACTE_STATE_COLOR.neemis,
	"anulat",
	ACTE_STATE_COLOR.anulat,
] as const
// Conturul = TIPUL actului (CU albastru, AC chihlimbar; restul cu conturul alb obișnuit).
const ACTE_TYPE_STROKES = [
	"certificat_urbanism",
	"#2563eb",
	"autorizatie_construire",
	"#d97706",
] as const
// Intrările de legendă — ACELEAȘI pe ecran (LegendPanel) și pe pagina tipărită.
const ACTE_LEGEND = [
	[ACTE_STATE_COLOR.emis, "Acte — emise"],
	[ACTE_STATE_COLOR.neemis, "Acte — pregătite (neemise)"],
	[ACTE_STATE_COLOR.anulat, "Acte — anulate"],
] as const

const SOLICITARI_SRC = "__solicitari"
// Culorile tematice per STATUS (aceleași sensuri ca badge-urile din #/solicitari).
const SOLICITARI_COLORS = [
	"new",
	"#2563eb",
	"in_progress",
	"#d97706",
	"answered",
	"#0891b2",
	"issued",
	"#059669",
	"closed",
	"#64748b",
	"rejected",
	"#dc2626",
] as const

async function set_solicitari_visible(on: boolean): Promise<void> {
	STATE.solicitari_visible = on
	if (!MAP) {
		return
	}
	const lid = SOLICITARI_SRC + "_pt"
	if (!on) {
		if (MAP.getLayer(lid)) {
			MAP.setLayoutProperty(lid, "visibility", "none")
		}
		return
	}
	const slug = STATE.selected_map_slug
	if (!slug) {
		return
	}
	const r = await api_get<GeoJSON.FeatureCollection>(
		`/api/maps/${encodeURIComponent(slug)}/layers/solicitari/geojson`,
	)
	if (!r.ok || !MAP) {
		STATE.solicitari_visible = false
		return
	}
	const src = MAP.getSource(SOLICITARI_SRC) as maplibregl.GeoJSONSource | undefined
	if (src) {
		src.setData(r.data)
		MAP.setLayoutProperty(lid, "visibility", "visible")
		return
	}
	MAP.addSource(SOLICITARI_SRC, { type: "geojson", data: r.data })
	MAP.addLayer({
		id: lid,
		type: "circle",
		source: SOLICITARI_SRC,
		paint: {
			"circle-radius": 6,
			"circle-color": ["match", ["get", "status"], ...SOLICITARI_COLORS, "#64748b"],
			"circle-stroke-color": "#ffffff",
			"circle-stroke-width": 2,
		},
	})
	if (!bound_click_layers.has(lid)) {
		bound_click_layers.add(lid)
		MAP.on("click", lid, (e) => {
			// Aceeași regulă ca la acte: un act venit din cerere are EXACT punctul solicitării, iar cu
			// ambele straturi aprinse un click ar fi navigat ȘI ar fi deschis popup-ul actului.
			if (map_click_consumed() || top_bound_layer_at(e) !== lid) {
				return
			}
			const rid = e.features?.[0]?.properties?.cerere_id as string | undefined
			if (rid) {
				location.hash = `#/solicitari?id=${encodeURIComponent(rid)}`
			}
		})
	}
}

async function set_acte_visible(on: boolean): Promise<void> {
	STATE.acte_visible = on
	if (!MAP) {
		return
	}
	const lid = ACTE_SRC + "_pt"
	if (!on) {
		if (MAP.getLayer(lid)) {
			MAP.setLayoutProperty(lid, "visibility", "none")
		}
		return
	}
	const slug = STATE.selected_map_slug
	if (!slug) {
		return
	}
	const r = await api_get<GeoJSON.FeatureCollection>(
		`/api/maps/${encodeURIComponent(slug)}/layers/acte/geojson`,
	)
	if (!r.ok || !MAP) {
		STATE.acte_visible = false
		return
	}
	const src = MAP.getSource(ACTE_SRC) as maplibregl.GeoJSONSource | undefined
	if (src) {
		src.setData(r.data) // re-activare: reîmprospătează punctele (s-au putut emite acte noi)
		MAP.setLayoutProperty(lid, "visibility", "visible")
		return
	}
	MAP.addSource(ACTE_SRC, { type: "geojson", data: r.data })
	MAP.addLayer({
		id: lid,
		type: "circle",
		source: ACTE_SRC,
		paint: {
			"circle-radius": 7,
			"circle-color": ["match", ["get", "stare"], ...ACTE_STATE_COLORS, "#64748b"],
			"circle-stroke-color": ["match", ["get", "tip"], ...ACTE_TYPE_STROKES, "#ffffff"],
			"circle-stroke-width": 2,
		},
	})
	// Click pe un act → popup cu detaliile lui (starea e subiectul cerinței — o navigare imediată o
	// arăta doar prin culoare); linkul spre cerere/documente rămâne la un click distanță. Înregistrat
	// O DATĂ (sursa nu se mai distruge; la re-montare `bound_click_layers` se golește odată cu MapLibre).
	if (!bound_click_layers.has(lid)) {
		bound_click_layers.add(lid)
		MAP.on("click", lid, (e) => {
			// Aceeași regulă ca delegații straturilor: acționează doar stratul desenat cel mai sus
			// (o clădire 3D sau o solicitare peste act câștigă — fără două acțiuni pe un click).
			if (map_click_consumed() || top_bound_layer_at(e) !== lid) {
				return
			}
			const f = e.features?.[0]
			if (f) {
				show_act_popup(e.lngLat, (f.properties ?? {}) as Record<string, unknown>)
			}
		})
	}
}

/** Intră/iese din modul regim. La intrare închide panoul de feature (dreapta e a regimului). */
function set_regim_mode(on: boolean): void {
	STATE.regim_mode = on
	if (on) {
		if (STATE.measure_mode) {
			set_measure_mode("")
		}
		if (STATE.pin_active) {
			set_pin_mode(false)
		}
		if (STATE.edit_mode) {
			set_edit_mode(false)
		}
		STATE.identify_mode = false
		STATE.selected_feature_id = "" // panoul din dreapta îl ocupă regimul
	} else {
		STATE.regim = null
		STATE.regim_loading = false
		STATE.regim_error = ""
	}
	if (MAP) {
		MAP.getCanvas().style.cursor = on ? "crosshair" : ""
	}
}

/** Interoghează regimul punctului: intern pe ruta gardată, public pe /api/public/regim (F6-T2). */
async function run_regim(lng: number, lat: number): Promise<void> {
	const map_slug = STATE.selected_map_slug
	if (!map_slug) {
		return
	}
	STATE.regim_loading = true
	const regim_url = CTX.is_public
		? `/api/public/regim?k=${encodeURIComponent(CTX.token)}&lng=${lng}&lat=${lat}`
		: `/api/maps/${encodeURIComponent(map_slug)}/regim?lng=${lng}&lat=${lat}`
	const res = await api_get<Regim>(regim_url)
	STATE.regim_loading = false
	STATE.regim = res.ok ? res.data : null
	// Fail-ul are mesaj util (ex. public: „nu este disponibil public") — altfel panoul ar reafișa
	// instrucțiunea de click, ca și cum nu s-ar fi întâmplat nimic.
	let regim_msg = res.ok ? "" : (res.err?.message ?? "Interogarea regimului a eșuat.")
	// Mesaj ACȚIONABIL (F13-T3): „hartă fără strat UTR" e aproape mereu CONFIG de date, nu bug —
	// simptomul își spune cauza (doar intern; publicul primește mesajul neutru al serverului).
	if (!CTX.is_public && regim_msg.toLowerCase().includes("utr")) {
		regim_msg +=
			" — Setează rolul „UTR” pe stratul unităților teritoriale în #/admin → strat → Rol urbanism."
	}
	STATE.regim_error = regim_msg
}

async function run_identify(
	target: { lng: number; lat: number } | { feature_id: string },
): Promise<void> {
	const map_slug = STATE.selected_map_slug
	if (!map_slug) {
		return
	}
	STATE.identify_loading = true
	// 3.4: raza (m) — geodezică pe server; la `feature_id` distanța e de la MARGINEA parcelei.
	const radius = STATE.radius_m > 0 ? `&radius_m=${STATE.radius_m}` : ""
	const qs =
		("feature_id" in target
			? `feature_id=${encodeURIComponent(target.feature_id)}`
			: `lng=${target.lng}&lat=${target.lat}`) + radius
	const identify_url = CTX.is_public
		? `/api/public/identify?k=${encodeURIComponent(CTX.token)}&${qs}`
		: `/api/maps/${map_slug}/identify?${qs}`
	const res = await api_get<IdentifyResult>(identify_url)
	STATE.identify_loading = false
	STATE.identify_result = res.ok ? res.data : null
	// Cercul razei: în jurul punctului interogat; pe `feature_id` (distanță de la margine) nu
	// există un centru — cercul se șterge.
	if (STATE.radius_m > 0 && "lng" in target) {
		show_radius_circle(target.lng, target.lat, STATE.radius_m)
	} else {
		clear_radius_circle()
	}
}

// Calculează bounds dintr-o geometrie GeoJSON și o folosește la „go to".
function extend_bounds(b: maplibregl.LngLatBounds, coords: unknown): void {
	if (
		typeof (coords as number[])[0] === "number" &&
		coords instanceof Array &&
		typeof coords[1] === "number"
	) {
		b.extend([coords[0] as number, coords[1] as number])
		return
	}
	if (Array.isArray(coords)) {
		for (const c of coords) {
			extend_bounds(b, c)
		}
	}
}

/**
 * Selecție din TABELUL de atribute: încarcă detaliul și zboară pe geometria LUI. Rândurile de tabel
 * nu mai poartă contur (se cer cu `attributes_only=1`), iar detaliul se aducea oricum aici — deci
 * zero cereri în plus. ⚠️ Gardă pe id: două click-uri rapide nu trebuie să zboare la elementul
 * greșit (fetch-ul anterior poate răspunde după cel nou).
 */
function go_to_feature(id: string): void {
	STATE.selected_feature_id = id
	void load_feature(id).then((detail) => {
		if (!detail || STATE.selected_feature_id !== id) {
			return
		}
		// padding 60 = încadrarea de dinainte pentru clicul din tabel (căutarea rămâne pe 120)
		fly_to_geometry(detail.geometry, 60)
	})
}

// ─────────────────────────────────────────────────────────────
// 1.5 COORDONATE Stereo 70 + WGS84 (contract stereo70.md): conversie în browser (lib PURĂ), bară de
// coordonate la cursor (touch: centrul hărții), bloc în FeaturePanel/IdentifyPanel, buton de copiere.
// Notația cadastrală: X = NORD, Y = EST (format_stereo70). Read-only → identic și în /embed.
// ─────────────────────────────────────────────────────────────

/** Copiază text în clipboard (API modern, cu fallback pe execCommand pentru iframe/HTTP). */
async function copy_text(text: string): Promise<boolean> {
	try {
		await navigator.clipboard.writeText(text)
		return true
	} catch {
		/* fallback mai jos */
	}
	try {
		const ta = document.createElement("textarea")
		ta.value = text
		ta.style.position = "fixed"
		ta.style.opacity = "0"
		document.body.appendChild(ta)
		ta.select()
		const ok = document.execCommand("copy")
		ta.remove()
		return ok
	} catch {
		return false
	}
}

/**
 * Cele două scrieri ale unui punct: Stereo 70 (X=nord, Y=est) și WGS84 (lng, lat cu 6 zecimale).
 * În afara anvelopei României proiecția Stereo 70 dă numere fără sens (cursor pe alt continent,
 * obiect georeferențiat greșit) → text explicit în locul lor; linia WGS84 rămâne neschimbată.
 */
function coords_lines(lng: number, lat: number): { stereo: string; wgs: string } {
	const p = wgs84_to_stereo70(lng, lat)
	return {
		stereo: stereo70_in_bounds(p) ? format_stereo70(p) : "în afara României",
		wgs: `${lng.toFixed(6)}, ${lat.toFixed(6)}`,
	}
}

/**
 * Feedback „Copiat" (1,5 s) PER buton — fiecare bloc de coordonate are starea lui (o stare
 * partajată făcea toate butoanele de pe ecran — bara + panoul — să clipească împreună). Se aprinde
 * DOAR când copierea a reușit (copy_text întoarce false pe clipboard blocat).
 */
function copied_flash() {
	const label = van.state("")
	let timer = 0
	return {
		label,
		flash: () => {
			label.val = "Copiat"
			clearTimeout(timer)
			timer = window.setTimeout(() => {
				label.val = ""
			}, 1500)
		},
	}
}

/** Textul copiat pentru un punct: ambele sisteme, pe două rânduri (se lipește direct în CAD/acte). */
function coords_clipboard_text(lng: number, lat: number): string {
	const c = coords_lines(lng, lat)
	return `Stereo 70: ${c.stereo}\nWGS84: ${c.wgs}`
}

/** Bloc reutilizabil: Stereo 70 + WGS84 + buton de copiere (FeaturePanel, IdentifyPanel). */
function CoordsBlock(lng: number, lat: number, title: string) {
	const c = coords_lines(lng, lat)
	const copied = copied_flash()
	return t.div(
		{ class: "mb-3" },
		t.div(
			{ class: "flex items-center justify-between gap-2 mb-1" },
			t.div(
				{
					class:
						"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500",
				},
				title,
			),
			t.button(
				{
					class:
						"flex items-center gap-1 text-[11px] text-cyan-700 dark:text-cyan-300 hover:underline cursor-pointer",
					title: "Copiază coordonatele (Stereo 70 + WGS84)",
					onclick: () => {
						void copy_text(coords_clipboard_text(lng, lat)).then((ok) => {
							if (ok) {
								copied.flash()
							}
						})
					},
				},
				icon("copy"),
				() => copied.label.val || "Copiază",
			),
		),
		t.div(
			{
				class:
					"rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 font-mono text-[11px] text-slate-700 dark:text-slate-200 space-y-0.5",
			},
			t.div(
				{ title: "Stereo 70 (EPSG:3844) — notația cadastrală: X = nord, Y = est" },
				t.span({ class: "text-slate-400 dark:text-slate-500" }, "Stereo 70  "),
				c.stereo || "—",
			),
			t.div(
				{ title: "WGS84 (longitudine, latitudine)" },
				t.span({ class: "text-slate-400 dark:text-slate-500" }, "WGS84  "),
				c.wgs,
			),
		),
	)
}

// Bara de coordonate: poziția cursorului, actualizată cel mult o dată pe cadru (rAF) — mousemove
// vine de sute de ori pe secundă, iar conversia + re-randarea la fiecare eveniment ar fi risipă.
let coord_pending: { lng: number; lat: number } | null = null
let coord_raf = 0
function schedule_coord_update(lng: number, lat: number): void {
	coord_pending = { lng, lat }
	if (coord_raf) {
		return
	}
	coord_raf = requestAnimationFrame(() => {
		coord_raf = 0
		if (coord_pending) {
			STATE.coord_lng = coord_pending.lng
			STATE.coord_lat = coord_pending.lat
		}
	})
}

/**
 * Bara de coordonate (1.5): Stereo 70 + WGS84 la cursor, cu copiere. Desktop (md+): ascunsă pe
 * telefon (fără cursor; blocurile din panouri dau coordonatele elementului/punctului). Dreapta-jos,
 * deasupra scării și atribuirii MapLibre.
 */
function CoordBar() {
	const copied = copied_flash()
	return t.div(
		{
			class: () =>
				"absolute bottom-14 right-3 z-20 hidden md:flex items-center gap-2 rounded-lg " +
				"bg-white/95 dark:bg-slate-900/95 shadow border border-slate-200 dark:border-slate-700 " +
				"px-2.5 py-1 font-mono text-[11px] text-slate-700 dark:text-slate-200" +
				(Number.isFinite(STATE.coord_lng) ? "" : " !hidden"),
		},
		t.span({ title: "Stereo 70 (EPSG:3844) — notația cadastrală: X = nord, Y = est" }, () =>
			Number.isFinite(STATE.coord_lng) ? coords_lines(STATE.coord_lng, STATE.coord_lat).stereo : "",
		),
		t.span({ class: "text-slate-300 dark:text-slate-600" }, "|"),
		t.span({ title: "WGS84 (longitudine, latitudine)" }, () =>
			Number.isFinite(STATE.coord_lng) ? coords_lines(STATE.coord_lng, STATE.coord_lat).wgs : "",
		),
		t.button(
			{
				class:
					"p-1 -m-1 text-slate-400 dark:text-slate-500 hover:text-cyan-700 dark:hover:text-cyan-300 cursor-pointer",
				title: "Copiază coordonatele (Stereo 70 + WGS84)",
				onclick: () => {
					if (Number.isFinite(STATE.coord_lng)) {
						void copy_text(coords_clipboard_text(STATE.coord_lng, STATE.coord_lat)).then((ok) => {
							if (ok) {
								copied.flash()
							}
						})
					}
				},
			},
			() =>
				copied.label.val ? t.span({ class: "text-emerald-700 font-sans" }, "Copiat") : icon("copy"),
		),
	)
}

// ─────────────────────────────────────────────────────────────
// 3.4 PROXIMITATE (contract proximitate.md): `radius_m` pe identify + căutare (geodezic, plafon
// 5000 m pe server), cu cercul razei desenat pe hartă cât e activ. Read-only → și în /embed.
// ─────────────────────────────────────────────────────────────

const RADIUS_SRC = "__radius"

/**
 * Modul „interogare pe punct" (click pe hartă = identify, cu raza din căutarea avansată). Butonul
 * lui stă în secțiunea „Avansat" a panoului de căutare → modul se ÎNCHIDE odată cu secțiunea sau cu
 * panoul: altfel rămânea activ invizibil (doar cursorul cruce) și fiecare click pe hartă mai deschidea
 * o interogare cu cerc (testare 08.09). Închiderea curăță și cercul razei.
 */
function set_identify_mode(on: boolean): void {
	if (on) {
		if (STATE.measure_mode) {
			set_measure_mode("")
		}
		if (STATE.regim_mode) {
			set_regim_mode(false)
		}
		if (STATE.edit_mode) {
			set_edit_mode(false)
		}
	}
	STATE.identify_mode = on
	if (!on) {
		clear_radius_circle()
	}
	if (MAP) {
		MAP.getCanvas().style.cursor = on ? "crosshair" : ""
	}
}
const RADIUS_MAX_M = 5000
const RADIUS_PRESETS = [50, 100, 250, 500, 1000]

/** Poligon-cerc geodezic (n vârfuri) în jurul unui punct — „turf-free", pe sfera medie. */
function circle_polygon(lng: number, lat: number, radius_m: number, n = 64): GeoJSON.Polygon {
	const R = 6371008.8
	const d = radius_m / R
	const lat1 = (lat * Math.PI) / 180
	const lng1 = (lng * Math.PI) / 180
	const ring: [number, number][] = []
	for (let i = 0; i <= n; i++) {
		const th = (2 * Math.PI * i) / n
		const lat2 = Math.asin(
			Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(th),
		)
		const lng2 =
			lng1 +
			Math.atan2(
				Math.sin(th) * Math.sin(d) * Math.cos(lat1),
				Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
			)
		ring.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI])
	}
	return { type: "Polygon", coordinates: [ring] }
}

function ensure_radius_layers(): void {
	if (!MAP || MAP.getSource(RADIUS_SRC)) {
		return
	}
	MAP.addSource(RADIUS_SRC, { type: "geojson", data: EMPTY_HIGHLIGHT })
	MAP.addLayer({
		id: RADIUS_SRC + "_fill",
		type: "fill",
		source: RADIUS_SRC,
		paint: { "fill-color": "#f59e0b", "fill-opacity": 0.08 },
	})
	MAP.addLayer({
		id: RADIUS_SRC + "_line",
		type: "line",
		source: RADIUS_SRC,
		paint: { "line-color": "#d97706", "line-width": 2, "line-dasharray": [3, 2] },
	})
}

/** Desenează cercul razei în jurul punctului interogat (cât raza e activă). */
function show_radius_circle(lng: number, lat: number, radius_m: number): void {
	if (!MAP) {
		return
	}
	ensure_radius_layers()
	const src = MAP.getSource(RADIUS_SRC) as maplibregl.GeoJSONSource | undefined
	src?.setData({
		type: "Feature",
		geometry: circle_polygon(lng, lat, radius_m),
		properties: {},
	} as GeoJSON.Feature)
	restack_layers() // cercul în banda lui: peste tematice, sub selecție și sub clădirile 3D
}

function clear_radius_circle(): void {
	const src = MAP?.getSource(RADIUS_SRC) as maplibregl.GeoJSONSource | undefined
	src?.setData(EMPTY_HIGHLIGHT)
}

/** Setează raza (0 = oprit); peste plafon se taie la 5000 (serverul ar răspunde oricum 400). */
function set_radius(m: number): void {
	const v = Number.isFinite(m) && m > 0 ? Math.min(RADIUS_MAX_M, Math.round(m)) : 0
	STATE.radius_m = v
	if (v === 0) {
		clear_radius_circle()
	}
}

/**
 * Câmpul razei (3.4): NEcontrolat — se scrie în STATE la `change`/Enter (un `value` reactiv +
 * oninput se bătea cu tastarea: „0" dispărea, „6000" se tăia la 5000 la jumătatea cifrelor).
 * Presetările/plafonul îl actualizează prin derive, DOAR când valoarea din DOM diferă. Derive-ul e
 * creat în binding-ul blocului „Avansat" → moare odată cu el (Van îl leagă de DOM-ul binding-ului).
 * ⚠️ Valoarea inițială vine tot din derive, NU citită sincron în corpul funcției: o citire a lui
 * `STATE.radius_m` în timpul construcției ar înscrie raza ca dependență a ÎNTREGULUI bloc „Avansat"
 * (Van capturează toate citirile din execuția binding-ului) → blocul s-ar re-randa la fiecare
 * salvare a razei, cu focusul pierdut și câmpul recreat.
 */
function RadiusInput() {
	const input = t.input({
		type: "number",
		min: "0",
		max: String(RADIUS_MAX_M),
		step: "10",
		class:
			"w-20 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-1.5 py-0.5 text-xs",
		placeholder: "0",
		value: "",
		onchange: () => set_radius(Number(input.value)),
		onkeydown: (e: KeyboardEvent) => {
			if (e.key === "Enter") {
				e.preventDefault()
				set_radius(Number(input.value))
			}
		},
	}) as HTMLInputElement
	van.derive(() => {
		const v = STATE.radius_m
		if (Number(input.value) !== v) {
			input.value = v > 0 ? String(v) : ""
		}
	})
	return input
}

// ─────────────────────────────────────────────────────────────
// MapLibre integration
// ─────────────────────────────────────────────────────────────

let MAP: maplibregl.Map | null = null
let ACTIVE_POPUP: maplibregl.Popup | null = null

// Track which sources/layers we've added so we can remove/update them
const added_sources = new Set<string>()
const added_layers = new Set<string>()
/** Harta pentru care `STATE.layer_visible` ține alegerile utilizatorului (vezi `load_map`). */
let last_visibility_map = ""
/**
 * Straturile pe care utilizatorul le-a comutat EXPLICIT (buton, swatch, „doar acesta", „toate") pe
 * harta curentă. DOAR acestea își păstrează starea la re-montare; restul iau implicitul stratului —
 * altfel o bifă schimbată în #/admin nu se vedea pe hartă fără reload, fiindcă starea „aprins" de la
 * montarea anterioară (care nu fusese o alegere, ci vechiul implicit) câștiga în fața celei noi
 * (testare 21.09). Golit la schimbarea hărții.
 */
const explicit_visibility = new Set<string>()
// Layerele cu handler-e de click deja legate — MAP.on() nu e idempotent, iar
// add_layer_to_map se re-apelează la fiecare schimbare de as_of.
const bound_click_layers = new Set<string>()

// ─────────────────────────────────────────────────────────────
// Highlight pentru entitatea selectată: sursă GeoJSON dedicată, alimentată din
// feature_detail (geometria completă din API) — funcționează identic pe surse
// GeoJSON și MVT (tile-urile au geometrii decupate, API-ul nu).
// ─────────────────────────────────────────────────────────────

const HIGHLIGHT_SRC = "__highlight"
const HIGHLIGHT_COLOR = "#f59e0b" // amber — distinct de culorile tematice
const EMPTY_HIGHLIGHT: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] }

function ensure_highlight_layers(): void {
	if (!MAP || MAP.getSource(HIGHLIGHT_SRC)) {
		return
	}
	MAP.addSource(HIGHLIGHT_SRC, { type: "geojson", data: EMPTY_HIGHLIGHT })
	MAP.addLayer({
		id: HIGHLIGHT_SRC + "_fill",
		type: "fill",
		source: HIGHLIGHT_SRC,
		filter: ["==", ["geometry-type"], "Polygon"],
		paint: { "fill-color": HIGHLIGHT_COLOR, "fill-opacity": 0.18 },
	})
	MAP.addLayer({
		id: HIGHLIGHT_SRC + "_line",
		type: "line",
		source: HIGHLIGHT_SRC,
		paint: { "line-color": HIGHLIGHT_COLOR, "line-width": 3 },
	})
	MAP.addLayer({
		id: HIGHLIGHT_SRC + "_point",
		type: "circle",
		source: HIGHLIGHT_SRC,
		filter: ["==", ["geometry-type"], "Point"],
		paint: {
			"circle-radius": 9,
			"circle-color": "rgba(245, 158, 11, 0.25)",
			"circle-stroke-color": HIGHLIGHT_COLOR,
			"circle-stroke-width": 3,
		},
	})
}

// ─────────────────────────────────────────────────────────────
// MĂSURARE + DESEN (REQ-PEX-004 „verificare/măsurare în portalul public", REQ-FMT-004).
// Client-only: NIMIC nu se scrie pe server. Trăiește în harta INTERNĂ, deci apare automat și în
// `/embed` (viewerul public montează exact acest modul) — de-aceea NU e gardat pe `caps`.
// Punctele stau într-un array simplu de modul, nu în `vanX.reactive`: e o listă mutată la fiecare
// click, iar în STATE ținem doar ce se RANDEAZĂ (modul + textul rezultatului).
// ─────────────────────────────────────────────────────────────

const MEASURE_SRC = "__measure"
const MEASURE_COLOR = "#db2777" // roz — distinct de tematic (cyan) și de highlight (amber)
const EMPTY_MEASURE: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] }

let measure_points: Array<[number, number]> = []
// Desen liber (REQ-PEX-004, „funcții de desenare"): formele TERMINATE se adună aici și rămân pe
// hartă (aceeași sursă MEASURE_SRC) până la „Șterge desenul" / remontare. Client-only.
let drawn_features: GeoJSON.Feature[] = []

function ensure_measure_layers(): void {
	if (!MAP || MAP.getSource(MEASURE_SRC)) {
		return
	}
	MAP.addSource(MEASURE_SRC, { type: "geojson", data: EMPTY_MEASURE })
	MAP.addLayer({
		id: MEASURE_SRC + "_fill",
		type: "fill",
		source: MEASURE_SRC,
		filter: ["==", ["geometry-type"], "Polygon"],
		paint: { "fill-color": MEASURE_COLOR, "fill-opacity": 0.15 },
	})
	MAP.addLayer({
		id: MEASURE_SRC + "_line",
		type: "line",
		source: MEASURE_SRC,
		filter: ["!=", ["geometry-type"], "Point"],
		paint: { "line-color": MEASURE_COLOR, "line-width": 2.5, "line-dasharray": [2, 1] },
	})
	MAP.addLayer({
		id: MEASURE_SRC + "_point",
		type: "circle",
		source: MEASURE_SRC,
		filter: ["==", ["geometry-type"], "Point"],
		paint: {
			"circle-radius": 5,
			"circle-color": "#fff",
			"circle-stroke-color": MEASURE_COLOR,
			"circle-stroke-width": 2,
		},
	})
}

/** Ține măsurătoarea deasupra tuturor (ca highlight-ul — layerele tematice s-ar suprapune). */
/**
 * Păstrată ca nume, dar face RESTIVUIREA COMPLETĂ: mutarea ad-hoc „la vârf" a schiței o urca peste
 * clădirile 3D și strica invariantul din `restack_layers` până la următoarea restivuire (review 21.09).
 * Costul e același — orice `moveLayer` cere oricum o plasare completă de simboluri la următorul cadru.
 */
function raise_measure_layers(): void {
	restack_layers()
}

/** Redesenează geometria măsurată + recalculează textul rezultatului (geodezic, vezi lib/geodesy). */
function render_measure(): void {
	if (!MAP) {
		return
	}
	ensure_measure_layers()
	const src = MAP.getSource(MEASURE_SRC) as maplibregl.GeoJSONSource | undefined
	if (!src) {
		return
	}
	const pts = measure_points
	// Formele desenate deja terminate rămân mereu pe hartă, sub schița curentă.
	const features: GeoJSON.Feature[] = [...drawn_features]
	features.push(
		...pts.map(
			(p): GeoJSON.Feature => ({
				type: "Feature",
				geometry: { type: "Point", coordinates: [p[0], p[1]] },
				properties: {},
			}),
		),
	)
	const is_area = STATE.measure_mode === "area"
	if (is_area && pts.length >= 3) {
		// Poligonul se închide vizual (inelul GeoJSON cere primul punct repetat la final).
		features.push({
			type: "Feature",
			geometry: { type: "Polygon", coordinates: [[...pts, pts[0]!]] },
			properties: {},
		})
	} else if (pts.length >= 2) {
		features.push({
			type: "Feature",
			geometry: { type: "LineString", coordinates: pts.map((p) => [p[0], p[1]]) },
			properties: {},
		})
	}
	src.setData({ type: "FeatureCollection", features })
	raise_measure_layers()

	// Rezultatul afișat: arie (+ perimetru), lungime sau starea desenului (fără calcul).
	if (STATE.measure_mode === "draw") {
		STATE.measure_readout =
			pts.length > 0
				? `Desen: ${pts.length} ${pts.length === 1 ? "punct" : "puncte"}`
				: drawn_features.length > 0
					? `Desen: ${drawn_features.length} ${drawn_features.length === 1 ? "formă" : "forme"} pe hartă`
					: ""
	} else if (is_area) {
		STATE.measure_readout =
			pts.length < 3
				? ""
				: `${format_area(polygon_area_m2(pts))}  ·  perimetru ${format_distance(
						path_length_m([...pts, pts[0]!]),
					)}`
	} else {
		STATE.measure_readout = pts.length < 2 ? "" : format_distance(path_length_m(pts))
	}
}

/** Intră/iese din modul de măsurare/desen. Schimbarea modului resetează punctele (altfel arie≠linie). */
function set_measure_mode(mode: "" | "distance" | "area" | "draw"): void {
	STATE.measure_mode = mode
	measure_points = []
	STATE.measure_readout = ""
	if (MAP) {
		MAP.getCanvas().style.cursor = mode ? "crosshair" : ""
		MAP.doubleClickZoom[mode ? "disable" : "enable"]() // dublu-click = „gata", nu zoom
	}
	// Măsurarea, pinul de sesizare, regimul și identify se exclud (toate consumă clickul pe hartă).
	if (mode && STATE.pin_active) {
		set_pin_mode(false)
	}
	if (mode && STATE.identify_mode) {
		STATE.identify_mode = false
	}
	if (mode && STATE.regim_mode) {
		set_regim_mode(false)
	}
	if (mode && STATE.edit_mode) {
		set_edit_mode(false) // editarea consumă și ea harta (Terra Draw) — un singur mod odată
	}
	render_measure()
}

function add_measure_point(lng: number, lat: number): void {
	measure_points.push([lng, lat])
	render_measure()
}

/** Șterge măsurătoarea ȘI formele desenate, dar RĂMÂNE în modul curent. */
function clear_measure(): void {
	measure_points = []
	drawn_features = []
	STATE.measure_readout = ""
	render_measure()
}

/**
 * Termină SCHIȚA curentă de desen liber → formă persistentă (rămâne în modul draw, pentru
 * următoarea formă). Convenție GIS: ultimul click PE primul punct (≤15px) ÎNCHIDE o zonă
 * (poligon); altfel forma e o linie. Sub 2 puncte = nimic de păstrat.
 */
function finalize_drawn_shape(): void {
	// Dublu-click-ul care termină forma emite și 2 click-uri normale → ultimul punct e dublat;
	// dedupe pe consecutive identice (altfel poligonul ar avea vertex duplicat).
	const pts = measure_points.filter(
		(p, i, all) => i === 0 || p[0] !== all[i - 1]![0] || p[1] !== all[i - 1]![1],
	)
	measure_points = []
	if (pts.length >= 2) {
		let closed = false
		if (pts.length >= 4 && MAP) {
			const a = MAP.project(pts[0]!)
			const b = MAP.project(pts[pts.length - 1]!)
			closed = Math.hypot(a.x - b.x, a.y - b.y) <= 15
		}
		drawn_features.push(
			closed
				? {
						type: "Feature",
						// fără punctul de închidere dat cu click — inelul se închide cu primul punct
						geometry: { type: "Polygon", coordinates: [[...pts.slice(0, -1), pts[0]!]] },
						properties: {},
					}
				: {
						type: "Feature",
						geometry: { type: "LineString", coordinates: pts },
						properties: {},
					},
		)
	}
	render_measure()
}

/** Ține-ți-o minte: dublu-click / Enter = gata (iese din mod, păstrează desenul pe hartă). */
function finish_measure(): void {
	STATE.measure_mode = ""
	if (MAP) {
		MAP.getCanvas().style.cursor = ""
		MAP.doubleClickZoom.enable()
	}
}

/** Ține highlight-ul deasupra layerelor tematice (cele adăugate ulterior ar acoperi). */

function apply_highlight(): void {
	if (!MAP) {
		return
	}
	const src = MAP.getSource(HIGHLIGHT_SRC) as maplibregl.GeoJSONSource | undefined
	if (!src) {
		return
	}
	const f = STATE.feature_detail
	src.setData(
		f && STATE.selected_feature_id
			? ({ type: "Feature", geometry: f.geometry, properties: {} } as GeoJSON.Feature)
			: EMPTY_HIGHLIGHT,
	)
}

// Reactiv: orice schimbare a selecției actualizează highlight-ul (indiferent de
// unde vine selecția: click pe hartă, tabel, căutare, identify sau URL).
van.derive(() => {
	void STATE.feature_detail
	void STATE.selected_feature_id
	apply_highlight()
})

// Selecția curentă se reflectă în URL (#/map?map=…&feature=…) — link partajabil,
// același format pe care îl emite MCP get_map_link. replaceState NU declanșează
// hashchange, deci router-ul nu re-montează pagina.
// GATE: nu rescrie URL-ul până când bootstrap-ul a CITIT deep-link-ul (?feature=/?lng=&lat=). Altfel
// derive-ul rulează la import cu STATE gol și șterge parametrii de deep-link înainte să-i consume nimeni.
let url_sync_ready = false
van.derive(() => {
	const map_slug = STATE.selected_map_slug
	const feature_id = STATE.selected_feature_id
	if (!url_sync_ready) {
		return
	}
	const hash = window.location.hash
	if (hash !== "#/map" && !hash.startsWith("#/map?")) {
		return
	}
	const qs = new URLSearchParams()
	if (map_slug) {
		qs.set("map", map_slug)
	}
	if (feature_id) {
		qs.set("feature", feature_id)
	} else {
		// Fără element selectat: PĂSTREAZĂ lng/lat dintr-un deep-link de locație (derive-ul nu le
		// gestionează activ, dar nu le aruncă) — ca linkul de coordonată să rămână în URL.
		const cur = new URLSearchParams(hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "")
		const lng = cur.get("lng")
		const lat = cur.get("lat")
		if (lng && lat) {
			qs.set("lng", lng)
			qs.set("lat", lat)
		}
	}
	const q = qs.toString()
	history.replaceState(null, "", `#/map${q ? "?" + q : ""}`)
})

// Cache of loaded GeoJSON data for bounds computation
const geojson_cache = new Map<string, GeoJSON.FeatureCollection>()

function source_id(map_slug: string, layer_slug: string): string {
	return `src-${map_slug}-${layer_slug}`
}

function layer_id_for(map_slug: string, layer_slug: string): string {
	return `lyr-${map_slug}-${layer_slug}`
}

function remove_map_layers(map_slug: string, layers: LayerSummary[]): void {
	// Geometriile exacte memorate pentru lipire sunt ale hărții/datei care pleacă: la altă hartă
	// n-au ce căuta, iar la altă dată de referință ar fi de-a dreptul GREȘITE (ruta de detaliu
	// întoarce versiunea cerută, nu neapărat cea afișată). Editarea e oricum blocată pe vederea
	// istorică, deci asta apără mai ales revenirea în prezent.
	reset_snap_cache()
	// Marcajele de trunchiere aparțin straturilor care tocmai pleacă (altă hartă / alt `as_of`).
	for (const l of layers) {
		write_layer_truncated(l.slug, false)
	}
	if (!MAP) {
		return
	}
	for (const layer of layers) {
		remove_filter_layers(map_slug, layer) // 2.2: și rândurile derivate
		const lid = layer_id_for(map_slug, layer.slug)
		const sid = source_id(map_slug, layer.slug)
		for (const suffix of ["__hatch", "__outline", "__label", "__3d"]) {
			const xid = lid + suffix
			if (added_layers.has(xid)) {
				try {
					MAP.removeLayer(xid)
				} catch (_) {
					/* ignore */
				}
				added_layers.delete(xid)
			}
		}
		if (added_layers.has(lid)) {
			try {
				MAP.removeLayer(lid)
			} catch (_) {
				/* ignore */
			}
			added_layers.delete(lid)
		}
		if (added_sources.has(sid)) {
			try {
				MAP.removeSource(sid)
			} catch (_) {
				/* ignore */
			}
			added_sources.delete(sid)
		}
		geojson_cache.delete(sid)
		snap_geojson_index.delete(sid)
	}
}

// Apelurile CONCURENTE pentru ACELAȘI strat se contopesc: load_all_layers și lazy-ul din
// sync_layer_visibility pot porni adăugarea simultan, iar secvența remove→add se întrețese
// (remove-ul unui source „în uz" eșuează TĂCUT dar set-ul e deja golit) → MapLibre aruncă
// „Source already exists" și harta rămâne pe jumătate încărcată.
const inflight_layer_adds = new Map<string, Promise<void>>()

// ─────────────────────────────────────────────────────────────
// Editare pe hartă (F13-T4): desen de elemente noi + editare geometrie/atribute/ștergere/revert
// peste API-ul EXISTENT (POST/PATCH/DELETE /api/features + /revert — gardat editor+ pe server).
// Geometria în lucru trăiește în managerul Terra Draw (draw.client, vendorat din vlib);
// aici e doar lipiciul: gating de rol, fluxuri, refresh-ul straturilor după scriere.
// ─────────────────────────────────────────────────────────────

let DRAW: DrawManager | null = null // moare odată cu instanța MAP (reset la re-montare)
// Momentul ultimei forme TERMINATE: dublu-click-ul de finish apucă altfel să ajungă și la
// handler-ele de click pe straturi (finish-ul vine pe PRIMUL click, edit_draw_active se golește,
// iar click-urile rămase din dublu-click selectau feature-ul de sub desen + deschideau popup-ul).
let last_draw_finish_at = 0

/** True cât timp un mod consumă clickul pe hartă (+ fereastra scurtă de după finish). */
function map_click_consumed(): boolean {
	return !!(
		STATE.measure_mode ||
		STATE.identify_mode ||
		STATE.pin_active ||
		STATE.regim_mode ||
		STATE.edit_draw_active ||
		Date.now() - last_draw_finish_at < 500
	)
}
let edit_pending_geometry: GeoJSON.Geometry | null = null // schița terminată, în așteptarea atributelor
let edit_from_gps = false // schița curentă vine de la butonul GPS → salvarea trimite `collect`
let edit_form: Record<string, string> = {} // valorile formularului de atribute (element nou)
// F9-T5: câmpurile + numele stratului nou — valorile în MODUL (tastarea nu re-randează
// formularul → focusul rămâne); STATE.edit_new_layer_name există DOAR pentru preview-ul de slug
let new_layer_fields: { name: string; type: string }[] = [{ name: "", type: "string" }]
let new_layer_name = ""
let edit_geom_rewrap = "" // familia Multi* a geometriei aflate în editare (re-împachetare la salvare)
let feature_edit_values: Record<string, string> = {} // formularul de atribute din FeaturePanel
let feature_edit_types: Record<string, string> = {} // typeof original — păstrăm tipul la salvare

/**
 * Poate userul curent să editeze harta selectată? Oglindește serverul (tenancy.decide):
 * super = tot; membru editor+ pe org-ul hărții; hartă neasignată = orice user autentificat;
 * support = doar citire. Refuzul REAL rămâne pe server — aici doar ascundem butoanele.
 */
function can_edit_map(): boolean {
	if (CTX.is_public || !STATE.map_detail) {
		return false
	}
	// Pe vederea istorică (as-of) editarea se ASCUNDE: ai desena peste un snapshot din trecut,
	// dar scrierea ar ateriza în prezent — iar refresh-ul (tot as-of) nici nu ți-ar arăta-o.
	if (STATE.as_of) {
		return false
	}
	const s = session.val
	if (!s.authenticated || !s.user) {
		return false
	}
	if (s.user.platform_role === "super") {
		return true
	}
	if (s.user.platform_role === "support") {
		return false
	}
	const org = STATE.map_detail.org_slug ?? ""
	const rank: Record<string, number> = { owner: 3, admin: 2, editor: 1, viewer: 0 }
	if (!org) {
		// org_id NULL (hartă demo/neasignată): scrierea cere STAFF editor+ al vreunui org —
		// oglindește decide() (un cetățean fără membership NU editează; constatare 25.08).
		return s.memberships.some((x) => (rank[x.role] ?? 0) >= 1)
	}
	const m = s.memberships.find((x) => x.org_slug === org)
	return !!m && (rank[m.role] ?? 0) >= 1
}

// ── Geometriile EXACTE pentru lipire (REQ-BD-037) ──────────────────────────────────────────────
// `queryRenderedFeatures` întoarce geometria DESENATĂ, nu cea din bază: MapLibre taie în tile-uri
// interne și sursele GeoJSON (extent 8192, tolerance 0.375), iar tile-urile noastre au extent 4096.
// În România, vârful de lipire e rotunjit cu ~10 cm la zoom 15 pe straturile servite ca date
// exacte și ~21 cm pe cele servite ca tile-uri; sub zoom 18 se pierd și vârfuri. Două parcele arată
// lipite pe ecran, dar coordonatele salvate diferă — „sliver"-ul pe care raportul topologic îl
// găsește abia după fapt. Ecranul rămâne deci pentru TRIAJ (ce obiecte sunt sub cursor), iar
// geometria vine exactă: din colecția deja adusă în client (sursele GeoJSON — gratis) sau de la
// `/api/features/:id`, memorată. Până sosește, folosim ce e pe ecran — deci niciodată mai rău.
const snap_exact = new GeometryCache()
const snap_geojson_index = new Map<string, Map<string, GeoJSON.Geometry>>() // sid → (id → geometrie)
const snap_pending = new Set<string>() // cereri în zbor + eșuate (nu reîncercăm la fiecare mișcare)

/** Uită tot ce știm despre geometriile exacte: după scrieri, la alt `as_of`, la altă hartă. */
function reset_snap_cache(): void {
	snap_exact.clear()
	snap_geojson_index.clear()
	snap_pending.clear()
}

/** Indexul id → geometrie al unei surse GeoJSON, construit LENEȘ (o dată per sursă). */
function snap_index_for(sid: string): Map<string, GeoJSON.Geometry> | null {
	const cached = snap_geojson_index.get(sid)
	if (cached) {
		return cached
	}
	const fc = geojson_cache.get(sid)
	if (!fc) {
		return null // sursă vectorială (tile-uri) — geometria exactă se cere de la server
	}
	const index = index_by_id(fc)
	snap_geojson_index.set(sid, index)
	return index
}

/** Aduce geometria reală a unui obiect și o memorează (fire-and-forget; o singură dată per id). */
function warm_snap_geometry(id: string): void {
	if (!id || snap_exact.has(id) || snap_pending.has(id)) {
		return
	}
	snap_pending.add(id)
	void (async () => {
		// Editarea e blocată pe vederea istorică (`can_edit_map`), deci aici `as_of` e mereu gol —
		// îl transmitem totuși, ca o cerere venită dintr-un context viitor să nu ia tăcut prezentul.
		const r = await api_get<FeatureDetail>(feature_url(id, STATE.as_of))
		if (r.ok && r.data.geometry) {
			snap_exact.set(id, r.data.geometry as GeoJSON.Geometry)
			snap_pending.delete(id) // reușită: cheia trăiește în cache
		}
		// La eșec id-ul RĂMÂNE în `snap_pending`: altfel am reîncerca la fiecare mișcare de mouse.
	})()
}

const SNAP_WARM_FACTOR = 3 // raza de aducere în avans, față de toleranța de lipire
const SNAP_WARM_MS = 120 // cât de des interogăm fereastra largă (aducerea e oricum deduplicată)
let snap_warm_at = 0

/**
 * Sursa geometriilor de LIPIRE (contract plan/contracts/snapping.md): geometriile din fereastra
 * de ±toleranță din jurul cursorului, prin indexarea MapLibre (`queryRenderedFeatures`), de pe
 * stratul editat (+ opțional straturile vizibile). Citește STATE LA APEL — managerul e lazy și
 * persistă între straturi. Doar fill/line/circle + conturul (lid, oid), NU etichetele (ancora
 * textului ar da un vertex fals). Exclude obiectul aflat în editare (id MVT = properties.feature_id,
 * GeoJSON = f.id) — altfel vertexul tras s-ar lipi de propria poziție veche.
 *
 * Geometria întoarsă e cea EXACTĂ când o avem (vezi mai sus); aducerea în avans se face pe o
 * fereastră mai LARGĂ decât toleranța, ca geometria vecinilor să fie deja în memorie când cursorul
 * intră în raza de lipire — altfel primul clic deasupra unui obiect ar prinde varianta de pe ecran.
 */
function snap_source(q: SnapQuery): GeoJSON.Geometry[] {
	const d = STATE.map_detail
	if (!MAP || !d) {
		return []
	}
	const map_slug = STATE.selected_map_slug
	// Stratul PRINCIPAL de lipire: la „Editează geometria" e stratul OBIECTULUI editat (fluxul nu
	// setează edit_layer_slug — acela e ținta elementelor NOI, gol sau alt strat), altfel ținta.
	const primary = STATE.edit_geom_feature
		? STATE.selected_feature_layer || STATE.feature_detail?.layer_slug || STATE.edit_layer_slug
		: STATE.edit_layer_slug
	const layers = STATE.edit_snap_all
		? d.layers.filter((l) => STATE.layer_visible[l.slug] !== false)
		: d.layers.filter((l) => l.slug === primary)
	const ids: string[] = []
	for (const l of layers) {
		const lid = layer_id_for(map_slug, l.slug)
		for (const suffix of ["", "__outline"]) {
			if (MAP.getLayer(lid + suffix)) {
				ids.push(lid + suffix)
			}
		}
	}
	if (ids.length === 0) {
		return []
	}
	const tol = q.tolerance_px
	const editing = STATE.edit_geom_feature
	const own = (f: maplibregl.MapGeoJSONFeature): string =>
		String(f.id ?? f.properties?.feature_id ?? "")
	const box = (r: number): [[number, number], [number, number]] => [
		[q.x - r, q.y - r],
		[q.x + r, q.y + r],
	]

	// Aducerea ÎN AVANS, pe o fereastră mai largă decât toleranța: până cursorul intră efectiv în
	// raza de lipire, geometria vecinilor e deja în memorie. Limitată în timp — altfel ar interoga
	// o suprafață de 9× la fiecare mișcare de mouse, degeaba (cererile sunt oricum deduplicate).
	const now = Date.now()
	if (now - snap_warm_at > SNAP_WARM_MS) {
		snap_warm_at = now
		for (const f of MAP.queryRenderedFeatures(box(tol * SNAP_WARM_FACTOR), { layers: ids })) {
			const id = own(f)
			if (id && id !== editing && !snap_index_for(f.source)?.has(id)) {
				warm_snap_geometry(id)
			}
		}
	}

	// Rezultatul rămâne pe fereastra de TOLERANȚĂ, ca înainte — doar geometria e alta: cea exactă
	// când o avem, altfel cea de pe ecran (deci niciodată mai rău decât comportamentul de dinainte).
	return MAP.queryRenderedFeatures(box(tol), { layers: ids })
		.filter((f) => !editing || own(f) !== editing)
		.map((f) => {
			const id = own(f)
			const exact = id ? (snap_index_for(f.source)?.get(id) ?? snap_exact.get(id)) : undefined
			return (exact ?? f.geometry) as GeoJSON.Geometry
		})
}

/** Managerul Terra Draw al instanței curente de hartă (creat lazy, la prima editare). */
function draw_manager(): DrawManager | null {
	if (!MAP) {
		return null
	}
	if (!DRAW) {
		DRAW = create_draw_manager(
			MAP,
			{
				finish: on_draw_finish,
				update: () => {
					STATE.edit_geom_dirty = true
				},
			},
			// 4.5: lipirea la obiecte — sursa citește STATE la apel; starea butonului din STATE.
			{ snap_source, snapping: STATE.edit_snapping },
		)
	}
	return DRAW
}

/** Butonul „Lipire la obiecte" (4.5) — comută managerul + memorează alegerea pentru sesiune. */
function set_edit_snapping(on: boolean): void {
	STATE.edit_snapping = on
	DRAW?.set_snapping(on)
}

/** O schiță e gata (dublu-click/închidere): oprim desenul și deschidem formularul de atribute. */
function on_draw_finish(f: DrawnFeature): void {
	if (STATE.edit_geom_feature) {
		return // în editarea de geometrie, `finish` de la drag e raportat ca update, nu ca schiță
	}
	last_draw_finish_at = Date.now()
	edit_pending_geometry = f.geometry
	edit_from_gps = false // schiță desenată manual — fără amprente de colectare
	STATE.edit_gps_accuracy = 0
	STATE.edit_pending = true
	STATE.edit_draw_active = ""
	STATE.edit_notice = ""
	draw_manager()?.set_mode(null) // schița rămâne vizibilă (render), harta nu mai desenează
	if (MAP) {
		MAP.getCanvas().style.cursor = ""
		// NU sincron: forma se termină chiar PE dublu-click — reactivarea imediată ar lăsa
		// același dublu-click să facă zoom și harta ar sări de sub formularul de atribute.
		const m = MAP
		setTimeout(() => {
			if (MAP === m && !STATE.edit_draw_active && !STATE.measure_mode) {
				m.doubleClickZoom.enable()
			}
		}, 350)
	}
}

/** Aruncă schița/geometria din editor și închide sub-fluxurile. NU atinge serverul. */
function cancel_edit_session(): void {
	DRAW?.clear()
	DRAW?.set_mode(null)
	edit_pending_geometry = null
	edit_form = {}
	edit_geom_rewrap = ""
	edit_from_gps = false
	STATE.edit_gps_pending = false
	STATE.edit_gps_accuracy = 0
	STATE.edit_draw_active = ""
	STATE.edit_pending = false
	STATE.edit_geom_feature = ""
	STATE.edit_geom_dirty = false
	STATE.edit_saving = false
	STATE.edit_error = ""
	// Nu călca peste modul care tocmai ne-a închis (măsurarea își vrea cursorul + dblclick-ul ei).
	if (MAP && !STATE.measure_mode && !STATE.pin_active && !STATE.regim_mode) {
		MAP.getCanvas().style.cursor = ""
		MAP.doubleClickZoom.enable()
	}
}

/** Intră/iese din modul editare. La intrare închide modurile care consumă clickul pe hartă. */
function set_edit_mode(on: boolean): void {
	STATE.edit_mode = on
	if (on) {
		if (STATE.measure_mode) {
			set_measure_mode("")
		}
		clear_measure() // și rezultatul rămas pe hartă — panoul de editare îi ia locul (top-14)
		finish_measure()
		if (STATE.pin_active) {
			set_pin_mode(false)
		}
		if (STATE.regim_mode) {
			set_regim_mode(false)
		}
		STATE.identify_mode = false
		STATE.edit_notice = ""
		// Strat țintă implicit: cel al tabelului deschis sau primul strat al hărții.
		if (!STATE.edit_layer_slug) {
			const first = STATE.table_layer_slug || STATE.map_detail?.layers?.[0]?.slug || ""
			if (first) {
				void select_edit_layer(first)
			}
		}
	} else {
		cancel_edit_session()
		STATE.edit_notice = ""
	}
}

/** Alege stratul țintă pentru elemente noi + îi încarcă attribute_schema (formularul). */
/** Normalizează attribute_schema (ambele formate istorice) în rândurile formularului. */
function normalize_edit_schema(
	schema: Record<string, unknown>,
): { key: string; type: string; values: ListValue[] | null }[] {
	// Cheile de SISTEM (amprente de colectare/poze) nu apar în formular — serverul le
	// scrie singur și le respinge la PATCH. `values` (1.6, contract liste_valori.md): lista
	// ÎNCHISĂ de valori permise → <select> în formular; null = input liber.
	return Object.entries(schema)
		.filter(([key]) => !SYSTEM_FEATURE_KEYS.includes(key))
		.map(([key, type]) => ({
			key,
			type: String(
				(typeof type === "object" && type !== null ? (type as { type?: unknown }).type : type) ??
					"",
			),
			values: allowed_values(schema, key),
		}))
}

// Schema BRUTĂ a stratului țintă (pentru validarea locală a listelor de valori, 1.6) — în modul,
// nu în STATE: nu se randează. Setată odată cu STATE.edit_schema.
let edit_schema_raw: Record<string, unknown> = {}
// Schemele straturilor, cache per slug — formularul de atribute din FeaturePanel are nevoie de
// listele de valori ale stratului elementului selectat (1.6); un GET per strat, apoi din cache.
// Cheiat pe `<hartă>/<strat>` — același slug de strat există pe mai multe hărți (ex. „utr"), iar
// cache-ul e la nivel de modul; golit oricum la re-montare (altă hartă = alte scheme).
const layer_schema_cache = new Map<string, Record<string, unknown>>()
function schema_cache_key(map_slug: string, slug: string): string {
	return `${map_slug}/${slug}`
}
let feature_edit_schema: Record<string, unknown> = {} // schema stratului elementului în editare

/** Schema de atribute a unui strat (cache), sau {} dacă nu se poate citi. */
async function load_layer_schema(map_slug: string, slug: string): Promise<Record<string, unknown>> {
	const key = schema_cache_key(map_slug, slug)
	const hit = layer_schema_cache.get(key)
	if (hit) {
		return hit
	}
	const r = await api_get<{ config?: { attribute_schema?: Record<string, unknown> } }>(
		`/api/maps/${encodeURIComponent(map_slug)}/layers/${encodeURIComponent(slug)}`,
	)
	const schema = r.ok && r.data?.config?.attribute_schema ? r.data.config.attribute_schema : {}
	layer_schema_cache.set(key, schema)
	return schema
}

async function select_edit_layer(
	slug: string,
	// F9-T5: stratul abia creat — schema e deja în mână, al 3-lea GET ar re-descărca fix ce
	// tocmai am trimis (RTT gratuit pe 4G, constatare review 27.08).
	preset_schema?: Record<string, unknown>,
): Promise<void> {
	STATE.edit_layer_slug = slug
	// Schița în lucru era pentru stratul vechi — o aruncăm (geometria poate să nu mai aibă sens).
	DRAW?.clear()
	edit_pending_geometry = null
	edit_form = {}
	STATE.edit_pending = false
	STATE.edit_draw_active = ""
	STATE.edit_error = ""
	STATE.edit_schema = []
	// Schimbarea stratului țintă invalidează schița GPS în curs (bannerul de precizie cu ea).
	edit_from_gps = false
	STATE.edit_gps_accuracy = 0
	const map_slug = STATE.selected_map_slug
	if (!map_slug || !slug) {
		return
	}
	if (preset_schema) {
		edit_schema_raw = preset_schema
		layer_schema_cache.set(schema_cache_key(map_slug, slug), preset_schema)
		STATE.edit_schema = normalize_edit_schema(preset_schema)
		return
	}
	STATE.edit_schema_loading = true
	// schema se poate schimba în admin între două selectări
	layer_schema_cache.delete(schema_cache_key(map_slug, slug))
	const schema = await load_layer_schema(map_slug, slug)
	STATE.edit_schema_loading = false
	if (STATE.edit_layer_slug === slug) {
		// Schema vine în DOUĂ formate istorice: `{cheie: "String"}` (GDAL brut) sau
		// `{cheie: {type: "string"}}` (importuri noi) — vezi normalize_edit_schema.
		edit_schema_raw = schema
		STATE.edit_schema = normalize_edit_schema(schema)
	}
}

/** F9-T5: deschide/închide formularul „Strat nou de colectare" — reset complet la ambele. */
function toggle_new_layer_form(open: boolean): void {
	// O schiță desenată dar nesalvată s-ar pierde TĂCUT (formularul o ascunde, iar selecția
	// stratului nou o șterge) — cerem confirmare și o aruncăm explicit (constatare review 27.08).
	if (open && STATE.edit_pending) {
		if (!confirm("Aveți o schiță nesalvată — se pierde dacă treceți la stratul nou. Continuați?")) {
			return
		}
		DRAW?.clear()
		edit_pending_geometry = null
		edit_form = {}
		edit_from_gps = false
		STATE.edit_gps_accuracy = 0
		STATE.edit_pending = false
	}
	STATE.edit_new_layer_open = open
	new_layer_name = ""
	STATE.edit_new_layer_name = ""
	STATE.edit_new_layer_geom = "Point"
	STATE.edit_new_layer_error = ""
	STATE.edit_new_layer_busy = false
	new_layer_fields = [{ name: "", type: "string" }]
	STATE.edit_new_layer_rows = 1
}

/**
 * F9-T5: creează stratul de colectare din formular și aterizează DIRECT în colectare pe el.
 * Slug-ul se derivă din nume (slugify partajat cu importul); coliziunile — inclusiv cu
 * slug-urile straturilor ȘTERSE, care rămân ocupate — se rezolvă cu sufix `_2`…`_9`.
 * Stratul apare pe hartă FĂRĂ reload: re-fetch detaliul hărții → sursă nouă → select.
 */
async function create_field_layer(): Promise<void> {
	const map_slug = STATE.selected_map_slug
	const name = new_layer_name.trim()
	if (!map_slug || STATE.edit_new_layer_busy) {
		return
	}
	if (!name) {
		STATE.edit_new_layer_error = "Dați un nume stratului."
		return
	}
	const attribute_schema: Record<string, unknown> = {}
	for (const f of new_layer_fields) {
		// slug_key (fără fallback): o etichetă degenerată („#", „---") NU devine tăcut o
		// coloană botezată „layer"; dublurile după slugificare păstrează primul rând.
		const key = slug_key(f.name.trim())
		if (key && !(key in attribute_schema)) {
			attribute_schema[key] = { type: f.type } // formatul NOU, aliniat cu importul
		}
	}
	STATE.edit_new_layer_busy = true
	STATE.edit_new_layer_error = ""
	// UN singur POST: serverul alocă primul slug liber (unique_slug) — fără probe repetate.
	const r = await api_post<{ id: string; slug: string }>(
		`/api/maps/${encodeURIComponent(map_slug)}/layers`,
		{
			slug: slugify(name),
			name,
			geometry_type: STATE.edit_new_layer_geom,
			attribute_schema,
			role: "colectare",
			unique_slug: true,
		},
	)
	if (!r.ok) {
		STATE.edit_new_layer_busy = false
		STATE.edit_new_layer_error = r.err?.message ?? "Crearea stratului a eșuat."
		return
	}
	const created = r.data
	// Apariția dinamică: detaliul hărții se re-încarcă (selectul de strat + sidebar-ul citesc
	// map_detail), sursa nouă se adaugă pe hartă, highlight-ul rămâne deasupra (aceiași
	// invarianți ca la load_map: ensure + raise + apply).
	const res = await api_get<MapDetail>(`/api/maps/${encodeURIComponent(map_slug)}`)
	if (!res.ok) {
		// Stratul EXISTĂ pe server, dar harta nu s-a putut reîncărca (4G căzut fix între cele
		// două cereri) — fără mesaj, UI-ul ar fi mințit că totul e gata (constatare review 27.08).
		STATE.edit_new_layer_busy = false
		STATE.edit_new_layer_error =
			"Stratul a fost creat, dar harta nu s-a putut reîncărca — reîncărcați pagina."
		return
	}
	STATE.map_detail = res.data
	explicit_visibility.add(created.slug)
	STATE.layer_visible = { ...STATE.layer_visible, [created.slug]: true }
	const idx = res.data.layers.findIndex((l) => l.slug === created.slug)
	if (idx >= 0 && MAP) {
		await add_layer_to_map(map_slug, res.data.layers[idx]!, idx, STATE.as_of)
		ensure_highlight_layers()
		restack_layers(map_slug)
		apply_highlight()
	}
	STATE.edit_new_layer_busy = false
	toggle_new_layer_form(false)
	await select_edit_layer(created.slug, attribute_schema)
	STATE.edit_notice = `Strat nou creat: „${name}” — colectați direct în el.`
}

/** Pornește desenul unei forme noi pe stratul țintă (toggle: re-apăsarea oprește). */
function start_edit_draw(mode: DrawMode): void {
	const d = draw_manager()
	if (!d) {
		return
	}
	if (STATE.edit_draw_active === mode) {
		d.set_mode(null)
		STATE.edit_draw_active = ""
		if (MAP) {
			MAP.getCanvas().style.cursor = ""
			MAP.doubleClickZoom.enable()
		}
		return
	}
	// O singură schiță în lucru: pornirea unui desen nou aruncă schița neconfirmată
	// (inclusiv una din GPS — altfel bannerul de precizie rămânea stale, constatare review 26.08).
	d.clear()
	edit_pending_geometry = null
	edit_from_gps = false
	STATE.edit_gps_accuracy = 0
	STATE.edit_pending = false
	STATE.edit_error = ""
	d.set_mode(mode)
	STATE.edit_draw_active = mode
	if (MAP) {
		MAP.getCanvas().style.cursor = "crosshair"
		MAP.doubleClickZoom.disable() // dublu-click termină forma, nu face zoom
	}
}

/** Tipul de geometrie al stratului țintă (pentru butoanele de desen permise). */
function edit_layer_geometry(): string {
	const l = STATE.map_detail?.layers?.find((x) => x.slug === STATE.edit_layer_slug)
	return l?.geometry_type ?? ""
}

/**
 * F9-T2: „punct la locația mea" — captură GPS pe stratul țintă (doar familia punct).
 * Punctul intră în ACELAȘI flux ca desenul manual (edit_pending → formular), dar marcat
 * `edit_from_gps` → salvarea trimite `collect` și serverul scrie amprentele.
 */
function capture_gps_point(): void {
	if (STATE.edit_gps_pending || typeof navigator === "undefined" || !navigator.geolocation) {
		return
	}
	// O singură schiță în lucru — capturarea aruncă desenul neconfirmat (ca start_edit_draw).
	DRAW?.clear()
	DRAW?.set_mode(null)
	edit_pending_geometry = null
	STATE.edit_pending = false
	STATE.edit_draw_active = ""
	STATE.edit_error = ""
	STATE.edit_gps_pending = true
	navigator.geolocation.getCurrentPosition(
		(pos) => {
			STATE.edit_gps_pending = false
			const lng = pos.coords.longitude
			const lat = pos.coords.latitude
			edit_pending_geometry = { type: "Point", coordinates: [lng, lat] }
			edit_from_gps = true
			STATE.edit_gps_accuracy = Math.round((pos.coords.accuracy ?? 0) * 10) / 10
			STATE.edit_pending = true
			// Punctul capturat se VEDE pe hartă ca schiță (ca la desenul manual — altfel formularul
			// apare „din nimic"; constatare la testarea din 26.08). set_mode(null) = doar afișat,
			// ne-trăgabil (geometria salvată e cea din GPS, nu una mutată din greșeală).
			const d = draw_manager()
			if (d) {
				d.clear()
				d.add_feature({ type: "Point", coordinates: [lng, lat] }, "point")
				d.set_mode(null)
			}
			MAP?.flyTo({ center: [lng, lat], zoom: Math.max(MAP.getZoom(), 18) })
		},
		(err) => {
			STATE.edit_gps_pending = false
			STATE.edit_error =
				err && err.code === 1 // PERMISSION_DENIED
					? "Accesul la locație e blocat — permiteți-l din setările browserului."
					: "Locația nu e disponibilă — reîncercați (semnal GPS slab?)."
		},
		{ enableHighAccuracy: true, timeout: 10000 },
	)
}

/** Re-încarcă sursa unui strat după o scriere (serverul a invalidat deja stamp-ul). */
async function refresh_layer(slug: string): Promise<void> {
	const detail = STATE.map_detail
	const map_slug = STATE.selected_map_slug
	if (!detail || !map_slug || !MAP) {
		return
	}
	const idx = detail.layers.findIndex((l) => l.slug === slug)
	if (idx < 0) {
		return
	}
	// `refresh_layer` vine DUPĂ o scriere: geometria memorată pentru lipire poate fi cea veche.
	// Golim tot (se reumple la cerere) — mai ieftin decât să ținem socoteala id-urilor atinse.
	reset_snap_cache()
	await add_layer_to_map(map_slug, detail.layers[idx], idx, STATE.as_of)
	// `refresh_layer` = removeLayer + addLayer, deci stratul editat ar rămâne în vârf: ordinea
	// configurată s-ar rupe la PRIMA editare a unui obiect, fără reload și fără niciun semnal.
	restack_layers(map_slug)
}

/** Eticheta prietenoasă a tipului de atribut (placeholder în formular): „text", „număr"… */
function schema_type_label(type: string): string {
	if (/int|real|float|double|decimal|number/i.test(type)) {
		return "număr"
	}
	if (/bool/i.test(type)) {
		return "da / nu"
	}
	if (/date|time/i.test(type)) {
		return "dată"
	}
	if (/string|text|char/i.test(type)) {
		return "text"
	}
	return type // tip necunoscut — îl arătăm brut, mai bine decât nimic
}

/** Valoarea unui câmp din formular, coerce-uită după tipul din attribute_schema (GDAL). */
function coerce_schema_value(raw: string, type: string): unknown {
	const v = raw.trim()
	if (v === "") {
		return undefined // câmp gol = atributul nu se scrie
	}
	if (/int|real|float|double|decimal|number/i.test(type)) {
		const n = Number(v)
		return Number.isFinite(n) ? n : v // nu blocăm salvarea pe un „N/A" — rămâne text
	}
	// bool (F9-T5, câmpurile „da / nu"): fără ramura asta, schema declara boolean iar valoarea
	// stocată era STRING („nu" e truthy). Nerecunoscut → nu se scrie (placeholder-ul ghidează).
	if (/bool/i.test(type)) {
		if (/^(da|true|1)$/i.test(v)) {
			return true
		}
		if (/^(nu|false|0)$/i.test(v)) {
			return false
		}
		return undefined
	}
	return v
}

/** Salvează schița desenată ca feature nou pe stratul țintă (POST /api/features). */
async function save_new_feature(): Promise<void> {
	const map_slug = STATE.selected_map_slug
	const layer_slug = STATE.edit_layer_slug
	if (!map_slug || !layer_slug || !edit_pending_geometry || STATE.edit_saving) {
		return
	}
	STATE.edit_saving = true
	STATE.edit_error = ""
	const properties: Record<string, unknown> = {}
	for (const { key, type } of STATE.edit_schema) {
		const v = coerce_schema_value(edit_form[key] ?? "", type)
		if (v !== undefined) {
			properties[key] = v
		}
	}
	// 1.6: validare LOCALĂ a listelor de valori — același mesaj ca al serverului, fără round-trip.
	const list_problem = value_list_problem(edit_schema_raw, properties)
	if (list_problem) {
		STATE.edit_saving = false
		STATE.edit_error = list_problem
		return
	}
	const geometry = wrap_geometry_for_layer(edit_pending_geometry, edit_layer_geometry())
	const r = await api_post<{ id: string }>("/api/features", {
		map: map_slug,
		layer: layer_slug,
		geometry,
		properties,
		message: edit_from_gps ? "colectat din teren (GPS)" : "creat din editorul hărții",
		// F9-T2: punct capturat cu GPS-ul → serverul scrie amprentele (collected_*) pe
		// identitatea din sesiune; desenul manual nu se ștampilează.
		...(edit_from_gps
			? {
					collect: {
						source: "gps" as const,
						...(STATE.edit_gps_accuracy > 0 ? { accuracy_m: STATE.edit_gps_accuracy } : {}),
					},
				}
			: {}),
	})
	STATE.edit_saving = false
	if (!r.ok) {
		STATE.edit_error = r.err?.message ?? "Salvarea a eșuat."
		return
	}
	DRAW?.clear()
	edit_pending_geometry = null
	edit_form = {}
	edit_from_gps = false
	STATE.edit_gps_accuracy = 0
	STATE.edit_pending = false
	STATE.edit_notice = "Element salvat pe strat."
	await refresh_layer(layer_slug)
}

/** Încarcă geometria entității selectate în editorul de vertecși (din FeaturePanel). */
function start_geometry_edit(): void {
	const f = STATE.feature_detail
	const d = draw_manager()
	if (!f || !d) {
		return
	}
	const parts = unwrap_for_edit(f.geometry)
	if (!parts) {
		STATE.feature_edit_error =
			"Geometrie multi-parte sau necunoscută — nu se poate edita pe hartă (folosiți re-importul)."
		return
	}
	cancel_edit_session() // curat: fără schițe rămase de la alt flux
	set_edit_mode(true) // panoul de editare devine consola fluxului (Salvează/Renunță)
	const tid = d.add_feature(parts.geometry, parts.mode)
	if (!tid) {
		STATE.edit_error = "Geometria nu a putut fi încărcată în editor."
		return
	}
	edit_geom_rewrap = parts.rewrap
	STATE.edit_geom_feature = f.id
	STATE.edit_geom_dirty = false
	d.set_mode("select")
	d.select_feature(tid)
	STATE.edit_draw_active = "select"
	STATE.edit_notice = ""
}

/** Salvează geometria editată (PATCH — versiune nouă `update_geom` pe server). */
async function save_geometry_edit(): Promise<void> {
	const id = STATE.edit_geom_feature
	const d = DRAW
	if (!id || !d || STATE.edit_saving) {
		return
	}
	const sketch = d.get_features()[0]
	if (!sketch) {
		STATE.edit_error = "Nu mai există geometrie în editor."
		return
	}
	STATE.edit_saving = true
	STATE.edit_error = ""
	const geometry = rewrap_geometry(sketch.geometry, edit_geom_rewrap)
	const r = await api_patch<{ id: string }>(`/api/features/${encodeURIComponent(id)}`, {
		geometry,
		message: "geometrie editată pe hartă",
	})
	STATE.edit_saving = false
	if (!r.ok) {
		STATE.edit_error = r.err?.message ?? "Salvarea geometriei a eșuat."
		return
	}
	const layer_slug = STATE.selected_feature_layer || STATE.feature_detail?.layer_slug || ""
	cancel_edit_session()
	STATE.edit_notice = "Geometrie salvată."
	if (layer_slug) {
		await refresh_layer(layer_slug)
	}
	if (STATE.selected_feature_id === id) {
		load_feature(id) // panoul din dreapta arată versiunea nouă
	}
}

/** Deschide formularul de atribute al entității selectate (valori + tipuri originale). */
function open_feature_edit(): void {
	const f = STATE.feature_detail
	if (!f) {
		return
	}
	feature_edit_values = {}
	feature_edit_types = {}
	for (const [k, v] of Object.entries(f.properties ?? {})) {
		if (SYSTEM_FEATURE_KEYS.includes(k)) {
			continue // amprente/poze: doar de citire — serverul respinge oricum PATCH-ul pe ele
		}
		feature_edit_types[k] = typeof v
		feature_edit_values[k] = v === null || v === undefined ? "" : String(v)
	}
	STATE.feature_edit_error = ""
	STATE.feature_edit_open = true
	// 1.6: listele de valori ale stratului elementului → <select> în formular (async, re-randare
	// prin feature_edit_rev; până vine schema, câmpurile sunt inputuri libere).
	feature_edit_schema = {}
	const layer_slug = STATE.selected_feature_layer || f.layer_slug || ""
	const map_slug = STATE.selected_map_slug
	if (layer_slug && map_slug && !CTX.is_public) {
		void load_layer_schema(map_slug, layer_slug).then((schema) => {
			if (STATE.feature_edit_open && STATE.feature_detail?.id === f.id) {
				feature_edit_schema = schema
				STATE.feature_edit_rev++
			}
		})
	}
}

/** Salvează atributele editate (PATCH properties = upsert pe cheile trimise). */
async function save_feature_edit(): Promise<void> {
	const f = STATE.feature_detail
	if (!f || STATE.feature_edit_saving) {
		return
	}
	STATE.feature_edit_saving = true
	STATE.feature_edit_error = ""
	const properties: Record<string, unknown> = {}
	for (const [k, raw] of Object.entries(feature_edit_values)) {
		const original = (f.properties ?? {})[k]
		const v = raw.trim()
		// Păstrăm tipul original: numerele rămân numere (altfel „12" ar deveni text la re-salvare).
		if (feature_edit_types[k] === "number" && v !== "" && Number.isFinite(Number(v))) {
			properties[k] = Number(v)
		} else if (feature_edit_types[k] === "boolean" && (v === "true" || v === "false")) {
			properties[k] = v === "true"
		} else {
			properties[k] = v === "" ? null : v
		}
		// Nemodificat = nu-l trimitem (istoric curat: doar cheile chiar schimbate).
		const orig_str = original === null || original === undefined ? "" : String(original)
		if (orig_str === raw) {
			delete properties[k]
		}
	}
	if (Object.keys(properties).length === 0) {
		STATE.feature_edit_saving = false
		STATE.feature_edit_open = false
		return
	}
	// 1.6: validare locală a listelor de valori pe cheile trimise (mesajul serverului, fără round-trip).
	const list_problem = value_list_problem(feature_edit_schema, properties)
	if (list_problem) {
		STATE.feature_edit_saving = false
		STATE.feature_edit_error = list_problem
		return
	}
	const r = await api_patch<{ id: string }>(`/api/features/${encodeURIComponent(f.id)}`, {
		properties,
		message: "atribute editate pe hartă",
	})
	STATE.feature_edit_saving = false
	if (!r.ok) {
		STATE.feature_edit_error = r.err?.message ?? "Salvarea atributelor a eșuat."
		return
	}
	STATE.feature_edit_open = false
	load_feature(f.id)
	const layer_slug = STATE.selected_feature_layer || f.layer_slug || ""
	if (layer_slug) {
		void refresh_layer(layer_slug) // etichetele/stilurile pot depinde de atribute
	}
}

let map_notice_timer: ReturnType<typeof setTimeout> | undefined
/** Arată o notiță pe hartă pentru câteva secunde (cea nouă o înlocuiește pe cea veche). */
function show_map_notice(message: string, ms = 12000): void {
	STATE.map_notice = message
	clearTimeout(map_notice_timer)
	map_notice_timer = setTimeout(() => {
		STATE.map_notice = ""
	}, ms)
}

/** Șterge entitatea selectată (tombstone — istoricul rămâne; confirmare explicită). */
async function delete_selected_feature(): Promise<void> {
	const f = STATE.feature_detail
	if (!f) {
		return
	}
	if (
		!confirm(
			"Ștergi acest element de pe strat? Ștergerea e versionată (istoricul rămâne) și poate fi anulată printr-un revert făcut de un editor.",
		)
	) {
		return
	}
	const r = await api_delete<{ id: string; anchored_documents?: number }>(
		`/api/features/${encodeURIComponent(f.id)}`,
		{ message: "șters din editorul hărții" },
	)
	if (!r.ok) {
		STATE.feature_edit_error = r.err?.message ?? "Ștergerea a eșuat."
		return
	}
	const layer_slug = STATE.selected_feature_layer || f.layer_slug || ""
	STATE.selected_feature_id = ""
	STATE.feature_detail = null
	STATE.feature_edit_open = false
	// Obiectul avea acte ancorate (O-C-7): ștergerea nu e refuzată, dar se spune — actele nu dispar.
	const acts = r.data.anchored_documents ?? 0
	if (acts > 0) {
		show_map_notice(
			`Element șters. Avea ${acts === 1 ? "un act ancorat" : `${acts} acte ancorate`}: ` +
				`${acts === 1 ? "rămâne" : "rămân"} în Documente, iar pe harta actelor ` +
				`${acts === 1 ? "apare" : "apar"} la ultima poziție a obiectului.`,
		)
	}
	if (layer_slug) {
		await refresh_layer(layer_slug)
	}
}

/** Revert la o versiune anterioară (buton în istoricul din FeaturePanel). */
async function revert_selected_feature(version: number): Promise<void> {
	const f = STATE.feature_detail
	if (!f) {
		return
	}
	if (
		!confirm(
			`Revii la versiunea v${version}? Se creează o versiune NOUĂ cu geometria și atributele de atunci (nimic nu se pierde).`,
		)
	) {
		return
	}
	const r = await api_post<{ id: string }>(`/api/features/${encodeURIComponent(f.id)}/revert`, {
		version,
		message: `revert din editorul hărții la v${version}`,
	})
	if (!r.ok) {
		STATE.feature_edit_error = r.err?.message ?? "Revert-ul a eșuat."
		return
	}
	load_feature(f.id)
	const layer_slug = STATE.selected_feature_layer || f.layer_slug || ""
	if (layer_slug) {
		void refresh_layer(layer_slug)
	}
}

function add_layer_to_map(
	map_slug: string,
	layer: LayerSummary,
	idx: number,
	as_of: string,
): Promise<void> {
	const key = layer_id_for(map_slug, layer.slug)
	const existing = inflight_layer_adds.get(key)
	if (existing) {
		return existing
	}
	const p = add_layer_to_map_inner(map_slug, layer, idx, as_of).finally(() => {
		inflight_layer_adds.delete(key)
	})
	inflight_layer_adds.set(key, p)
	return p
}

async function add_layer_to_map_inner(
	map_slug: string,
	layer: LayerSummary,
	idx: number,
	as_of: string,
): Promise<void> {
	if (!MAP) {
		return
	}
	const sid = source_id(map_slug, layer.slug)
	const lid = layer_id_for(map_slug, layer.slug)

	// 2.2: rândurile derivate ale stratului pleacă primele (se re-adaugă la final dacă sunt aprinse)
	remove_filter_layers(map_slug, layer)

	// remove existing if present — TOATE sub-layerele (fill/contur/label/3D) ÎNAINTE de sursă:
	// removeSource pe o sursă „în uz" (ex. __outline rămas) eșuează TĂCUT, dar evidența
	// added_sources era deja golită → următorul addSource arunca „Source already exists" și
	// stratul rămânea neîncărcat (console-ul Măxineni, 19.08).
	for (const suffix of ["__3d", "__label", "__outline", "__hatch", ""]) {
		const xid = lid + suffix
		if (added_layers.has(xid) || MAP.getLayer(xid)) {
			try {
				MAP.removeLayer(xid)
			} catch (_) {
				/* ignore */
			}
			added_layers.delete(xid)
		}
	}
	if (added_sources.has(sid)) {
		try {
			MAP.removeSource(sid)
		} catch (_) {
			/* ignore */
		}
		added_sources.delete(sid)
	}

	const use_mvt = use_vector_tiles(layer, as_of, CTX.is_public)

	if (use_mvt) {
		// Tile-urile aduc tot ce e vizibil → stratul NU e trunchiat. Resetul e obligatoriu: altfel
		// eticheta rămasă de la o vizualizare `as_of` (care dezactivează tile-urile) ar persista pe
		// un strat acum complet, și s-ar scurge și între hărți care au un strat cu același slug.
		write_layer_truncated(layer.slug, false)
		if (!added_sources.has(sid)) {
			MAP.addSource(sid, {
				type: "vector",
				tiles: [tiles_url(map_slug, layer.slug)],
				// feature_id (uuid) devine f.id pe feature-urile interogate la click
				promoteId: "feature_id",
			})
			added_sources.add(sid)
			// NU intră în geojson_cache → fit_to_data sare automat peste layerele MVT
		}
	} else {
		const res = await api_get<GeoJSON.FeatureCollection & { truncated?: boolean }>(
			geojson_url(map_slug, layer.slug, as_of),
		)
		if (!res.ok) {
			return
		}
		// Calea asta cere stratul ÎNTREG (fără `limit`), deci în mod normal `truncated` lipsește.
		// Îl citim totuși: dacă vreodată apare o limită pe traseu, rândul din listă și foaia tipărită
		// o spun — un strat incomplet nu are voie să arate ca fiind complet.
		write_layer_truncated(layer.slug, res.data.truncated === true)
		if (!MAP) {
			return
		}
		// ⚠️ MapLibre ARUNCĂ id-urile STRING pe sursele GeoJSON (uuid-ul din Feature.id cere
		// promoteId, care promovează doar o PROPRIETATE) → la click, f.id venea undefined și
		// garda UUID lăsa doar popup-ul, fără selecție — selecția era MOARTĂ pe toate straturile
		// mici (bug găsit la testarea F13-T4, 21.08). Copiem identitatea în properties.feature_id,
		// aceeași convenție ca tile-urile MVT (click-handler-ul o caută deja; popup-ul o filtrează).
		const sort_area = layer.style?.sort === "area"
		for (const f of res.data.features ?? []) {
			if (f.id != null) {
				f.properties = { ...f.properties, feature_id: String(f.id) }
			}
			// Ordinea de desenare (09.10): tile-ul poartă `__area` din SQL; pe GeoJSON o calculăm aici,
			// geodezic (doar ordinea contează — mare dedesubt), ca `fill-sort-key` să meargă identic.
			if (sort_area && f.geometry) {
				f.properties = { ...f.properties, [AREA_KEY]: geojson_area_m2(f.geometry) }
			}
		}
		// Check if source was added by another concurrent call
		if (!added_sources.has(sid)) {
			MAP.addSource(sid, { type: "geojson", data: res.data })
			added_sources.add(sid)
			geojson_cache.set(sid, res.data)
			snap_geojson_index.delete(sid) // se reconstruiește leneș din datele NOI
		}
	}

	// Pe sursă vectorială, TOATE layerele de stil au nevoie de source-layer = slug
	// (numele stratului din tile e ST_AsMVT(..., layer_slug)).
	const src_layer = use_mvt ? { "source-layer": layer.slug } : {}

	// Stilul EFECTIV (lib/layer_style): paletă + implicite — aceleași pe care le scrie exportul în QGIS.
	// Cu stil PE VALOARE (09.10), culoarea/opacitatea/conturul/grosimea sunt expresii `case` pe atributul
	// categoriilor (lib/layer_style.paint_values) — identice pe GeoJSON și pe tile (tile-ul poartă atributul).
	const es = effective_style(layer, idx)
	const pv = paint_values(es)
	const color = pv.color
	const opacity = pv.opacity
	const stroke = pv.stroke
	const width = pv.width
	const dash = es.dash ? { "line-dasharray": [...DASH_ARRAY] } : {}
	// Ordinea de desenare în interiorul stratului (09.10): mare dedesubt, ca în QGIS (`$area` desc) —
	// altfel ordinea e a tile-ului și se schimbă cu zoom-ul (violetul T1 peste tot orașul, pe staging).
	const fill_sort = es.sort === "area" ? { "fill-sort-key": area_sort_key() } : {}
	const line_sort = es.sort === "area" ? { "line-sort-key": area_sort_key() } : {}
	const gt = layer.geometry_type?.toLowerCase() ?? ""

	if (gt.includes("polygon")) {
		// Fill + contur separat (conturul rămâne vizibil chiar dacă fill-opacity e 0 — poligonul doar-contur
		// al planșelor CAD).
		if (!added_layers.has(lid)) {
			MAP.addLayer({
				id: lid,
				type: "fill",
				source: sid,
				...src_layer,
				layout: { ...fill_sort },
				paint: { "fill-color": color, "fill-opacity": opacity },
			} as maplibregl.LayerSpecification)
			added_layers.add(lid)
		}
		// Hașura (B, 13.10): un `fill` cu `fill-pattern` PESTE umplere — o imagine per combinație distinctă
		// (lib/layer_style.hatch_image_name), filtrul doar pe categoriile hașurate; nimic nou în tile-uri sau
		// în GeoJSON (atributul categoriilor e deja acolo), deci identic pe harta internă și pe cea publică.
		const hs = hatch_layer_spec(es)
		const hid = lid + "__hatch"
		if (hs && !added_layers.has(hid)) {
			for (const img of hs.images) {
				ensure_hatch_image(img.name, img.hatch)
			}
			MAP.addLayer({
				id: hid,
				type: "fill",
				source: sid,
				...src_layer,
				...(hs.filter ? { filter: hs.filter } : {}),
				layout: { ...fill_sort },
				paint: { "fill-pattern": hs.pattern, "fill-opacity": 1 },
			} as maplibregl.LayerSpecification)
			added_layers.add(hid)
		}
		const oid = lid + "__outline"
		if (!added_layers.has(oid)) {
			MAP.addLayer({
				id: oid,
				type: "line",
				source: sid,
				...src_layer,
				layout: { ...line_sort },
				paint: { "line-color": stroke, "line-width": width, ...dash },
			} as maplibregl.LayerSpecification)
			added_layers.add(oid)
		}
		// Vederea 3D activă supraviețuiește re-adăugării layerelor (as_of/workspace).
		if (STATE.mode_3d && extrudes_3d(layer)) {
			add_3d_for_layer(map_slug, layer, idx, utr_ctx_3d)
		}
	} else if (gt.includes("line") || gt.includes("string") || gt.includes("curve")) {
		if (!added_layers.has(lid)) {
			MAP.addLayer({
				id: lid,
				type: "line",
				source: sid,
				...src_layer,
				layout: { ...line_sort },
				paint: { "line-color": color, "line-width": width, "line-opacity": opacity, ...dash },
			} as maplibregl.LayerSpecification)
			added_layers.add(lid)
		}
	} else {
		// point / default — 1.4: cu iconiță configurată → simbol; altfel (sau la eșec) cercul implicit.
		// 09.10: opacitate 0 fără iconiță = FĂRĂ simbol (textul CAD convertit în puncte, `nullSymbol` în
		// QGIS): nu se adaugă cercul — nici conturul lui —, rămân doar etichetele.
		const icon_name = layer.icon_url ? icon_image_name(layer) : ""
		const has_icon = icon_name ? await ensure_icon_image(icon_name, layer.icon_url ?? "") : false
		if (!MAP) {
			return
		}
		const no_symbol = es.opacity === 0 && !has_icon && !es.categories
		if (!added_layers.has(lid) && !no_symbol) {
			MAP.addLayer(
				(has_icon
					? {
							id: lid,
							type: "symbol",
							source: sid,
							...src_layer,
							layout: {
								"icon-image": icon_name,
								"icon-size": 1,
								"icon-allow-overlap": true,
								"icon-anchor": "center",
							},
							paint: { "icon-opacity": Math.min(1, es.opacity + 0.3) },
						}
					: {
							id: lid,
							type: "circle",
							source: sid,
							...src_layer,
							paint: {
								"circle-color": color,
								"circle-radius": POINT_RADIUS_PX,
								"circle-opacity": opacity,
								"circle-stroke-color": stroke,
								"circle-stroke-width": width,
							},
						}) as maplibregl.LayerSpecification,
			)
			added_layers.add(lid)
		}
	}

	// Etichete (dacă layerul are un câmp de label din stilul QGIS).
	// Pe MVT funcționează identic: tile-ul conține atributul de label sub numele lui real.
	if (layer.style?.label) {
		const tid = lid + "__label"
		if (!added_layers.has(tid)) {
			// Scara etichetelor (09.10): `label_scale_max` 1:N din QGIS → zoom minim la latitudinea hărții.
			// Pe Popești-Leordeni 7.600 de etichete CAD la orice zoom făceau harta greoaie; proiectantul le
			// arată doar sub 1:10.000–1:40.000.
			const label_zoom = label_min_zoom(es.label_scale_max, MAP.getCenter().lat)
			MAP.addLayer({
				id: tid,
				type: "symbol",
				source: sid,
				...src_layer,
				...(label_zoom > 0 ? { minzoom: label_zoom } : {}),
				layout: {
					"text-field": ["get", layer.style.label],
					"text-font": ["Noto Sans Regular"],
					"text-size": es.label_size,
					"text-anchor": "center",
					"symbol-placement": gt.includes("line") || gt.includes("curve") ? "line" : "point",
				},
				paint: {
					"text-color": es.label_color,
					"text-halo-color": LABEL_HALO_COLOR,
					"text-halo-width": LABEL_HALO_PX,
				},
			} as maplibregl.LayerSpecification)
			added_layers.add(tid)
		}
	}

	// Cursă lazy (F13): userul poate STINGE stratul cât timp fetch-ul async încă rulează —
	// pasul de sync nu-l găsea în added_layers (no-op), iar layerele MapLibre se adaugă
	// implicit VIZIBILE → strat desenat cu sidebar-ul pe „ascuns". Aplicăm vizibilitatea
	// CURENTĂ din STATE la finalul adăugării, nu pe cea de la pornirea fetch-ului.
	const visible_now = STATE.layer_visible[layer.slug] !== false
	for (const suffix of ["", "__hatch", "__outline", "__label", "__3d"]) {
		if (MAP.getLayer(lid + suffix)) {
			MAP.setLayoutProperty(lid + suffix, "visibility", visible_now ? "visible" : "none")
		}
	}

	// click to select feature — opens side panel + popup.
	bind_layer_click(lid, layer.slug)

	// 2.2: rândurile „strat derivat" (filtre salvate) aprinse se re-adaugă odată cu părintele
	// (as_of/workspace re-creează sursa părinte; cele derivate urmează).
	for (const [fi, f] of (layer.saved_filters ?? []).entries()) {
		if (STATE.filter_visible[filter_key(layer.slug, f.slug)]) {
			void add_filter_layer(map_slug, layer, idx, fi, f)
		}
	}
}

/**
 * Click pe un strat tematic (sau pe un rând derivat): selectează entitatea (panoul din dreapta +
 * popup). O singură înregistrare per layer-id: MAP.on() nu e idempotent, iar adăugarea stratului se
 * re-apelează la fiecare schimbare de as_of (handler-ele s-ar acumula).
 */
/**
 * Stratul desenat CEL MAI SUS sub cursor, dintre cele cu handler de click ("" = niciunul).
 *
 * Delegații de click sunt per strat, iar MapLibre îi rulează pe TOȚI cei cu un obiect sub cursor, în
 * ordinea ÎNREGISTRĂRII — nu a desenării — și fiecare scria selecția, deci ultimul câștiga. Cât timp
 * `addLayer` punea stratul mereu în vârf, cele două ordini coincideau; de când ordinea de desenare e a
 * primăriei și straturile se aprind lazy (delegat înregistrat ultimul, strat coborât la locul lui de
 * restivuire), stratul abia aprins fura clickul celor desenate peste el (21.09).
 *
 * Un click = o singură interogare (toți delegații primesc ACELAȘI eveniment): candidații sunt toate
 * straturile cu handler (`bound_click_layers` — tematice, rânduri derivate, `__3d`, zonificare, acte,
 * solicitări), iar MapLibre întoarce rezultatele în ordinea de desenare, cel mai de sus întâi
 * (style.ts `_flattenAndSortRenderedFeatures`; clădirile 3D intră în aceeași ordonare, deci nu mai
 * sunt un caz special). ⚠️ doar id-uri PREZENTE: după 3D oprit, `__3d`-urile rămân în set, dar nu pe
 * hartă, iar un id lipsă face `queryRenderedFeatures` să emită eroare.
 */
let last_click_probe: { ev: unknown; winner: string } = { ev: null, winner: "" }
function top_bound_layer_at(e: maplibregl.MapMouseEvent): string {
	if (last_click_probe.ev === e.originalEvent) {
		return last_click_probe.winner
	}
	let winner = ""
	if (MAP) {
		const ids = [...bound_click_layers].filter((id) => MAP?.getLayer(id))
		if (ids.length > 0) {
			const hits = MAP.queryRenderedFeatures(e.point, { layers: ids })
			winner = hits.length > 0 ? hits[0].layer.id : ""
		}
	}
	last_click_probe = { ev: e.originalEvent, winner }
	return winner
}

function bind_layer_click(lid: string, layer_slug: string): void {
	if (!MAP) {
		return
	}
	if (!bound_click_layers.has(lid)) {
		bound_click_layers.add(lid)
		MAP.on("click", lid, (e) => {
			// În modul măsurare, interogare punct, regim SAU pin de sesizare, click-ul e al handler-ului
			// global (altfel un click pe un strat ar deschide și panoul de element în timp ce interoghezi).
			if (map_click_consumed()) {
				return
			}
			// Acționează DOAR delegatul stratului desenat cel mai sus sub cursor (vezi top_bound_layer_at);
			// fără niciun rezultat (caz limită), ca înainte — nimic nu poate deveni ne-clicabil.
			const winner = top_bound_layer_at(e)
			if (winner && winner !== lid) {
				return
			}
			if (!e.features || e.features.length === 0) {
				return
			}
			const f = e.features[0]
			// Acceptăm DOAR valori în formă de UUID, din f.id (GeoJSON) sau properties.feature_id
			// (tile MVT). NICIODATĂ properties.id: e coloana „id" din DATELE proiectantului
			// (OSM/CAD au id=10 etc.), nu identitatea entității — pe Măxineni ducea la
			// GET /api/features/10 (bug 19.08). Fără uuid → doar popup, fără panou de detaliu.
			const uuid_re = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
			const fid =
				[f.id, f.properties?.feature_id].map((v) => String(v ?? "")).find((v) => uuid_re.test(v)) ??
				""
			if (fid) {
				STATE.selected_feature_id = fid
				STATE.selected_feature_layer = layer_slug
				load_feature(fid)
			}
			// Show MapLibre popup with key properties
			show_feature_popup(e.lngLat, f.properties ?? {})
		})

		MAP.on("mouseenter", lid, () => {
			if (MAP) {
				MAP.getCanvas().style.cursor = "pointer"
			}
		})
		MAP.on("mouseleave", lid, () => {
			if (MAP) {
				MAP.getCanvas().style.cursor = ""
			}
		})
	}
}

/** Sub-straturile 2D ale unui strat tematic, în ordinea lor de desenare (fill < contur < etichetă).
 *  Extrudarea (`__3d`) NU e aici: stă într-o bandă DEASUPRA tuturor straturilor 2D (vezi `restack_layers`). */
const LAYER_SUFFIXES = ["", "__hatch", "__outline", "__label"] as const

/**
 * REAȘAZĂ TOATĂ STIVA în ordinea canonică (18.09) — punctul UNIC în care se decide ce se desenează
 * peste ce:
 *
 *   [fundaluri] < [straturi tematice 2D, în ordinea primăriei] < [zonificare/acte/solicitări] <
 *   [rază] < [măsurare/desen] < [contur de selecție] < [extrudările 3D, în aceeași ordine] < [Terra Draw]
 *
 * ⚠️ Extrudările stau într-o BANDĂ deasupra a tot ce e 2D — inclusiv selecția și schița — nu în grupul
 * stratului lor: un fill 2D desenat DUPĂ o extrudare se pictează peste clădiri (regula clasică
 * MapLibre „extrudarea deasupra tuturor fill-urilor"), și exact așa arată harta după apăsarea
 * butonului 3D, care adaugă `__3d` în vârf. Înainte, cele două căi se contraziceau (după o editare,
 * re-adăugarea punea extrudarea în grupul stratului, sub fill-urile următoare); acum ordinea nu
 * depinde de moment.
 *
 * ⚠️ Se mută EXCLUSIV cu `moveLayer(id)` FĂRĂ al doilea argument, de jos în sus: fiecare strat mutat
 * ajunge deasupra celui mutat înaintea lui, iar invariantul se auto-susține. Varianta cu ancoră
 * (`beforeId`) e INTERZISĂ aici, și nu din prudență: în MapLibre 4.7.1 `addLayer` cu o ancoră
 * inexistentă NU adaugă stratul deloc (emite un eveniment și iese — style.ts:958), iar `moveLayer`
 * cu ancoră inexistentă SCOATE stratul din ordinea de desenare fără să-l reinsereze
 * (style.ts:1013-1019). În ambele cazuri `getLayer()` și `added_layers` continuă să raporteze că
 * totul e în regulă, deci stratul ar dispărea de pe hartă fără niciun semnal. `moveLayer(id)` fără
 * ancoră cade pe `_order.length` și nu poate eșua.
 *
 * ⚠️ Cost: ZERO rețea și zero re-parsare — `moveLayer` nu cheamă `_updateLayer`, deci nu marchează
 * sursa pentru reîncărcare (spre deosebire de `addLayer`). Rămâne o singură plasare completă de
 * simboluri la următorul cadru (`_layerOrderChanged`). De aceea se cheamă O DATĂ la finalul unei
 * încărcări, nu per strat.
 *
 * Închide și un defect preexistent: conturul selecției și desenul de măsurare se ridicau ad-hoc, din
 * șapte locuri, și lipseau tocmai de pe căile prin care un strat se aprinde lazy sau se reîmprospătează
 * după o scriere — adică fix calea care devine normală când straturile pot porni stinse.
 */
function restack_layers(map_slug_arg?: string): void {
	if (!MAP) {
		return
	}
	const detail = STATE.map_detail
	// Slug-ul hărții MONTATE, nu `STATE.selected_map_slug`: în portalul public acesta se scrie abia
	// DUPĂ ce `load_map` s-a încheiat, deci o restivuire din timpul încărcării n-ar fi găsit niciun
	// strat tematic și ar fi tăcut degeaba.
	const map_slug = map_slug_arg || detail?.slug || STATE.selected_map_slug
	const order: string[] = []
	if (detail && map_slug) {
		for (const l of detail.layers) {
			const lid = layer_id_for(map_slug, l.slug)
			for (const suffix of LAYER_SUFFIXES) {
				order.push(lid + suffix)
			}
			// Rândurile derivate din filtre stau lipite de părintele lor.
			for (const f of l.saved_filters ?? []) {
				const fid = filter_layer_id(map_slug, l.slug, f.slug)
				order.push(fid, fid + "__outline")
			}
		}
	}
	order.push(...ZONIFICARE_IDS, ACTE_SRC + "_pt", SOLICITARI_SRC + "_pt")
	order.push(RADIUS_SRC + "_fill", RADIUS_SRC + "_line")
	for (const suffix of ["_fill", "_line", "_point"]) {
		order.push(MEASURE_SRC + suffix)
	}
	for (const suffix of ["_fill", "_line", "_point"]) {
		order.push(HIGHLIGHT_SRC + suffix)
	}
	// Extrudările: deasupra INCLUSIV a conturului de selecție și a schiței — un fill 2D desenat după o
	// extrudare se pictează peste clădiri, iar evidențierea unui poligon mare (UTR) pusă peste ele arăta
	// ca și cum s-ar fi selectat tot stratul de dedesubt (testare 21.09). Exact ordinea pe care o dădea
	// butonul 3D dintotdeauna; sub clădiri, selecția se vede pe la margini.
	if (detail && map_slug) {
		for (const l of detail.layers) {
			order.push(layer_id_for(map_slug, l.slug) + "__3d")
		}
	}
	for (const id of order) {
		if (MAP.getLayer(id)) {
			MAP.moveLayer(id)
		}
	}
	// Terra Draw își numește straturile cu prefixul adapterului („td-<geometrie>"); nu le construim
	// noi, deci le ridicăm după listă, ca schița în lucru să rămână deasupra a tot.
	for (const id of TERRA_DRAW_LAYER_IDS) {
		if (MAP.getLayer(id)) {
			MAP.moveLayer(id)
		}
	}
}

/**
 * Restivuire COMASATĂ pe cadru: „aprinde toate" pornește zeci de adăugări lazy în paralel, iar o
 * restivuire per adăugare ar însemna zeci de plasări complete de simboluri. Cererile care se termină
 * în același cadru produc o singură reașezare.
 */
let restack_pending = false
function schedule_restack(map_slug: string): void {
	if (restack_pending) {
		return
	}
	restack_pending = true
	requestAnimationFrame(() => {
		restack_pending = false
		restack_layers(map_slug)
	})
}

/** Straturile adapterului Terra Draw (prefix implicit „td"), în ordinea lor de desenare. */
const TERRA_DRAW_LAYER_IDS = [
	"td-polygon",
	"td-polygon-outline",
	"td-linestring",
	"td-point",
	"td-point-marker",
] as const

async function load_all_layers(map_slug: string, layers: LayerSummary[]): Promise<void> {
	for (let i = 0; i < layers.length; i++) {
		// Lazy (F13 perf): straturile care pornesc ASCUNSE nu-și încarc sursa/tile-urile acum —
		// se adaugă la prima aprindere (sync_layer_visibility). La deschiderea implicită (toate
		// vizibile) nu schimbă nimic; câștigă când harta pornește cu un subset sau userul ascunde.
		if (STATE.layer_visible[layers[i].slug] === false) {
			continue
		}
		// Aprins între timp de `reveal_feature_layer` (deep-link pe un strat stins) → e deja pe hartă;
		// re-adăugarea ar însemna încă un GET integral al stratului.
		if (added_layers.has(layer_id_for(map_slug, layers[i].slug))) {
			continue
		}
		await add_layer_to_map(map_slug, layers[i], i, STATE.as_of)
	}
	// O SINGURĂ restivuire, la final: per strat ar fi însemnat o plasare completă de simboluri la
	// fiecare pas al buclei, pe o hartă cu zeci de straturi cu etichete.
	restack_layers(map_slug)
}

/** Aprinde/stinge TOATE layerele dintr-un foc. */
function set_all_layers(visible: boolean): void {
	if (!STATE.map_detail) {
		return
	}
	const vis: Record<string, boolean> = {}
	for (const layer of STATE.map_detail.layers) {
		vis[layer.slug] = visible
		explicit_visibility.add(layer.slug)
	}
	STATE.layer_visible = vis
	sync_layer_visibility(STATE.selected_map_slug)
}

// ─────────────────────────────────────────────────────────────
// 1.3 FUNDALURI CONFIGURABILE (contract basemaps.md): `MapDetail.basemaps` (intern + /embed).
// Când există, butoanele configurate ÎNLOCUIESC perechea fixă Hartă/Satelit (primul = implicit);
// gol = comportamentul istoric. Sursele raster stau SUB straturile tematice. Atribuirea se
// ESCAPEAZĂ înainte de a ajunge la MapLibre (AttributionControl randează HTML) și la tipărire.
// ─────────────────────────────────────────────────────────────

const BM_PREFIX = "bm-"
const basemap_source_id = (i: number) => `${BM_PREFIX}${i}`
const basemap_layer_id = (i: number) => `${BM_PREFIX}${i}-tiles`

/** Fundalurile configurate pe harta curentă (gol = perechea fixă). */
function custom_basemaps(): Basemap[] {
	return STATE.map_detail?.basemaps ?? []
}

/** (Re)montează fundalurile configurate: scoate pe cele vechi, adaugă sursele + straturile sub cele tematice. */
function apply_basemaps(basemaps: Basemap[]): void {
	const m = MAP
	if (!m) {
		return
	}
	if (!m.isStyleLoaded()) {
		m.once("styledata", () => apply_basemaps(basemaps))
		return
	}
	const style = m.getStyle()
	for (const l of style.layers ?? []) {
		if (l.id.startsWith(BM_PREFIX)) {
			try {
				m.removeLayer(l.id)
			} catch (_) {
				/* ignore */
			}
		}
	}
	for (const sid of Object.keys(style.sources ?? {})) {
		if (sid.startsWith(BM_PREFIX)) {
			try {
				m.removeSource(sid)
			} catch (_) {
				/* ignore */
			}
		}
	}
	// Ancora: primul strat care NU e fundal → fundalurile configurate intră sub cele tematice.
	const anchor = (m.getStyle().layers ?? []).find(
		(l) => l.id !== "osm-tiles" && l.id !== "satelit-tiles" && !l.id.startsWith(BM_PREFIX),
	)?.id
	basemaps.forEach((b, i) => {
		try {
			m.addSource(basemap_source_id(i), {
				type: "raster",
				tiles: [basemap_tile_url(b)],
				tileSize: b.tile_size || 256,
				maxzoom: b.max_zoom || 19,
				attribution: escape_html(b.attribution ?? ""), // apărare în adâncime (innerHTML în control)
			})
			m.addLayer(
				{
					id: basemap_layer_id(i),
					type: "raster",
					source: basemap_source_id(i),
					minzoom: 0,
					maxzoom: 22,
					layout: { visibility: "none" },
				},
				anchor,
			)
		} catch (_) {
			/* un fundal defect nu strică harta — rămâne doar butonul fără efect */
		}
	})
	// Alegerea implicită: primul configurat; selecția curentă se păstrează dacă mai există.
	const cur = STATE.basemap
	const keep = cur.startsWith("bm:")
		? Number(cur.slice(3)) < basemaps.length
		: basemaps.length === 0
	set_basemap(keep ? cur : basemaps.length > 0 ? "bm:0" : "harta")
}

/** Comută fundalul: "harta" (OSM), "satelit" (Esri) sau "bm:<i>" (al i-lea fundal configurat). */
function set_basemap(kind: string): void {
	STATE.basemap = kind
	if (!MAP) {
		return
	}
	if (MAP.getLayer("osm-tiles")) {
		MAP.setLayoutProperty("osm-tiles", "visibility", kind === "harta" ? "visible" : "none")
	}
	if (MAP.getLayer("satelit-tiles")) {
		MAP.setLayoutProperty("satelit-tiles", "visibility", kind === "satelit" ? "visible" : "none")
	}
	for (let i = 0; i < custom_basemaps().length; i++) {
		if (MAP.getLayer(basemap_layer_id(i))) {
			MAP.setLayoutProperty(
				basemap_layer_id(i),
				"visibility",
				kind === `bm:${i}` ? "visible" : "none",
			)
		}
	}
}

/** Atribuirea fundalului ACTIV — pentru pagina de tipărire (lib/print o escapează). */
function basemap_attribution(): string {
	if (STATE.basemap.startsWith("bm:")) {
		const b = custom_basemaps()[Number(STATE.basemap.slice(3))]
		if (b) {
			return b.attribution ? `Fundal: ${b.attribution}` : `Fundal: ${b.name}`
		}
	}
	return STATE.basemap === "satelit"
		? "Fundal: imagini Esri, Maxar, Earthstar Geographics"
		: "Fundal: © OpenStreetMap contributors"
}

// ─────────────────────────────────────────────────────────────
// 1.4 ICONIȚE pe straturile punctuale (contract icoane.md): `LayerRef.icon_url` → imaginea se
// desenează în canvas la 2× (MapLibre nu rasterizează SVG direct) → `map.addImage` → strat `symbol`
// cu `icon-image` în locul cercului. Numele imaginii include uuid-ul fișierului (URL imutabil) — o
// iconiță nouă = imagine nouă, fără cache stale. Eșec de încărcare → cercul implicit.
// ─────────────────────────────────────────────────────────────

const ICON_PX = 24 // dimensiunea afișată (CSS px); desenată la 2× pentru ecranele dense
const icon_image_loading = new Map<string, Promise<boolean>>()

function icon_image_name(layer: LayerSummary): string {
	const file = (layer.icon_url ?? "").split("/").pop() ?? ""
	return `icon-${layer.slug}-${file.replace(/[^A-Za-z0-9._-]/g, "")}`
}

/** Încarcă (o dată per hartă) iconița în registrul de imagini MapLibre; false = fallback pe cerc. */
function ensure_icon_image(name: string, url: string): Promise<boolean> {
	const m = MAP
	if (!m) {
		return Promise.resolve(false)
	}
	if (m.hasImage(name)) {
		return Promise.resolve(true)
	}
	const pending = icon_image_loading.get(name)
	if (pending) {
		return pending
	}
	const p = new Promise<boolean>((resolve) => {
		const img = new Image()
		img.addEventListener("load", () => {
			try {
				const size = ICON_PX * 2
				const cv = document.createElement("canvas")
				cv.width = size
				cv.height = size
				const ctx = cv.getContext("2d")
				if (!ctx) {
					resolve(false)
					return
				}
				const iw = img.naturalWidth || img.width || size
				const ih = img.naturalHeight || img.height || size
				const r = Math.min(size / iw, size / ih)
				const w = iw * r
				const h = ih * r
				ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h)
				const data = ctx.getImageData(0, 0, size, size)
				if (MAP === m && !m.hasImage(name)) {
					m.addImage(name, data, { pixelRatio: 2 })
				}
				resolve(MAP === m)
			} catch (_) {
				resolve(false)
			}
		})
		img.addEventListener("error", () => resolve(false))
		img.src = url
	}).finally(() => icon_image_loading.delete(name))
	icon_image_loading.set(name, p)
	return p
}

/** Iconița ca data URL (documentul tipărit e self-contained); "" la eșec → fallback pe URL absolut. */
async function icon_data_url(url: string): Promise<string> {
	try {
		const r = await fetch(url)
		if (!r.ok) {
			return ""
		}
		const blob = await r.blob()
		return await new Promise<string>((resolve) => {
			const fr = new FileReader()
			fr.addEventListener("load", () => resolve(String(fr.result ?? "")))
			fr.addEventListener("error", () => resolve(""))
			fr.readAsDataURL(blob)
		})
	} catch (_) {
		return ""
	}
}

function sync_layer_visibility(map_slug: string, only = ""): void {
	if (!MAP || !STATE.map_detail) {
		return
	}
	for (let i = 0; i < STATE.map_detail.layers.length; i++) {
		const layer = STATE.map_detail.layers[i]
		// `only` = un singur strat (reveal_feature_layer): pe toate, în timpul încărcării inițiale, ar
		// porni în paralel adăugările pe care bucla serială din load_all_layers nu le-a atins încă.
		if (only && layer.slug !== only) {
			continue
		}
		const lid = layer_id_for(map_slug, layer.slug)
		const visible = STATE.layer_visible[layer.slug] !== false
		// Lazy (F13): dacă un strat devine VIZIBIL dar n-a fost încă încărcat (a pornit ascuns),
		// îl adăugăm acum — o singură dată; apoi toggle-ul doar comută vizibilitatea.
		if (visible && !added_layers.has(lid)) {
			// ⚠️ `addLayer` pune stratul DEASUPRA tuturor: fără restivuire, un strat aprins mai târziu
			// (calea normală de când straturile pot porni stinse) ar acoperi tot ce e sub el, indiferent
			// de ordinea aleasă de primărie — și ar acoperi și conturul selecției.
			void add_layer_to_map(map_slug, layer, i, STATE.as_of).then(() => schedule_restack(map_slug))
			continue
		}
		// Un layer tematic = până la 3 layere MapLibre: fill/line/circle + conturul
		// poligoanelor (__outline) + etichetele (__label) + extrudarea 3D (__3d).
		// Toggle-ul le stinge pe TOATE.
		for (const suffix of ["", "__hatch", "__outline", "__label", "__3d"]) {
			const id = lid + suffix
			if (added_layers.has(id)) {
				MAP.setLayoutProperty(id, "visibility", visible ? "visible" : "none")
			}
		}
		// 2.2: părintele ASCUNS își stinge și rândurile derivate — harta, butonul „vizibil/ascuns"
		// și legenda rămân de acord (altfel filtrul rămânea pe hartă după „ascunde"/„ascunde toate");
		// re-aprinderea părintelui NU le re-aprinde: toggle-urile rămân independente în rest.
		if (!visible) {
			for (const f of layer.saved_filters ?? []) {
				const key = filter_key(layer.slug, f.slug)
				if (STATE.filter_visible[key]) {
					write_filter_visible(key, false)
				}
				const fid = filter_layer_id(map_slug, layer.slug, f.slug)
				for (const suffix of ["", "__outline"]) {
					if (added_layers.has(fid + suffix)) {
						MAP.setLayoutProperty(fid + suffix, "visibility", "none")
					}
				}
			}
		}
	}
}

/**
 * Aplică pe sursa MVT înălțimile REZOLVATE de server (fix-ul constatării #4: tile-urile nu
 * transportă atribute → clientul nu poate calcula local). Serverul rulează ACELAȘI lanț de
 * precedență (lib/style3d, funcții pure partajate) și întoarce `{feature_id: metri}`;
 * setFeatureState e cheiat pe id-ul PROMOVAT din tile (`promoteId: "feature_id"`), deci
 * starea prinde și feature-urile din tile-urile încărcate ULTERIOR. Se re-cere la fiecare
 * intrare în 3D (config-ul se poate schimba între timp — aceeași doctrină ca `__h3d`).
 */
// FĂRĂ gardă de dedup pe cererile în zbor (scoasă la review): aplicarea e idempotentă și
// „ultimul câștigă", deci un duplicat ocazional (re-add de sursă în timpul intrării în 3D) e
// inofensiv — pe când o gardă ar servi config VECHI la re-intrarea rapidă în 3D și s-ar putea
// bloca permanent pe un path de excepție. Serverul absoarbe rafalele printr-un cache scurt.
async function apply_mvt_heights3d(map_slug: string, layer: LayerSummary, sid: string) {
	try {
		const r = await api_get<{ heights: Record<string, number>; bases: Record<string, number> }>(
			heights3d_url(map_slug, layer.slug),
		)
		// Între cerere și răspuns user-ul poate ieși din 3D / schimba harta — nu aplica pe moarte.
		// Pe eșec (rețea, 403 de contract) nu se aplică nimic: expresia rămâne pe fallback-ul
		// uniform DEFAULT_HEIGHT_M, iar următoarea intrare în 3D re-încearcă.
		if (!r.ok || !MAP || !STATE.mode_3d || !added_sources.has(sid)) {
			return
		}
		for (const [id, h] of Object.entries(r.data.heights)) {
			MAP.setFeatureState(
				{ source: sid, sourceLayer: layer.slug, id },
				{ h3d: h, b3d: r.data.bases[id] ?? 0 },
			)
		}
	} catch {
		// sursa a dispărut între timp (re-mount) — starea moare oricum cu ea
	}
}

/**
 * Adaugă layerul de extrudare 3D pentru un strat configurat/detectat (F10-T1).
 * Sursele GeoJSON primesc `__h3d` (+`__b3d` la base_attr) per feature — precedența:
 * atributele NUMITE în style3d → lanțul generic de chei → join-ul dinamic cu UTR-ul
 * (rh_max din RLU) → fallback determinist. Sursele MVT (tile-urile nu transportă atribute)
 * primesc aceleași înălțimi REZOLVATE de server, prin feature-state (apply_mvt_heights3d).
 */
function add_3d_for_layer(
	map_slug: string,
	layer: LayerSummary,
	idx: number,
	utr_ctx: UtrCtx3d | null,
): void {
	if (!MAP) {
		return
	}
	const sid = source_id(map_slug, layer.slug)
	const lid = layer_id_for(map_slug, layer.slug)
	const xid = lid + "__3d"
	if (!added_sources.has(sid) || added_layers.has(xid)) {
		return
	}
	const s3d = layer_style3d(layer)
	const fc = geojson_cache.get(sid) as
		| (GeoJSON.FeatureCollection & { __h3d_done?: boolean })
		| undefined
	// Recalculează la fiecare intrare în 3D (nu o singură dată): config-ul / contextul UTR
	// se pot schimba între montări, iar costul e mic (join-ul e indexat pe bbox).
	// ACELAȘI lanț ca serverul — compute_heights3d e funcția pură partajată (anti-drift:
	// GeoJSON, MVT, intern și public dau înălțimi identice prin construcție).
	if (fc) {
		const { heights, bases } = compute_heights3d(fc.features, s3d, s3d.regim_utr ? utr_ctx : null)
		for (const f of fc.features) {
			const props = (f.properties ?? {}) as Record<string, unknown>
			const id = String(f.id ?? props.feature_id ?? "")
			props.__h3d = heights[id] ?? DEFAULT_HEIGHT_M
			if (s3d.base_attr) {
				props.__b3d = bases[id] ?? 0
			}
			f.properties = props
		}
		fc.__h3d_done = true
		const src = MAP.getSource(sid) as maplibregl.GeoJSONSource | undefined
		src?.setData(fc)
	}
	const is_vector = !geojson_cache.has(sid)
	const src_layer = is_vector ? { "source-layer": layer.slug } : {}
	// GeoJSON: înălțimile precalculate mai sus, în properties (__h3d). MVT: din feature-state,
	// populat asincron de server (apply_mvt_heights3d) — până sosește (sau pe orice eșec),
	// fallback-ul uniform. ⚠️ `to-number` stă în AFARA lui `coalesce`: per spec,
	// to-number(null) = 0, deci pus înăuntru ar face fallback-ul DEFAULT_HEIGHT_M de neatins
	// (clădiri la 0 = invizibile până sosesc înălțimile).
	const num_expr = (key: string, dflt: number) =>
		is_vector
			? ["to-number", ["coalesce", ["feature-state", key], dflt]]
			: ["to-number", ["coalesce", ["get", "__" + key], dflt]]
	MAP.addLayer({
		id: xid,
		type: "fill-extrusion",
		source: sid,
		...src_layer,
		paint: {
			"fill-extrusion-color": s3d.color ?? layer_color(layer, idx),
			"fill-extrusion-opacity": s3d.opacity ?? 0.85,
			"fill-extrusion-height": num_expr("h3d", DEFAULT_HEIGHT_M),
			"fill-extrusion-base": s3d.base_attr ? num_expr("b3d", 0) : 0,
		},
	} as maplibregl.LayerSpecification)
	added_layers.add(xid)
	// Clickul pe acoperiș/perete selectează CLĂDIREA (cursor pointer pe ea); vezi bind_layer_click.
	bind_layer_click(xid, layer.slug)
	if (is_vector) {
		void apply_mvt_heights3d(map_slug, layer, sid)
	}
}

// Sursa DEM a terenului (F10-T3) — adăugată o singură dată per instanță de hartă; moare
// odată cu MAP.remove() la re-montare (nu intră în added_sources: nu e sursă de layer).
const DEM_SRC = "__dem"

/**
 * Comută relieful (REQ-M3D-008): sursă raster-dem din config-ul instanței + setTerrain.
 * Fail-closed: fără `terrain.url` de la server (env TERRAIN_TILES_URL nesetat) nu face nimic —
 * butonul nici nu se afișează. Encoding: terrarium (implicit) sau mapbox (TERRAIN_ENCODING).
 */
function set_terrain(on: boolean): void {
	const terr = STATE.map_detail?.terrain
	if (!MAP || !terr?.url) {
		STATE.terrain_on = false
		return
	}
	STATE.terrain_on = on
	if (on) {
		if (!MAP.getSource(DEM_SRC)) {
			MAP.addSource(DEM_SRC, {
				type: "raster-dem",
				tiles: [terr.url],
				tileSize: 256,
				encoding: terr.encoding === "mapbox" ? "mapbox" : "terrarium",
				maxzoom: 15,
			})
		}
		MAP.setTerrain({ source: DEM_SRC, exaggeration: 1 })
	} else {
		MAP.setTerrain(null)
	}
}

// Lumina implicită MapLibre — restaurată la ieșirea din simularea de însorire.
const DEFAULT_LIGHT = {
	anchor: "viewport",
	color: "#ffffff",
	intensity: 0.5,
	position: [1.15, 210, 30],
} as const

/**
 * Aplică soarele curent (STATE.sun_date + sun_hour, oră locală browser) pe lumina hărții.
 * Simulare VIZUALĂ (REQ-M3D-009): direcția luminii = azimutul/elevația reală a soarelui la
 * centrul hărții → umbrirea fațadelor extrudate se mișcă. NU calculează umbre purtate.
 */
function apply_sun(): void {
	if (!MAP || !STATE.sun_active) {
		return
	}
	const c = MAP.getCenter()
	const d = new Date(`${STATE.sun_date}T00:00:00`)
	if (Number.isNaN(d.getTime())) {
		return
	}
	d.setMinutes(Math.round(STATE.sun_hour * 60))
	const s = sun_position(d, c.lat, c.lng)
	MAP.setLight({
		anchor: "map",
		color: s.elevation > 0 ? "#ffffff" : "#dbe6f3",
		intensity: s.elevation > 0 ? 0.6 : 0.35,
		position: sun_to_light_position(s),
	})
	STATE.sun_readout =
		s.elevation > 0
			? `azimut ${Math.round(s.azimuth)}° · elevație ${Math.round(s.elevation)}°`
			: "soarele e sub orizont (noapte)"
}

/** Pornește/oprește simularea de însorire; oprit → lumina implicită MapLibre. */
function set_sun(on: boolean): void {
	STATE.sun_active = on
	if (!MAP) {
		return
	}
	if (on) {
		apply_sun()
	} else {
		STATE.sun_readout = ""
		MAP.setLight(DEFAULT_LIGHT as unknown as maplibregl.LightSpecification)
	}
}

/**
 * Comută vederea 3D: înclină camera + extrudează straturile configurate (style3d) sau
 * detectate (clădiri). Async doar pe ramura de pornire: join-ul UTR poate cere aducerea
 * stratului UTR — camera se înclină imediat, extrudările apar când contextul e gata.
 */
function set_mode_3d(on: boolean): void {
	STATE.mode_3d = on
	if (!MAP) {
		return
	}
	if (on && STATE.map_detail) {
		const map_slug = STATE.selected_map_slug
		const layers = STATE.map_detail.layers
		MAP.easeTo({ pitch: 55, duration: 700 })
		const needs_utr = layers.some((l) => extrudes_3d(l) && layer_style3d(l).regim_utr === true)
		void (needs_utr ? ensure_utr_ctx_3d(map_slug) : Promise.resolve(null)).then((ctx) => {
			// Utilizatorul a ieșit din 3D / a schimbat harta cât timp se încărca UTR-ul.
			if (!STATE.mode_3d || STATE.selected_map_slug !== map_slug || !MAP) {
				return
			}
			for (let i = 0; i < layers.length; i++) {
				if (extrudes_3d(layers[i])) {
					add_3d_for_layer(map_slug, layers[i], i, ctx)
				}
			}
			sync_layer_visibility(map_slug)
			// `addLayer` a pus extrudările în vârf, PESTE conturul selecției și schița de măsurare;
			// restivuirea le coboară în banda lor (deasupra 2D-ului, sub straturile de interacțiune).
			restack_layers(map_slug)
		})
	} else {
		utr_ctx_3d = null
		// Ieșirea din 3D stinge și relieful + soarele — butoanele lor trăiesc doar în modul 3D,
		// altfel ar rămâne active fără niciun control vizibil care să le mai închidă.
		if (STATE.terrain_on) {
			set_terrain(false)
		}
		if (STATE.sun_active) {
			set_sun(false)
		}
		// Ștergerea intrării curente în timpul iterării unui Set e sigură (spec ES).
		for (const id of added_layers) {
			if (id.endsWith("__3d")) {
				try {
					MAP.removeLayer(id)
				} catch (_) {
					/* ignore */
				}
				added_layers.delete(id)
			}
		}
		MAP.easeTo({ pitch: 0, duration: 700 })
	}
}

// INVARIANT (gating pe contract): dacă 3D-ul devine indisponibil cât timp e APRINS (sesiunea se
// hidratează după load_map și aduce model_3d=false, sau harta se schimbă pe un org fără flag),
// butoanele 3D dispar prin class — fără invariant, harta ar rămâne înclinată/extrudată fără
// niciun control vizibil de ieșire. Derive la nivel de modul: reactiv pe session + map_detail.
van.derive(() => {
	if (STATE.mode_3d && !map_3d_available()) {
		set_mode_3d(false)
	}
})

// ─────────────────────────────────────────────────────────────
// Feature popup
// ─────────────────────────────────────────────────────────────

/**
 * Popup-ul unui ACT de pe stratul virtual „acte" (N3): cod de registru, tip, stare, dată + linkul
 * spre cererea lui (`#/solicitari?id=`) sau spre documentele entității-ancoră (`#/documents?entity=`).
 * Același mecanism ca popup-ul de obiect (ACTIVE_POPUP); `sursa_geometrie` și id-urile nu se afișează
 * — din `sursa_geometrie` se deduce doar nota „obiectul a fost șters" (ancoră / parcelă ștearsă logic).
 */
function show_act_popup(lnglat: maplibregl.LngLat, props: Record<string, unknown>): void {
	if (!MAP) {
		return
	}
	if (ACTIVE_POPUP) {
		ACTIVE_POPUP.remove()
		ACTIVE_POPUP = null
	}
	const str = (k: string): string => (props[k] == null ? "" : String(props[k]))
	const raw_state = str("stare")
	const stare: ActeState = raw_state in ACTE_STATE_COLOR ? (raw_state as ActeState) : "emis"
	const color = ACTE_STATE_COLOR[stare]
	const code = str("registry_code") || "fără număr de registru"
	const cerere = str("cerere_id")
	const ancora = str("ancora_id")
	const doc_id = str("document_id")
	// `entity` restrânge lista paginii de documente la parcela actului, `doc` evidențiază actul.
	const link = cerere
		? `<a href="#/solicitari?id=${encodeURIComponent(cerere)}" style="color:#0369a1">Deschide cererea →</a>`
		: ancora
			? `<a href="#/documents?entity=${encodeURIComponent(ancora)}&doc=${encodeURIComponent(doc_id)}" style="color:#0369a1">Documentele entității →</a>`
			: ""
	// Obiectul pe care a fost emis actul a fost șters (O-C-7): punctul e ultima lui poziție cunoscută
	// — `sursa_geometrie` nu se afișează ca atare, dar din ea se știe cazul (fără proprietăți noi).
	const deleted_object = str("sursa_geometrie").endsWith("_stearsa")
		? `<div style="color:#b45309">Obiectul de pe hartă a fost șters — punctul arată ultima lui poziție.</div>`
		: ""
	const html =
		`<div style="font:12px system-ui,sans-serif;color:#0f172a;display:flex;flex-direction:column;gap:3px">` +
		`<div style="font-weight:600">${escape_html(code)}</div>` +
		`<div>${escape_html(str("tip_label"))}</div>` +
		`<div><span style="color:${color};font-weight:600">● ${escape_html(ACTE_STATE_LABEL[stare])}</span>` +
		(str("data") ? ` · ${escape_html(str("data"))}` : "") +
		`</div>` +
		deleted_object +
		(link ? `<div>${link}</div>` : "") +
		`</div>`
	ACTIVE_POPUP = new maplibregl.Popup({ closeButton: true, maxWidth: "260px" })
		.setLngLat(lnglat)
		.setHTML(html)
		.addTo(MAP)
}

function show_feature_popup(lnglat: maplibregl.LngLat, props: Record<string, unknown>): void {
	if (!MAP) {
		return
	}
	if (ACTIVE_POPUP) {
		ACTIVE_POPUP.remove()
		ACTIVE_POPUP = null
	}
	// Pick first 3-4 non-null properties to display (fără feature_id — e zgomot MVT).
	// `photos` (F9-T3) se redă prin fmt_prop („📷 N poze", nu JSON-ul tehnic); gol → omis,
	// să nu ardă unul din cele 4 sloturi pe „0 poze".
	const entries = Object.entries(props)
		.filter(([k, v]) => k !== "feature_id" && !k.startsWith("__") && v != null && v !== "")
		.filter(([k, v]) => k !== PHOTOS_KEY || parse_photos(v).length > 0)
		.map(([k, v]) => [k, k === PHOTOS_KEY ? fmt_prop(k, v) : v] as [string, unknown])
		.slice(0, 4)

	if (entries.length === 0) {
		return
	}

	// Datele feature-ului sunt controlate de utilizator (atribute EAV / import) → ESCAPE obligatoriu
	// înainte de .setHTML, altfel o valoare ca `<img onerror=…>` ar fi stored XSS în popup.
	const esc = (s: string) =>
		s
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#39;")
	const rows = entries
		.map(
			([k, v]) =>
				`<tr>
					<td style="color:#64748b;padding:2px 6px 2px 0;white-space:nowrap;font-weight:500">${esc(k)}</td>
					<td style="color:#0f172a;padding:2px 0;word-break:break-all">${esc(typeof v === "object" ? JSON.stringify(v) : String(v))}</td>
				</tr>`,
		)
		.join("")

	const html = `<table style="border-collapse:collapse;font-size:11px;font-family:system-ui,sans-serif">${rows}</table>`

	ACTIVE_POPUP = new maplibregl.Popup({ closeButton: true, maxWidth: "240px" })
		.setLngLat(lnglat)
		.setHTML(html)
		.addTo(MAP)
}

// ─────────────────────────────────────────────────────────────
// Fit to data (initial load only)
// ─────────────────────────────────────────────────────────────

function fit_to_data(map_slug: string, layers: LayerSummary[]): void {
	if (!MAP) {
		return
	}
	// Layerele MVT nu intră în geojson_cache, deci sunt sărite intenționat aici —
	// fit-ul e condus de layerele GeoJSON (cele mici) ale aceleiași hărți.
	const b = new maplibregl.LngLatBounds()
	for (const layer of layers) {
		const sid = source_id(map_slug, layer.slug)
		const fc = geojson_cache.get(sid)
		if (!fc) {
			continue
		}
		for (const feat of fc.features) {
			if (!feat.geometry || !("coordinates" in feat.geometry)) {
				continue
			}
			extend_bounds(b, (feat.geometry as { coordinates: unknown }).coordinates)
		}
	}
	if (b.isEmpty()) {
		return
	}
	const ne = b.getNorthEast()
	const sw = b.getSouthWest()
	if (ne.lng === sw.lng && ne.lat === sw.lat) {
		MAP.flyTo({ center: ne, zoom: 14 })
	} else {
		MAP.fitBounds(b, { padding: 60, maxZoom: 18, duration: 800 })
	}
}

// ─────────────────────────────────────────────────────────────
// UI Components
// ─────────────────────────────────────────────────────────────

// Spinner SVG inline (no dependency)
function Spinner(cls = "w-4 h-4") {
	const s = van.tags("http://www.w3.org/2000/svg")
	return s.svg(
		{
			class: `${cls} animate-spin text-cyan-600`,
			viewBox: "0 0 24 24",
			fill: "none",
		},
		s.circle({
			class: "opacity-25",
			cx: "12",
			cy: "12",
			r: "10",
			stroke: "current_color",
			"stroke-width": "4",
		}),
		s.path({
			class: "opacity-75",
			fill: "current_color",
			d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z",
		}),
	)
}

function MsgError(get_msg: () => string) {
	return t.div(
		{
			class:
				"rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-xs px-3 py-2 mb-3",
			style: () => `display:${get_msg() ? "block" : "none"}`,
		},
		() => get_msg(),
	)
}

function AsOfInput() {
	return t.div(
		{ class: "mb-4" },
		t.label(
			{
				class:
					"block text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5",
				for: "as-of-input",
			},
			"As-of (dată/oră)",
		),
		t.input({
			type: "datetime-local",
			id: "as-of-input",
			class:
				"w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 transition",
			onchange: async (e: Event) => {
				const val = (e.target as HTMLInputElement).value
				STATE.as_of = val
				// refetch current map layers and feature if selected
				if (STATE.selected_map_slug && STATE.map_detail) {
					remove_map_layers(STATE.selected_map_slug, STATE.map_detail.layers)
					await load_all_layers(STATE.selected_map_slug, STATE.map_detail.layers) // + restivuire
					sync_layer_visibility(STATE.selected_map_slug)
				}
				if (STATE.selected_feature_id) {
					load_feature(STATE.selected_feature_id)
				}
			},
		}),
	)
}

function IdentifyPanel() {
	return t.div(
		{
			style: () => `display:${STATE.identify_result || STATE.identify_loading ? "block" : "none"}`,
		},
		() => {
			if (STATE.identify_loading) {
				return t.div(
					{
						class: "flex items-center gap-2 py-2 text-xs text-slate-400 dark:text-slate-500 italic",
					},
					Spinner("w-4 h-4"),
					"Interogare spațială...",
				)
			}
			const r = STATE.identify_result
			if (!r) {
				return t.div()
			}
			const title =
				(r.target.kind === "feature"
					? `Intersecții: ${r.target.key ? `${r.target.key} = ${fmt_prop(r.target.key, r.target.value)}` : "parcela selectată"}`
					: `Intersecții la punct (${r.target.lng.toFixed(5)}, ${r.target.lat.toFixed(5)})`) +
				(r.radius_m ? ` · pe o rază de ${r.radius_m} m` : "")
			return t.div(
				{
					class:
						"rounded-xl bg-amber-50/60 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 p-3 mb-3",
				},
				t.div(
					{ class: "flex items-center justify-between gap-2 mb-1" },
					t.span(
						{
							class:
								"text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300",
						},
						title,
					),
					t.button(
						{
							class:
								"p-2 -m-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 text-sm leading-none cursor-pointer",
							title: "Închide",
							onclick: () => {
								STATE.identify_result = null
								clear_radius_circle()
							},
						},
						"✕",
					),
				),
				// 1.5: coordonatele punctului interogat (Stereo 70 + WGS84, cu copiere)
				r.target.kind === "point" ? CoordsBlock(r.target.lng, r.target.lat, "Punct interogat") : "",
				r.intersections.length === 0
					? t.div(
							{ class: "text-xs text-slate-400 dark:text-slate-500 italic" },
							"Nimic la această locație.",
						)
					: t.div(
							{ class: "space-y-2" },
							...r.intersections.map((h) =>
								t.div(
									t.div(
										{ class: "text-xs font-bold text-slate-700 dark:text-slate-200 mt-1.5" },
										`${h.layer_name} `,
										t.span(
											{ class: "font-normal text-slate-400 dark:text-slate-500" },
											`(${h.features.length})`,
										),
									),
									...h.features.slice(0, 6).map((f) => {
										const entries = Object.entries(f.properties)
											.filter(([, v]) => v != null && v !== "")
											.slice(0, 3)
										return t.button(
											{
												class:
													"block w-full text-left px-2 py-1 mt-0.5 rounded-md bg-white/70 dark:bg-slate-800/80 border border-amber-100 dark:border-amber-900 text-[11px] hover:bg-white dark:hover:bg-slate-700 transition cursor-pointer",
												onclick: () => {
													STATE.selected_feature_id = f.id
													load_feature(f.id)
												},
											},
											// 3.4: distanța geodezică (doar cu rază); 0 = atinge/conține punctul
											typeof f.distance_m === "number"
												? t.span(
														{
															class:
																"inline-block mr-1.5 px-1 rounded bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 font-semibold",
														},
														f.distance_m === 0 ? "atinge" : `~${format_distance(f.distance_m)}`,
													)
												: "",
											entries.length === 0
												? t.span(
														{ class: "text-slate-400 dark:text-slate-500 italic" },
														"fără atribute",
													)
												: t.span(
														{ class: "font-mono text-slate-600 dark:text-slate-300" },
														entries.map(([k, v]) => `${k}: ${fmt_prop(k, v)}`).join("  ·  "),
													),
										)
									}),
									h.features.length > 6
										? t.div(
												{ class: "text-[10px] text-slate-400 dark:text-slate-500 mt-0.5 pl-2" },
												`… încă ${h.features.length - 6}`,
											)
										: t.div(),
								),
							),
						),
			)
		},
	)
}

// Legend swatch based on geometry type (+ 1.4: iconița stratului, când există — oglinda `swatch_img` din print)
/**
 * Imaginea de model a unei hașuri (B, 13.10): un pătrat de 2^n px (MapLibre cere puteri ale lui 2 ca
 * modelul să se repete fără cusur), desenat O SINGURĂ dată per combinație distinctă și refolosit de toate
 * straturile hărții (`MAP.hasImage`). Desenat la 2× (pixelRatio 2) ca liniile să rămână de ~1 px și pe
 * HiDPI. Pe diagonală se desenează și liniile prin colțuri (x+y = 0 și 2T), altfel modelul avea goluri
 * la marginile pătratului.
 */
function ensure_hatch_image(name: string, h: Hatch): void {
	if (!MAP || MAP.hasImage(name)) {
		return
	}
	const T = hatch_tile_px(h) * 2
	const canvas = document.createElement("canvas")
	canvas.width = T
	canvas.height = T
	const ctx = canvas.getContext("2d")
	if (!ctx) {
		return
	}
	ctx.clearRect(0, 0, T, T)
	ctx.strokeStyle = h.color
	ctx.lineWidth = 2
	ctx.lineCap = "butt"
	const q = hatch_angle_q(h.angle)
	const dirs = h.crossed ? [q, (q + 90) % 180] : [q]
	ctx.beginPath()
	for (const d of dirs) {
		if (d === 45) {
			for (const k of [0, T, 2 * T]) {
				ctx.moveTo(k + T, -T) // x + y = k, segment lung — canvasul decupează
				ctx.lineTo(k - 2 * T, 2 * T)
			}
		} else if (d === 135) {
			for (const k of [-T, 0, T]) {
				ctx.moveTo(k - T, -T) // x − y = k
				ctx.lineTo(k + 2 * T, 2 * T)
			}
		} else if (d === 90) {
			ctx.moveTo(T / 2, 0)
			ctx.lineTo(T / 2, T)
		} else {
			ctx.moveTo(0, T / 2)
			ctx.lineTo(T, T / 2)
		}
	}
	ctx.stroke()
	const data = ctx.getImageData(0, 0, T, T)
	MAP.addImage(name, { width: T, height: T, data: data.data }, { pixelRatio: 2 })
}

// Legend swatch based on geometry type (+ 1.4: iconița stratului, când există — oglinda `swatch_img` din print)
function LayerSwatch(
	color: string,
	stroke: string,
	geometry_type: string,
	icon_url = "",
	fill_opacity = 0.7, // 0 = fără umplere (poligon doar-contur) / fără simbol (punct doar-etichetă)
	hatch: { color: string; angle: number; crossed: boolean } | null = null, // hașura peste careu (B) — oglinda swatch_svg
) {
	const gt = geometry_type?.toLowerCase() ?? ""
	const s = van.tags("http://www.w3.org/2000/svg")
	const hollow = fill_opacity <= 0

	if (icon_url && !gt.includes("polygon") && !gt.includes("line")) {
		return t.img({
			src: icon_url,
			width: "18",
			height: "18",
			class: "flex-shrink-0 object-contain",
			alt: "",
		})
	}

	if (gt.includes("polygon")) {
		return s.svg(
			{ width: "18", height: "18", viewBox: "0 0 18 18", style: "flex-shrink:0" },
			s.rect({
				x: "1",
				y: "1",
				width: "16",
				height: "16",
				rx: "2",
				fill: hollow ? "none" : color,
				"fill-opacity": "0.7",
				stroke: stroke,
				"stroke-width": "1.5",
			}),
			...(hatch
				? hatch_paths(hatch.angle, hatch.crossed).map((d) =>
						s.path({ d, stroke: hatch.color, "stroke-width": "1.2", fill: "none" }),
					)
				: []),
		)
	}
	if (gt.includes("line") || gt.includes("string") || gt.includes("curve")) {
		return s.svg(
			{ width: "18", height: "18", viewBox: "0 0 18 18", style: "flex-shrink:0" },
			s.path({
				d: "M2 14 L6 6 L12 10 L16 4",
				fill: "none",
				stroke: color,
				"stroke-width": "2.5",
				"stroke-linecap": "round",
				"stroke-linejoin": "round",
			}),
		)
	}
	// point / default — circle dot; fără simbol → „Aa" (stratul e doar etichete)
	if (hollow) {
		return s.svg(
			{ width: "18", height: "18", viewBox: "0 0 18 18", style: "flex-shrink:0" },
			s.text(
				{
					x: "9",
					y: "13",
					"text-anchor": "middle",
					"font-size": "11",
					"font-family": "sans-serif",
					fill: color,
				},
				"Aa",
			),
		)
	}
	return s.svg(
		{ width: "18", height: "18", viewBox: "0 0 18 18", style: "flex-shrink:0" },
		s.circle({
			cx: "9",
			cy: "9",
			r: "6",
			fill: color,
			"fill-opacity": "0.85",
			stroke: stroke,
			"stroke-width": "1.5",
		}),
	)
}

/**
 * Simboluri CONVENITE pentru elementele de urbanism (REQ-URB-001, F5-T2): stratul UTR și
 * derogările (PUZ) au semne dedicate în legendă, nu swatch-ul generic — urbanista le
 * recunoaște dintr-o privire, indiferent de culoarea tematică a stratului.
 */
function RoleSwatch(role: string) {
	const s = van.tags("http://www.w3.org/2000/svg")
	if (role === "utr") {
		// Grilă de zonificare (careu împărțit) — semnul convenit pentru UTR.
		return s.svg(
			{ width: "18", height: "18", viewBox: "0 0 18 18", style: "flex-shrink:0" },
			s.rect({
				x: "1",
				y: "1",
				width: "16",
				height: "16",
				rx: "2",
				fill: "#059669",
				"fill-opacity": "0.15",
				stroke: "#059669",
				"stroke-width": "1.5",
			}),
			s.path({ d: "M9 1 V17 M1 9 H17", stroke: "#059669", "stroke-width": "1.2" }),
		)
	}
	// role === 'derogare': triunghi de atenționare (PUZ care suprascrie regimul).
	return s.svg(
		{ width: "18", height: "18", viewBox: "0 0 18 18", style: "flex-shrink:0" },
		s.path({
			d: "M9 2 L16.5 15.5 H1.5 Z",
			fill: "#d97706",
			"fill-opacity": "0.2",
			stroke: "#d97706",
			"stroke-width": "1.5",
			"stroke-linejoin": "round",
		}),
		s.path({ d: "M9 7 V11", stroke: "#d97706", "stroke-width": "1.8", "stroke-linecap": "round" }),
		s.circle({ cx: "9", cy: "13.4", r: "1", fill: "#d97706" }),
	)
}

const ROLE_META: Record<string, { label: string; cls: string; title: string }> = {
	utr: {
		label: "UTR",
		cls: "bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
		title: "Unități teritoriale de referință — sursa regimului urbanistic on-click",
	},
	derogare: {
		label: "PUZ/derogare",
		cls: "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
		title: "Derogări aprobate (PUZ/HCL) — prevalează asupra regimului de bază (REQ-URB-008)",
	},
}

/** Rândul „strat derivat" (2.2): un filtru salvat al stratului părinte, cu toggle propriu. */
function FilterRow(layer: LayerSummary, idx: number, fi: number, f: SavedFilter) {
	const color = filter_color(layer_color(layer, idx), fi)
	const key = filter_key(layer.slug, f.slug)
	const label = saved_filter_label(f)
	return t.div(
		{
			class:
				"flex items-center gap-2 ml-5 pl-2 pr-2 py-1 mb-1 rounded-lg border border-dashed border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60",
			title: label,
		},
		LayerSwatch(color, shade_color(color, 0.7), layer.geometry_type),
		t.div(
			{ class: "flex-1 min-w-0" },
			t.div({ class: "text-xs text-slate-700 dark:text-slate-200 truncate" }, f.name || f.slug),
			t.div({ class: "text-[10px] text-slate-400 dark:text-slate-500 truncate" }, label),
		),
		t.button(
			{
				class: () =>
					`flex-shrink-0 rounded-md border px-2 py-0.5 text-[11px] font-medium transition ` +
					(STATE.filter_visible[key]
						? "bg-cyan-50 dark:bg-cyan-950 border-cyan-200 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100"
						: "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:bg-slate-200"),
				title: () => (STATE.filter_visible[key] ? "Ascunde filtrul" : "Afișează filtrul pe hartă"),
				onclick: () => set_filter_visible(layer, idx, fi, f, !STATE.filter_visible[key]),
			},
			() => (STATE.filter_visible[key] ? "vizibil" : "ascuns"),
		),
	)
}

/** Aria geodezică a unei geometrii GeoJSON — inelele exterioare ale poligoanelor; linii/puncte = 0. */
function geojson_area_m2(g: GeoJSON.Geometry): number {
	if (g.type === "Polygon") {
		return polygon_area_m2((g.coordinates[0] ?? []) as [number, number][])
	}
	if (g.type === "MultiPolygon") {
		return g.coordinates.reduce(
			(s, poly) => s + polygon_area_m2((poly[0] ?? []) as [number, number][]),
			0,
		)
	}
	return 0
}

/** Categoriile stilului pe valoare (09.10): o linie compactă per valoare, sub stratul părinte — legenda planșei. */
function CategoryRows(layer: LayerSummary, idx: number): HTMLElement[] {
	const es = effective_style(layer, idx)
	if (!es.categories) {
		return []
	}
	// 12.10: categoriile ASCUNSE (debifate de proiectant în QGIS) nu se desenează; 13.10: un SINGUR rând estompat
	// pentru toate, cu numele lor în tooltip — câte un rând per categorie făcea jumătate din listă „neafișată” pe
	// 21 de straturi din 12 hărți (circulațiile Băciei: 5 clase de folosință debifate + „altele”). Legenda de pe
	// ecran și tipărirea le omit (explică doar ce se desenează).
	const visible = es.categories.values.filter((c: EffectiveCategory) => !is_hidden_symbol(c))
	const hidden = es.categories.values.filter((c: EffectiveCategory) => is_hidden_symbol(c))
	const rows = visible.map((c: EffectiveCategory) =>
		t.div(
			{
				class: "flex items-center gap-2 ml-5 pl-2 pr-2 py-0.5",
				title: `${es.categories!.attr} = ${c.value}`,
			},
			LayerSwatch(c.color, c.stroke, layer.geometry_type, "", c.opacity, c.hatch),
			t.div({ class: "text-[11px] text-slate-600 dark:text-slate-300 truncate" }, c.label),
		),
	)
	const else_hidden = es.categories.has_else && is_hidden_symbol(es.categories.else)
	if (es.categories.has_else && !else_hidden) {
		const e = es.categories.else
		rows.push(
			t.div(
				{ class: "flex items-center gap-2 ml-5 pl-2 pr-2 py-0.5", title: "celelalte valori" },
				LayerSwatch(e.color, e.stroke, layer.geometry_type, "", e.opacity, e.hatch),
				t.div({ class: "text-[11px] text-slate-400 dark:text-slate-500 truncate" }, "altele"),
			),
		)
	}
	if (hidden.length > 0 || else_hidden) {
		const names = hidden.map((c: EffectiveCategory) => c.label).join(", ")
		const text =
			hidden.length === 0
				? "celelalte valori · neafișate"
				: `${hidden.length} ${hidden.length === 1 ? "categorie neafișată" : "categorii neafișate"} de proiectant${else_hidden ? ", și celelalte valori" : ""}`
		rows.push(
			t.div(
				{
					class: "flex items-center gap-2 ml-5 pl-2 pr-2 py-0.5 opacity-70",
					title: `debifate în QGIS de proiectant, nu se desenează: ${names}${else_hidden ? `${names ? " · " : ""}celelalte valori` : ""}`,
				},
				t.span({
					class:
						"inline-block w-[18px] h-[18px] rounded border border-dashed border-slate-300 dark:border-slate-600 flex-shrink-0",
				}),
				t.div({ class: "text-[11px] text-slate-400 dark:text-slate-500 italic truncate" }, text),
			),
		)
	}
	return rows
}

function LayerItem(layer: LayerSummary, idx: number) {
	const filters = layer.saved_filters ?? []
	const row = LayerRow(layer, idx)
	const cats = CategoryRows(layer, idx)
	if (filters.length === 0 && cats.length === 0) {
		return row
	}
	return t.div(row, ...cats, ...filters.map((f, fi) => FilterRow(layer, idx, fi, f)))
}

function LayerRow(layer: LayerSummary, idx: number) {
	const slug = layer.slug
	const color = layer_color(layer, idx)
	const es_row = effective_style(layer, idx)
	const stroke = es_row.stroke
	const fill_op = es_row.opacity === 0 ? 0 : 0.7
	const map_slug = STATE.selected_map_slug
	const role = layer.role && ROLE_META[layer.role] ? layer.role : ""

	return t.div(
		{
			class:
				"flex items-center gap-2 px-2.5 py-2 bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 shadow-sm mb-1.5 group",
		},
		// Legend swatch — simbolul convenit pentru straturile de urbanism, generic altfel.
		role
			? RoleSwatch(role)
			: LayerSwatch(color, stroke, layer.geometry_type, layer.icon_url, fill_op, es_row.hatch),
		// Name — cipul de rol stă LÂNGĂ numele trunchiat, nu în el (truncate l-ar tăia la nume lungi).
		t.div(
			{ class: "flex-1 min-w-0" },
			t.div(
				{ class: "flex items-center gap-1.5 min-w-0" },
				t.div(
					{
						class: "text-xs font-medium text-slate-800 dark:text-slate-200 truncate min-w-0",
						title: layer.name,
					},
					layer.name,
				),
				role
					? t.span(
							{
								class: `flex-shrink-0 inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${ROLE_META[role].cls}`,
								title: ROLE_META[role].title,
							},
							ROLE_META[role].label,
						)
					: "",
			),
			t.div(
				{ class: "text-xs text-slate-400 dark:text-slate-500" },
				() =>
					`${layer.feature_count ?? 0} entități` +
					// `=== true`: pe un slug ca „constructor” proxy-ul VanX cade pe refGet pentru cheia absentă și
					// întoarce Object.prototype.constructor (o funcție, truthy) — nu un marcaj real
					(STATE.layer_truncated[slug] === true ? " · afișat parțial" : ""),
			),
		),
		// Visibility toggle
		t.button(
			{
				class: () =>
					`flex-shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium transition ` +
					(STATE.layer_visible[slug] !== false
						? "bg-cyan-50 dark:bg-cyan-950 border-cyan-200 text-cyan-700 dark:text-cyan-300 hover:bg-cyan-100"
						: "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:bg-slate-200"),
				title: () => (STATE.layer_visible[slug] !== false ? "Ascunde stratul" : "Afișează stratul"),
				onclick: () => {
					explicit_visibility.add(slug)
					STATE.layer_visible[slug] = STATE.layer_visible[slug] === false
					sync_layer_visibility(map_slug)
				},
			},
			() => (STATE.layer_visible[slug] !== false ? "vizibil" : "ascuns"),
		),
		// Solo: „doar acest strat" — ascunde toate celelalte; click din nou = arată toate.
		t.button(
			{
				class:
					"flex-shrink-0 rounded-md border px-1.5 py-1 text-xs transition " +
					"bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:text-cyan-700 hover:border-cyan-400",
				title: "Doar acest strat (ascunde restul)",
				onclick: () => solo_layer(slug),
			},
			icon("target"),
		),
		// Table button
		t.button(
			{
				class: () =>
					`flex-shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium transition ` +
					(STATE.table_layer_slug === slug
						? "bg-slate-800 border-slate-700 text-white"
						: "bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"),
				title: "Tabel de atribute",
				onclick: () => {
					if (STATE.table_layer_slug === slug) {
						close_table()
					} else {
						load_table(slug, layer.name)
					}
				},
			},
			icon("table"),
		),
	)
}

// Predefiniri de șabloane după ROLUL stratului (F13) — se aplică când stratul n-are
// `allowed_templates` explicit. Restul straturilor (fără rol, fără config) → NICIUNUL.
const TEMPLATES_BY_ROLE: Record<string, string[]> = {
	utr: ["certificat_urbanism"],
	derogare: ["certificat_urbanism"],
}

/**
 * Șabloanele permise pe feature-ul selectat: `null` = TOATE (strat necunoscut — nu putem
 * restricționa), altfel un Set de slug-uri (explicit din config, apoi predefinire după rol,
 * apoi GOL = niciunul). Fail-closed pe strat cunoscut și neconfigurat.
 */
function allowed_templates_for_selected(): Set<string> | null {
	const slug = STATE.selected_feature_layer
	if (!slug || !STATE.map_detail) {
		return null // strat necunoscut (deep-link/search) → nu restricționăm
	}
	const layer = STATE.map_detail.layers.find((l) => l.slug === slug)
	if (!layer) {
		return null
	}
	const explicit = layer.allowed_templates
	if (Array.isArray(explicit) && explicit.length > 0) {
		return new Set(explicit)
	}
	const by_role = layer.role ? TEMPLATES_BY_ROLE[layer.role] : undefined
	return new Set(by_role ?? []) // fără config + fără predefinire pe rol → GOL (niciunul)
}

/**
 * Solo: arată DOAR stratul dat (ascunde restul). Dacă e deja singurul vizibil → arată toate
 * (comportament togglabil, ca la aplicațiile GIS).
 */
function solo_layer(slug: string): void {
	if (!STATE.map_detail) {
		return
	}
	const layers = STATE.map_detail.layers
	const only_this =
		STATE.layer_visible[slug] !== false &&
		layers.every((l) => l.slug === slug || STATE.layer_visible[l.slug] === false)
	const vis: Record<string, boolean> = {}
	for (const l of layers) {
		vis[l.slug] = only_this ? true : l.slug === slug
		explicit_visibility.add(l.slug)
	}
	STATE.layer_visible = vis
	sync_layer_visibility(STATE.selected_map_slug)
}

function LayerList() {
	return t.div(
		{ class: "mb-4" },
		t.div(
			{ class: "flex items-center gap-2 mb-1.5" },
			t.label(
				{
					class:
						"text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 flex-1",
				},
				"Straturi",
			),
			// Bulk: aprinde/stinge toate straturile dintr-un click.
			t.button(
				{
					class: "text-slate-400 dark:text-slate-500 hover:text-cyan-700 transition",
					title: "Arată toate straturile",
					onclick: () => set_all_layers(true),
				},
				icon("eye", "text-base"),
			),
			t.button(
				{
					class: "text-slate-400 dark:text-slate-500 hover:text-cyan-700 transition",
					title: "Ascunde toate straturile",
					onclick: () => set_all_layers(false),
				},
				icon("eye-slash", "text-base"),
			),
			// Loading spinner inline in header
			t.div(
				{
					class: "flex items-center gap-1",
					style: () => `display:${STATE.map_loading ? "flex" : "none"}`,
				},
				Spinner("w-3 h-3"),
				t.span({ class: "text-xs text-slate-400 dark:text-slate-500 italic" }, "Se încarcă..."),
			),
		),
		MsgError(() => STATE.map_error),
		// Filtru pe nume (F13-T2): scurtează DOAR lista — vizibilitatea pe hartă rămâne neatinsă.
		// Fuzzy pe subsecvențe + diacritice normalizate: „zonif" → „Zonificare funcțională".
		t.div(
			{ class: "relative mb-2" },
			t.input({
				// type=text, NU search: pe „search" browserul își pune PROPRIUL ✕ de golire
				// lângă al nostru → două ✕-uri una lângă alta (bug vizual raportat 21.08).
				type: "text",
				class:
					"w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 " +
					"pl-3 pr-7 py-1.5 text-xs text-slate-800 dark:text-slate-200 outline-none focus:border-cyan-500 " +
					"placeholder:text-slate-400 dark:placeholder:text-slate-500",
				placeholder: "Filtrează straturile…",
				value: () => STATE.layer_q,
				oninput: (e: Event) => {
					STATE.layer_q = (e.target as HTMLInputElement).value
				},
			}),
			t.button(
				{
					class: () =>
						"absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 " +
						"dark:hover:text-slate-300 leading-none " +
						(STATE.layer_q ? "" : "hidden"),
					title: "Șterge filtrul",
					onclick: () => {
						STATE.layer_q = ""
					},
				},
				"✕",
			),
		),
		// Layer items (reactive wrapper)
		t.div(
			{
				style: () =>
					`display:${!STATE.map_loading && STATE.map_detail && STATE.map_detail.layers.length > 0 ? "block" : "none"}`,
			},
			() => {
				const detail = STATE.map_detail
				if (!detail) {
					return t.div()
				}
				// Extins = listă cu nume (LayerItem). Reprezentarea „doar iconițe" e sidebar-ul COLLAPSED.
				// Filtrul păstrează idx-ul ORIGINAL al stratului (culoarea/sursa depind de el).
				const rows = detail.layers
					.map((layer, idx) => ({ layer, idx }))
					.filter(({ layer }) => fuzzy_match(STATE.layer_q, layer.name || layer.slug))
				if (rows.length === 0) {
					return t.div(
						{ class: "text-xs text-slate-400 dark:text-slate-500 italic py-2" },
						`Niciun strat nu se potrivește cu „${STATE.layer_q}".`,
					)
				}
				return t.div(
					STATE.layer_q
						? t.div(
								{ class: "text-[10px] text-slate-400 dark:text-slate-500 mb-1" },
								`${rows.length} din ${detail.layers.length} straturi`,
							)
						: t.span({ style: "display:none" }),
					...rows.map(({ layer, idx }) => LayerItem(layer, idx)),
				)
			},
		),
		// 1.2 Stratul VIRTUAL „Zonificare actuală" — PUG decupat de PUZ-urile aprobate (contract
		// zonificare_actuala.md); doar pe hărți cu strat UTR (+ modulul de regim). Intern + public.
		t.div(
			{
				class: () =>
					"py-1.5 mt-1 pt-2 border-t border-slate-200 dark:border-slate-700" +
					(zonificare_supported() ? "" : " hidden"),
			},
			t.div(
				{ class: "flex items-center gap-2" },
				LayerSwatch("#94a3b8", ZONIFICARE_PUG_LINE, "Polygon"),
				t.div(
					{ class: "flex-1 min-w-0" },
					t.div(
						{ class: "text-xs font-medium text-slate-800 dark:text-slate-200" },
						"Zonificare actuală (tematic)",
					),
					t.div({ class: "text-xs text-slate-400 dark:text-slate-500 truncate" }, () =>
						STATE.zonificare_error
							? STATE.zonificare_error
							: STATE.zonificare_derogari >= 0
								? `PUG (${STATE.zonificare_utr || "UTR"}) decupat de ${STATE.zonificare_derogari} ` +
									(STATE.zonificare_derogari === 1 ? "derogare" : "derogări")
								: "zonificarea în vigoare: PUG decupat de PUZ-urile aprobate",
					),
				),
				t.button(
					{
						class: () =>
							`flex-shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium transition ` +
							(STATE.zonificare_visible
								? "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100"
								: "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:bg-slate-200"),
						title: () =>
							STATE.zonificare_visible
								? "Ascunde zonificarea actuală"
								: "Afișează zonificarea în vigoare (PUG decupat de PUZ-urile aprobate)",
						disabled: () => STATE.zonificare_loading,
						onclick: () => void set_zonificare_visible(!STATE.zonificare_visible),
					},
					() => (STATE.zonificare_loading ? "…" : STATE.zonificare_visible ? "vizibil" : "ascuns"),
				),
			),
		),
		// Stratul VIRTUAL „solicitări" (F13) — toate cererile cu locație, tematic pe status.
		CTX.caps.documents
			? t.div(
					{
						class:
							"flex items-center gap-2 py-1.5 mt-1 pt-2 border-t border-slate-200 dark:border-slate-700",
					},
					t.span({
						class: "inline-block w-[14px] h-[14px] rounded-full flex-shrink-0",
						style: "background:#d97706; border:2px solid #fff; box-shadow:0 0 0 1px #cbd5e1",
					}),
					t.div(
						{ class: "flex-1 min-w-0" },
						t.div(
							{ class: "text-xs font-medium text-slate-800 dark:text-slate-200" },
							"Solicitări (tematic)",
						),
						t.div(
							{ class: "text-xs text-slate-400 dark:text-slate-500" },
							"toate cererile cu locație, colorate pe status",
						),
					),
					t.button(
						{
							class: () =>
								`flex-shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium transition ` +
								(STATE.solicitari_visible
									? "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100"
									: "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:bg-slate-200"),
							title: () =>
								STATE.solicitari_visible
									? "Ascunde solicitările"
									: "Afișează solicitările pe hartă",
							onclick: () => void set_solicitari_visible(!STATE.solicitari_visible),
						},
						() => (STATE.solicitari_visible ? "vizibil" : "ascuns"),
					),
				)
			: "",
		// Stratul VIRTUAL „acte" (F5-T9 → N3: documentele hărții, cu starea lor) — doar intern
		// (date de solicitări); Faza 2: și pe contractul org-ului (modulul `rapoarte`) — hidden reactiv.
		CTX.caps.documents
			? t.div(
					{
						class: () =>
							"flex items-center gap-2 py-1.5 mt-1 pt-2 border-t border-slate-200 dark:border-slate-700" +
							(rapoarte_available() ? "" : " hidden"),
					},
					t.span({
						class: "inline-block w-[14px] h-[14px] rounded-full flex-shrink-0",
						style: "background:#059669; border:2px solid #fff; box-shadow:0 0 0 1px #cbd5e1",
					}),
					t.div(
						{ class: "flex-1 min-w-0" },
						t.div(
							{ class: "text-xs font-medium text-slate-800 dark:text-slate-200" },
							"Acte (tematic)",
						),
						t.div(
							{ class: "text-xs text-slate-400 dark:text-slate-500" },
							"acte emise, pregătite și anulate, cu starea lor",
						),
					),
					t.button(
						{
							class: () =>
								`flex-shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium transition ` +
								(STATE.acte_visible
									? "bg-emerald-50 dark:bg-emerald-950 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100"
									: "bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:bg-slate-200"),
							title: () =>
								STATE.acte_visible
									? "Ascunde actele"
									: "Afișează actele pe hartă, colorate după stare",
							onclick: () => void set_acte_visible(!STATE.acte_visible),
						},
						() => (STATE.acte_visible ? "vizibil" : "ascuns"),
					),
				)
			: "",
		// Empty state
		t.div(
			{
				class: "text-xs text-slate-400 dark:text-slate-500 italic py-2",
				style: () => {
					const d = STATE.map_detail
					const empty = d && d.layers.length === 0 && !STATE.map_loading
					return `display:${empty ? "block" : "none"}`
				},
			},
			"Nicio hartă nu are straturi definite.",
		),
	)
}

// ─────────────────────────────────────────────────────────────
// Poze de teren (F9-T3) — compresie în browser + galerie în FeaturePanel
// ─────────────────────────────────────────────────────────────

/**
 * Redimensionează poza ÎN BROWSER (max 1600px, JPEG ~300KB): bandă mică pe 4G, HEIC-ul de
 * iPhone devine JPEG universal, iar re-encodarea prin canvas șterge EXIF-ul (inclusiv GPS-ul).
 * La orice eșec de decodare întoarce fișierul original — serverul validează oricum magic bytes.
 */
async function compress_photo(file: File): Promise<Blob | null> {
	try {
		const bmp = await createImageBitmap(file)
		const scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height))
		const w = Math.max(1, Math.round(bmp.width * scale))
		const h = Math.max(1, Math.round(bmp.height * scale))
		const canvas = document.createElement("canvas")
		canvas.width = w
		canvas.height = h
		const ctx = canvas.getContext("2d")
		if (!ctx) {
			return null
		}
		ctx.drawImage(bmp, 0, 0, w, h)
		bmp.close()
		return await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.8))
	} catch {
		return null // browserul nu poate decoda fișierul (ex. HEIC pe Chrome/Android)
	}
}

async function upload_feature_photo(file: File): Promise<void> {
	const id = STATE.selected_feature_id
	if (!id || STATE.feature_photo_busy) {
		return
	}
	STATE.feature_photo_busy = true
	STATE.feature_photo_error = ""
	const compressed = await compress_photo(file)
	// Fără compresie + format pe care browserele nu-l redau (HEIC pe Chrome/Android) →
	// eroare clară, nu o poză de neafișat în galerie.
	if (!compressed && /hei[cf]/i.test(file.type + " " + file.name)) {
		STATE.feature_photo_busy = false
		STATE.feature_photo_error =
			"Formatul HEIC nu poate fi convertit pe acest dispozitiv — alegeți un JPEG/PNG."
		return
	}
	const fd = new FormData()
	fd.append("file", compressed ?? file, "poza.jpg")
	const r = await api_upload<{ key: string; mime: string; size: number; at: string }>(
		`/api/features/${encodeURIComponent(id)}/photos`,
		fd,
	)
	STATE.feature_photo_busy = false
	if (!r.ok) {
		STATE.feature_photo_error = r.err?.message ?? "Încărcarea pozei a eșuat."
		return
	}
	// Actualizare LOCALĂ (serverul întoarce poza creată): fără refetch → fără flicker de panou,
	// modul „Gestionează" rămâne, iar dacă utilizatorul a selectat ÎNTRE timp alt feature nu-i
	// suprascriem panoul (upload-ul pe 4G poate dura). Istoricul se împrospătează discret.
	const f = STATE.feature_detail
	if (r.data && f && STATE.selected_feature_id === id) {
		const photos = parse_photos((f.properties ?? {})[PHOTOS_KEY])
		STATE.feature_detail = {
			...f,
			properties: { ...f.properties, [PHOTOS_KEY]: [...photos, r.data] },
		}
		void load_feature_history(id)
	}
}

async function remove_feature_photo(photo_id: string): Promise<void> {
	const id = STATE.selected_feature_id
	if (!id || !confirm("Ștergeți poza? Fișierul dispare definitiv.")) {
		return
	}
	STATE.feature_photo_error = ""
	const r = await api_delete<{ id: string }>(
		`/api/features/${encodeURIComponent(id)}/photos/${encodeURIComponent(photo_id)}`,
	)
	if (!r.ok) {
		STATE.feature_photo_error = r.err?.message ?? "Ștergerea pozei a eșuat."
		return
	}
	// Actualizare locală (vezi upload) — modul „Gestionează" rămâne activ pentru următoarea poză.
	const f = STATE.feature_detail
	if (f && STATE.selected_feature_id === id) {
		const photos = parse_photos((f.properties ?? {})[PHOTOS_KEY]).filter(
			(p) => photo_uuid(p) !== photo_id,
		)
		STATE.feature_detail = { ...f, properties: { ...f.properties, [PHOTOS_KEY]: photos } }
		void load_feature_history(id)
	}
}

/** Galeria pozelor de teren — DOAR intern (publicul nu primește nici cheia, nici fișierele). */
function PhotosSection() {
	if (CTX.is_public) {
		return t.span({ style: "display:none" })
	}
	return t.div(
		{ class: "mb-3" },
		t.div(
			{
				class:
					"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
			},
			"Poze de teren",
		),
		() => {
			const f = STATE.feature_detail
			const id = STATE.selected_feature_id
			const photos = f && id ? parse_photos((f.properties ?? {})[PHOTOS_KEY]) : []
			return t.div(
				t.div(
					{ class: "flex flex-wrap gap-2" },
					...photos.map((p) => {
						const url = `/api/features/${encodeURIComponent(id ?? "")}/photos/${encodeURIComponent(photo_uuid(p))}`
						return t.div(
							{ class: "relative" },
							t.a(
								{ href: url, target: "_blank", rel: "noopener", title: "Deschide poza" },
								t.img({
									src: url,
									loading: "lazy",
									alt: "poză de teren",
									class:
										"w-20 h-20 object-cover rounded-lg border border-slate-200 dark:border-slate-700",
								}),
							),
							// ✕ apare DOAR în modul „Gestionează" (+ confirm) — două gesturi deliberate,
							// nu un buton permanent pe colțul fiecărei poze (mis-tap pe mobil).
							can_edit_map() && STATE.feature_photo_manage
								? t.button(
										{
											class:
												"absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-600 text-white text-[10px] leading-none shadow",
											title: "Șterge poza",
											onclick: () => void remove_feature_photo(photo_uuid(p)),
										},
										"✕",
									)
								: t.span({ style: "display:none" }),
						)
					}),
					photos.length === 0
						? t.div(
								{ class: "text-xs text-slate-400 dark:text-slate-500 italic py-1" },
								"Nicio poză.",
							)
						: t.span({ style: "display:none" }),
				),
				// Faza 2: fără modulul de teren, pozele EXISTENTE se văd și se pot ȘTERGE
				// („Gestionează" rămâne pe can_edit_map — serverul lasă intenționat ștergerea
				// deschisă, GDPR/curățenie), dar nu se mai ADAUGĂ (butoanele de upload dispar).
				can_edit_map()
					? t.div(
							{ class: "flex items-center gap-2 mt-2 flex-wrap" },
							// Două intrări DOAR pe mobil (26.08): „Fotografiază" (capture → camera direct)
							// + „Din galerie" (chooser-ul complet; cu un singur input + capture, iOS nu
							// oferea galeria). Pe desktop capture e ignorat și ambele ar deschide același
							// file-picker → rămâne un singur buton, cu etichetă neutră.
							...(teren_available()
								? ([
										{ label: "📷 Fotografiază", capture: true, cls: " md:hidden" },
										{ label: "🖼 Din galerie", capture: false, cls: "", desktop: "📷 Adaugă poză" },
									] as const)
								: []
							).map((v) =>
								t.label(
									{
										class:
											"inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-300 dark:border-slate-600 text-xs cursor-pointer hover:border-cyan-500 select-none" +
											v.cls,
									},
									() =>
										STATE.feature_photo_busy
											? t.span("Se încarcă…")
											: "desktop" in v
												? t.span(
														t.span({ class: "md:hidden" }, v.label),
														t.span({ class: "hidden md:inline" }, v.desktop),
													)
												: t.span(v.label),
									t.input({
										type: "file",
										accept: "image/*",
										...(v.capture ? { capture: "environment" } : {}),
										class: "hidden",
										disabled: () => STATE.feature_photo_busy,
										onchange: (e: Event) => {
											const input = e.target as HTMLInputElement
											const file = input.files?.[0]
											input.value = "" // aceeași poză de două ori la rând tot declanșează onchange
											if (file) {
												void upload_feature_photo(file)
											}
										},
									}),
								),
							),
							photos.length > 0
								? t.button(
										{
											class: () =>
												"px-2 py-1 rounded-md border text-xs cursor-pointer select-none " +
												(STATE.feature_photo_manage
													? "border-rose-400 text-rose-600 dark:text-rose-400"
													: "border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-500"),
											onclick: () => {
												STATE.feature_photo_manage = !STATE.feature_photo_manage
											},
										},
										() => (STATE.feature_photo_manage ? "Gata" : "Gestionează"),
									)
								: t.span({ style: "display:none" }),
						)
					: t.span({ style: "display:none" }),
				() =>
					STATE.feature_photo_error
						? t.div({ class: "text-xs text-rose-600 mt-1" }, STATE.feature_photo_error)
						: t.span({ style: "display:none" }),
			)
		},
	)
}

function PropertiesTable(props: Record<string, unknown>, hide_photos = false) {
	// La Proprietăți, `photos` (F9-T3) nu se afișează ca JSON brut — îl randează galeria;
	// la Metadate cheia rămâne vizibilă (alt spațiu de chei, negestionat de sistem).
	const entries = Object.entries(props).filter(([k]) => !hide_photos || k !== PHOTOS_KEY)
	if (entries.length === 0) {
		return t.div(
			{ class: "text-xs text-slate-400 dark:text-slate-500 italic py-1" },
			"Nicio proprietate.",
		)
	}
	return t.table(
		{ class: "w-full text-xs border-collapse" },
		t.tbody(
			...entries.map(([k, v], i) =>
				t.tr(
					{ class: i % 2 === 0 ? "bg-slate-50 dark:bg-slate-950" : "bg-white dark:bg-slate-900" },
					t.td(
						{
							class:
								"py-1 px-2 text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap w-2/5 align-top",
						},
						k,
					),
					t.td(
						{ class: "py-1 px-2 text-slate-800 dark:text-slate-200 break-words" },
						v == null ? "" : String(typeof v === "object" ? JSON.stringify(v) : v),
					),
				),
			),
		),
	)
}

function HistoryList(events: HistoryEvent[]) {
	if (events.length === 0) {
		return t.div(
			{ class: "text-xs text-slate-400 dark:text-slate-500 italic py-1" },
			"Niciun istoric.",
		)
	}
	// Ultima versiune (curentă) nu primește buton de revert — ești deja pe ea.
	const max_version = events.reduce(
		(m, e) => (e.kind === "version" && (e.version ?? 0) > m ? (e.version ?? 0) : m),
		0,
	)
	return t.div(
		{ class: "space-y-1.5" },
		...events.map((ev) => {
			const when = ev.at ? new Date(ev.at).toLocaleString("ro-RO") : ""
			// Agentul MCP al primăriei apare ca „Agent AI”, nu cu adresa lui tehnică (lib/mcp_agent).
			const author = ev.author
				? t.span(
						{ class: "text-cyan-600 text-[10px]" },
						is_mcp_agent(ev.author) ? author_label(ev.author) : `@${ev.author}`,
					)
				: t.span()
			const message = ev.message
				? t.div({ class: "text-slate-600 dark:text-slate-300 mt-0.5" }, ev.message)
				: t.div()

			if (ev.kind === "version") {
				// Revert (F13-T4): doar intern + editor, doar pe versiuni ANTERIOARE ne-șterse.
				const can_revert =
					!CTX.is_public &&
					ev.version !== undefined &&
					ev.version < max_version &&
					ev.change_type !== "delete"
				const revert_btn = can_revert
					? t.button(
							{
								class: () =>
									"ml-auto flex-shrink-0 text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer " +
									(can_edit_map() ? "" : "hidden"),
								title: "Creează o versiune nouă cu geometria și atributele acestei versiuni",
								onclick: () => void revert_selected_feature(ev.version as number),
							},
							`Revino la v${ev.version}`,
						)
					: t.span()
				return t.div(
					{
						class:
							"bg-white dark:bg-slate-900 border-l-2 border-slate-300 dark:border-slate-600 rounded-r-lg pl-2.5 pr-2 py-1.5 text-xs",
					},
					t.div(
						{ class: "flex items-center gap-2 flex-wrap" },
						t.span(
							{
								class:
									"font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wide text-[10px]",
							},
							`v${ev.version} ${ev.change_type}`,
						),
						t.span({ class: "text-slate-400 dark:text-slate-500" }, when),
						author,
						revert_btn,
					),
					message,
				)
			}

			// kind === "attribute" — schimbare de atribut; delete = tombstone (cheie tăiată)
			const deleted = ev.change_type === "delete"
			// `photos` (F9-T3): valoarea e array-ul tehnic cu chei de storage — pentru funcționar
			// se redă doar numărul de poze după modificare, nu JSON-ul brut (constatare 26.08).
			const is_photos = ev.key === PHOTOS_KEY
			return t.div(
				{
					class:
						"bg-white dark:bg-slate-900 border-l-2 border-amber-300 rounded-r-lg pl-2.5 pr-2 py-1.5 text-xs",
				},
				t.div(
					{ class: "flex items-center gap-2 flex-wrap" },
					deleted
						? t.span({ class: "font-mono text-slate-400 dark:text-slate-500 line-through" }, ev.key)
						: is_photos
							? t.span({ class: "text-slate-700 dark:text-slate-200" }, fmt_prop(ev.key, ev.value))
							: t.span(
									{ class: "font-mono text-slate-700 dark:text-slate-200" },
									`${ev.key}: ${fmt_cell(ev.value)}`,
								),
					deleted
						? t.span({ class: "text-[10px] text-red-500 uppercase font-semibold" }, "șters")
						: t.span(),
					t.span({ class: "text-slate-400 dark:text-slate-500" }, when),
					author,
				),
				message,
			)
		}),
	)
}

// ─────────────────────────────────────────────────────────────
// Panoul de regim urbanistic (F5-T2, REQ-URB-003/006): coloana din DREAPTA (ca FeaturePanel —
// se exclud prin interacțiune: intrarea în modul regim închide selecția de feature, iar cât
// modul e activ handler-ele per-strat se abțin). Nod STABIL, ascuns prin display (F8).
// ─────────────────────────────────────────────────────────────

/** Un rând etichetă → valoare din fișa RLU / indicatori (valorile pot fi text normativ lung).
 *  `hint` (opțional, modul public): explicația non-tehnică a termenului, sub etichetă. */
function regim_row(label: string, value: unknown, hint = "") {
	const text = value == null ? "" : String(value).trim()
	if (!text || text === "Nu este cazul") {
		return "" // câmpurile goale/neaplicabile nu ocupă loc — fișa are ~23 de rânduri
	}
	return t.div(
		{ class: "py-1.5 border-b border-slate-100 dark:border-slate-800 last:border-0" },
		t.div(
			{
				class:
					"text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400",
			},
			label,
		),
		hint
			? t.div(
					{ class: "text-[10px] text-slate-400 dark:text-slate-500 leading-tight mb-0.5" },
					hint,
				)
			: "",
		t.div(
			{ class: "text-xs text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words" },
			text,
		),
	)
}

// Etichetele panoului de regim (F6-T2, REQ-PEX-006): publicul primește limbaj NON-TEHNIC +
// explicații scurte; intern rămân termenii regulamentului. Alese la montare (CTX e fix).
function regim_labels() {
	if (!CTX.is_public) {
		return {
			titlu: "Regim urbanistic",
			pot: "POT maxim",
			pot_hint: "",
			cut: "CUT maxim",
			cut_hint: "",
			rh: "Regim de înălțime maxim",
			rh_hint: "",
			admise: "Utilizări admise",
			cond: "Utilizări admise cu condiționări",
			interzise: "Utilizări interzise",
			avize: "Avize și acorduri necesare",
		}
	}
	return {
		titlu: "Ce poți construi aici?",
		pot: "Ocuparea maximă a terenului (POT)",
		pot_hint: "cât din suprafața terenului poate fi acoperită de construcții",
		cut: "Intensitatea maximă a construirii (CUT)",
		cut_hint: "totalul suprafețelor construite, raportat la suprafața terenului",
		rh: "Înălțimea maximă a clădirilor",
		rh_hint: "regimul de înălțime (ex. P+2 = parter plus două etaje)",
		admise: "Ce se poate construi",
		cond: "Permis doar cu condiții",
		interzise: "Interzis în această zonă",
		avize: "Avize și acorduri de care vei avea nevoie",
	}
}

/** Avizele necesare în zonă (decizia 4.8, contract avize.md) — listă simplă; goală = nu apare. */
function regim_avize_block(r: Regim, label: string) {
	const list = Array.isArray(r.avize) ? r.avize.filter((a) => typeof a === "string" && a) : []
	if (list.length === 0) {
		return ""
	}
	return t.div(
		{ class: "mb-3" },
		t.div(
			{
				class:
					"text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1",
			},
			label,
		),
		t.ul(
			{
				class:
					"rounded-lg border border-slate-200 dark:border-slate-700 p-3 pl-7 list-disc space-y-0.5 text-xs text-slate-700 dark:text-slate-200",
			},
			...list.map((a) => t.li(a)),
		),
	)
}

/** Nomenclatorul legislativ al hărții (REQ-URB-004) — bloc comun intravilan/extravilan. */
function regim_legislatie_block(r: Regim) {
	if (r.legislatie.length === 0) {
		return ""
	}
	return t.div(
		{ class: "mt-3" },
		t.div(
			{
				class:
					"text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1",
			},
			"Nomenclator legislativ",
		),
		t.div(
			{ class: "rounded-lg border border-slate-200 dark:border-slate-700 p-3 space-y-1.5" },
			...r.legislatie.map((l) =>
				t.div(
					{ class: "text-xs text-slate-700 dark:text-slate-200" },
					t.span({ class: "font-semibold" }, l.act),
					l.descriere ? ` — ${l.descriere}` : "",
					l.url
						? t.a(
								{
									href: l.url,
									target: "_blank",
									rel: "noopener",
									class: "ml-1 text-cyan-700 dark:text-cyan-300 hover:underline",
									title: l.url,
								},
								"↗",
							)
						: "",
				),
			),
		),
	)
}

/**
 * „API-uri definibile" (F7-T4, REQ-API-004): butoane către sisteme EXTERNE, definite pe hartă
 * (`attributes.api_links`, editate în #/admin — clonă a nomenclatorului legislativ), cu
 * placeholders {lng}/{lat}/{attr:*} completați din elementul selectat. Pe calea publică linkurile
 * vin gata parsate de server (`api_links` din PublicMapView), iar `properties` sunt deja
 * F10-filtrate — substituția nu poate scurge un atribut restricționat.
 */
function api_links_block(f: FeatureDetail) {
	const d = STATE.map_detail
	const links: ApiLink[] = CTX.is_public
		? (d?.api_links ?? [])
		: parse_api_links(d?.attributes?.api_links)
	const slug = f.layer_slug ?? ""
	// `layers` gol = linkul apare peste tot; altfel doar pe straturile listate (necunoscut → tot).
	const visible = links.filter(
		(l) => l.layers.length === 0 || slug === "" || l.layers.includes(slug),
	)
	if (visible.length === 0) {
		return ""
	}
	const [lng, lat] = anchor_point(f.geometry as { type?: string; coordinates?: unknown })
	const items = visible
		.map((l) => {
			const href = expand_url_template(l.url_template, { lng, lat, props: f.properties ?? {} })
			return href
				? t.a(
						{
							href,
							target: "_blank",
							rel: "noopener",
							class:
								"inline-flex items-center gap-1 rounded-lg border border-slate-200 dark:border-slate-700 " +
								"px-2 py-1 text-xs font-semibold text-cyan-700 dark:text-cyan-300 hover:bg-slate-50 dark:hover:bg-slate-800",
							title: href,
						},
						`${l.label} ↗`,
					)
				: "" // șablon nesigur (non-http) → nu se randează deloc
		})
		.filter((x) => x !== "")
	if (items.length === 0) {
		return ""
	}
	return t.div(
		{ class: "mb-3" },
		t.div(
			{
				class:
					"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
			},
			"Interogare sisteme externe",
		),
		t.div({ class: "flex flex-wrap gap-1.5" }, ...items),
	)
}

function RegimPanel() {
	const L = regim_labels() // limbaj non-tehnic în public, termenii regulamentului intern
	// Corpul se re-randează reactiv din STATE.regim (funcția-copil nu întoarce niciodată null).
	const body = () => {
		if (STATE.regim_loading) {
			return t.div(
				{ class: "flex items-center gap-2 py-3 text-xs text-slate-400 dark:text-slate-500 italic" },
				Spinner("w-4 h-4"),
				"Se interoghează regimul…",
			)
		}
		const r = STATE.regim
		if (!r) {
			// Fail-ul serverului (ex. public: regim indisponibil — strat UTR nepublicat / F10) are
			// mesaj propriu; fără el, panoul ar reafișa instrucțiunea ca și cum clickul n-ar fi contat.
			if (STATE.regim_error) {
				return t.div(
					{
						class:
							"rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 p-3 text-xs text-amber-800 dark:text-amber-300",
					},
					STATE.regim_error,
				)
			}
			return t.div(
				{ class: "text-xs text-slate-500 dark:text-slate-400 py-3" },
				"Click pe orice punct al hărții pentru regimul urbanistic al parcelei — fără a selecta un strat în prealabil.",
			)
		}
		if (r.in_extravilan) {
			return t.div(
				t.div(
					{
						class:
							"rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-600 dark:text-slate-300",
					},
					`Punctul (${r.lng.toFixed(5)}, ${r.lat.toFixed(5)}) e în afara unităților teritoriale de referință ale hărții (extravilan).`,
				),
				t.div({ class: "mt-3" }, regim_avize_block(r, L.avize)), // avizele GENERALE (și în extravilan)
				regim_legislatie_block(r),
			)
		}
		const denumire = String(r.indicatori.denumire ?? r.utr.attributes.denumire ?? "").trim()
		return t.div(
			// Derogarea PRIMA (REQ-URB-008: regimul derogării câștigă) — vizual deasupra regimului de bază.
			// Public fără detalii (strat derogare nepublicat / act restricționat F10): serverul trimite
			// doar EXISTENȚA (F6-T2) — regimul de bază nu e ultimul cuvânt, detaliile sunt la primărie.
			r.derogare
				? t.div(
						{
							class:
								"rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-300 dark:border-amber-800 p-3 mb-3",
						},
						t.div(
							{
								class:
									"text-[11px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1",
							},
							"Derogare (PUZ/HCL aprobat) — prevalează",
						),
						t.div(
							{ class: "text-xs text-amber-900 dark:text-amber-200" },
							[r.derogare.act, r.derogare.hcl].filter(Boolean).join(" · ") ||
								(CTX.is_public
									? "Zona are o derogare aprobată — detaliile complete se pot obține de la primărie."
									: "act de derogare fără denumire"),
						),
					)
				: "",
			// UTR + denumirea zonei
			t.div(
				{ class: "flex items-baseline gap-2 mb-1" },
				t.span(
					{ class: "text-2xl font-bold text-emerald-800 dark:text-emerald-300" },
					r.utr.cod || "?",
				),
				t.span({ class: "text-xs text-slate-500 dark:text-slate-400" }, denumire),
			),
			t.div(
				{ class: "text-[11px] text-slate-400 dark:text-slate-500 mb-3" },
				`strat: ${r.utr.layer_slug} · punct (${r.lng.toFixed(5)}, ${r.lat.toFixed(5)})`,
			),
			// Indicatorii urbanistici (⚠️ POT/CUT pot fi TEXT normativ, nu doar numere)
			t.div(
				{
					class:
						"rounded-lg bg-emerald-50/50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 p-3 mb-3",
				},
				regim_row(L.pot, r.indicatori.pot_max, L.pot_hint),
				regim_row(L.cut, r.indicatori.cut_max, L.cut_hint),
				regim_row(L.rh, r.indicatori.rh_max, L.rh_hint),
			),
			// Avizele necesare în zonă (4.8) — imediat sub indicatori
			regim_avize_block(r, L.avize),
			// Funcțiuni (permise / condiționate / interzise)
			regim_row(L.admise, r.functiuni.admise),
			regim_row(L.cond, r.functiuni.admise_cu_conditionari),
			regim_row(L.interzise, r.functiuni.interzise),
			// Fișa RLU integrală, colapsabilă
			Object.keys(r.fisa).length > 0
				? t.details(
						{ class: "mt-3" },
						t.summary(
							{
								class:
									"cursor-pointer text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 select-none",
							},
							`Fișa RLU completă (${Object.keys(r.fisa).length} câmpuri)`,
						),
						t.div(
							{ class: "mt-2 rounded-lg border border-slate-200 dark:border-slate-700 p-3" },
							...Object.entries(r.fisa).map(([k, v]) => regim_row(k, v)),
						),
					)
				: "",
			// Nomenclatorul legislativ (REQ-URB-004) — legile care guvernează regimurile.
			regim_legislatie_block(r),
			// F5-T5: puntea spre flux — deschide formularul de solicitare PRE-COMPLETAT (fluxul de
			// ghișeu: operatorul depune în numele solicitantului), cu punctul regimului ca locație.
			// Din cerere, actul se generează cu autofill din regim (F5-T4). Cere harta asignată
			// unei primării — altfel nu există destinatar pentru cerere.
			CTX.caps.documents
				? t.button(
						{
							// Gating pe contract: fără `emitere_cu` pe org-ul hărții, puntea spre flux dispare
							// (class reactiv — workspace-ul se poate schimba; refuzul real e pe server).
							class: () =>
								"mt-4 w-full px-3 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold " +
								"hover:bg-emerald-800 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" +
								(!STATE.map_detail?.org_slug ||
								session_org_feature(STATE.map_detail.org_slug, "emitere_cu")
									? ""
									: " hidden"),
							disabled: () => !STATE.map_detail?.org_slug,
							title: () =>
								STATE.map_detail?.org_slug
									? "Creează o cerere de CU de informare pentru acest punct (formular pre-completat)"
									: "Harta nu e asignată unei primării — nu există destinatar pentru cerere.",
							onclick: () => {
								const d = STATE.map_detail
								const rg = STATE.regim
								if (!d?.org_slug || !rg) {
									return
								}
								const p = new URLSearchParams({
									new: "1",
									org: d.org_slug,
									map_id: d.id,
									tip_act: "cu_informare",
									lng: String(rg.lng),
									lat: String(rg.lat),
									location_source: "pin",
								})
								location.hash = `#/solicitari?${p.toString()}`
							},
						},
						"Generează CU de informare",
					)
				: "",
			// Public (F6-T2): disclaimer NON-negociabil — panoul informează, nu certifică (REQ-PEX-006;
			// același text e și în ghidul cetățeanului).
			CTX.is_public
				? t.div(
						{
							class:
								"mt-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-300 leading-snug",
						},
						"Informațiile au caracter informativ și nu țin loc de certificat de urbanism. ",
						"Pentru un act oficial, trimite o solicitare către primărie.",
					)
				: "",
			// Public (F6-T2): puntea cetățeanului e sesizarea existentă (anonim → formular autentificat,
			// pre-completat cu punctul regimului ca pin) — nu generarea de acte (aia rămâne la ghișeu).
			CTX.caps.ticket && STATE.ticket_available
				? t.button(
						{
							class:
								"mt-4 w-full px-3 py-2 rounded-lg bg-cyan-700 text-white text-xs font-semibold " +
								"hover:bg-cyan-800 cursor-pointer",
							title:
								"Depune o solicitare către primărie pentru acest punct (necesită cont; formular pre-completat)",
							onclick: () => {
								const rg = STATE.regim
								if (rg) {
									open_ticket({ lng: rg.lng, lat: rg.lat, source: "pin" })
								}
							},
						},
						"Fă o solicitare pentru acest punct",
					)
				: "",
		)
	}

	return t.div(
		{
			id: "regim-panel",
			// Mobil: BOTTOM-SHEET (max 55dvh — harta rămâne vizibilă deasupra); desktop (md+):
			// exact coloana istorică din dreapta — fiecare utilitar de bază scos are geamănul md:.
			class:
				"absolute inset-x-0 bottom-0 max-h-[55dvh] rounded-t-2xl shadow-xl border-t md:static " +
				"md:max-h-none md:rounded-none md:shadow-none md:border-t-0 md:border-l " +
				"md:w-[380px] md:min-w-[260px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 flex-col overflow-hidden z-30",
			style: () =>
				`display:${STATE.regim_mode || STATE.regim || STATE.regim_loading ? "flex" : "none"}`,
		},
		t.div(
			{
				class:
					"flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-emerald-50/60 dark:bg-emerald-950/40 flex-shrink-0",
			},
			t.div(
				{
					class:
						"text-sm font-bold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5",
				},
				icon("book-open-text"),
				L.titlu,
			),
			t.button(
				{
					class:
						// p-2 -m-2: hit-area touch ~40px, zero deplasare vizuală (marginile negative anulează)
						"p-2 -m-2 text-slate-400 dark:text-slate-500 hover:text-slate-600 text-base leading-none cursor-pointer",
					title: "Închide (iese din modul regim)",
					onclick: () => set_regim_mode(false),
				},
				"✕",
			),
		),
		t.div({ class: "flex-1 overflow-y-auto p-4" }, body),
	)
}

/**
 * Secțiunea de editare din FeaturePanel (F13-T4): atribute / geometrie / ștergere.
 * Montată doar intern; vizibilă prin class doar pentru editorii hărții (sesiunea se
 * hidratează async — gating reactiv, nu snapshot). Refuzul real rămâne pe server.
 */
function FeatureEditSection() {
	if (CTX.is_public) {
		return t.span({ style: "display:none" })
	}
	const small_btn =
		"rounded-md px-2 py-1 text-[11px] font-semibold border transition cursor-pointer "
	return t.div(
		{ class: () => "mb-3 " + (can_edit_map() ? "" : "hidden") },
		t.div(
			{
				class:
					"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
			},
			"Editare",
		),
		() => {
			void STATE.feature_edit_rev // „Adaugă atribut" scrie în modul → re-randăm de aici
			if (!STATE.feature_edit_open) {
				return t.div(
					{ class: "flex flex-wrap gap-1.5" },
					t.button(
						{
							class:
								small_btn +
								"bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-indigo-400",
							onclick: () => open_feature_edit(),
						},
						"Editează atributele",
					),
					t.button(
						{
							class:
								small_btn +
								"bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-indigo-400",
							title:
								"Încarcă geometria în editorul de pe hartă (trage de vertecși, apoi Salvează geometria)",
							onclick: () => start_geometry_edit(),
						},
						"Editează geometria",
					),
					t.button(
						{
							class:
								small_btn +
								"bg-white dark:bg-slate-900 border-red-200 dark:border-red-900 text-red-600 hover:border-red-500",
							onclick: () => void delete_selected_feature(),
						},
						"Șterge",
					),
				)
			}
			// Formularul de atribute: cheile existente + adăugare de cheie nouă.
			const add_key = t.input({
				class:
					"flex-1 min-w-0 border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900",
				placeholder: "cheie nouă",
			})
			const add_val = t.input({
				class:
					"flex-1 min-w-0 border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900",
				placeholder: "valoare",
			})
			return t.div(
				t.div(
					{ class: "max-h-56 overflow-y-auto space-y-1.5 mb-2 pr-1" },
					...Object.keys(feature_edit_values).map((key) => {
						const values = allowed_values(feature_edit_schema, key)
						const cur = feature_edit_values[key] ?? ""
						return t.label(
							{ class: "block" },
							t.span({ class: "text-[10px] font-mono text-slate-500 dark:text-slate-400" }, key),
							values
								? // 1.6: listă ÎNCHISĂ → select; valoarea veche (import) rămâne ca opțiune marcată
									t.select(
										{
											class:
												"w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900",
											onchange: (e: Event) => {
												feature_edit_values[key] = (e.target as HTMLSelectElement).value
											},
										},
										t.option({ value: "", selected: cur === "" }, "— nesetat —"),
										...values.map((v) =>
											t.option({ value: String(v), selected: String(v) === cur }, String(v)),
										),
										...(cur !== "" && !values.some((v) => String(v) === cur)
											? [t.option({ value: cur, selected: true }, `${cur} (valoare veche)`)]
											: []),
									)
								: t.input({
										class:
											"w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900",
										value: cur,
										oninput: (e: Event) => {
											feature_edit_values[key] = (e.target as HTMLInputElement).value
										},
									}),
						)
					}),
				),
				t.div(
					{ class: "flex items-center gap-1.5 mb-2" },
					add_key,
					add_val,
					t.button(
						{
							class:
								small_btn +
								"flex-shrink-0 bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-indigo-400",
							title: "Adaugă un atribut nou (se salvează ca text)",
							onclick: () => {
								const k = add_key.value.trim()
								if (!k || k in feature_edit_values) {
									return
								}
								feature_edit_values[k] = add_val.value
								feature_edit_types[k] = "string"
								STATE.feature_edit_rev++
							},
						},
						"+",
					),
				),
				t.div(
					{ class: "flex items-center gap-2" },
					t.button(
						{
							class: () =>
								small_btn +
								(STATE.feature_edit_saving
									? "bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 cursor-not-allowed"
									: "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"),
							disabled: () => STATE.feature_edit_saving,
							onclick: () => void save_feature_edit(),
						},
						() => (STATE.feature_edit_saving ? "Se salvează…" : "Salvează atributele"),
					),
					t.button(
						{
							class:
								small_btn +
								"bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-slate-500",
							onclick: () => {
								STATE.feature_edit_open = false
								STATE.feature_edit_error = ""
							},
						},
						"Renunță",
					),
				),
			)
		},
		t.div(
			{
				class: () =>
					"mt-1.5 text-[11px] font-semibold text-red-600 " +
					(STATE.feature_edit_error ? "" : "hidden"),
			},
			() => STATE.feature_edit_error,
		),
	)
}

function FeaturePanel() {
	return t.div(
		{
			id: "feature-panel",
			// Mobil: BOTTOM-SHEET (max 55dvh); desktop (md+): coloana istorică din dreapta —
			// fiecare utilitar de bază scos are geamănul md: (vezi și #regim-panel).
			class:
				"absolute inset-x-0 bottom-0 max-h-[55dvh] rounded-t-2xl shadow-xl border-t md:static " +
				"md:max-h-none md:rounded-none md:shadow-none md:border-t-0 md:border-l " +
				"md:w-[360px] md:min-w-[260px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 flex-col overflow-hidden z-30",
			// Sidebar pe DREAPTA (desktop) / sheet jos (mobil): apare doar cu o entitate selectată.
			style: () => `display:${STATE.selected_feature_id ? "flex" : "none"}`,
		},
		t.div(
			{
				class:
					"flex-shrink-0 flex items-center justify-between px-4 h-11 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950",
			},
			t.span(
				{ class: "text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400" },
				"Entitate selectată",
			),
			t.button(
				{
					class:
						"p-2 -m-2 text-slate-400 dark:text-slate-500 hover:text-slate-700 font-bold text-base leading-none",
					title: "Închide panoul (deselectează)",
					onclick: () => {
						STATE.selected_feature_id = ""
						STATE.feature_detail = null
					},
				},
				"✕",
			),
		),
		t.div(
			{ class: "flex-1 overflow-y-auto p-3" },
			// Sesizare despre acest element (DOAR public + primărie) — SUS, imediat vizibil la selectare.
			FeatureTicketButton(),
			// Loading state
			t.div(
				{
					class: "flex items-center gap-2 py-2",
					style: () => `display:${STATE.feature_loading ? "flex" : "none"}`,
				},
				Spinner("w-4 h-4"),
				t.span(
					{ class: "text-xs text-slate-400 dark:text-slate-500 italic" },
					"Se încarcă entitatea...",
				),
			),
			// Error state
			MsgError(() => STATE.feature_error),
			// Feature content
			t.div(
				{
					style: () =>
						`display:${!STATE.feature_loading && STATE.feature_detail ? "block" : "none"}`,
				},
				() => {
					const f = STATE.feature_detail
					if (!f) {
						return t.div()
					}
					return t.div(
						// Header row
						t.div(
							{ class: "flex items-center gap-2 mb-3" },
							t.h3(
								{
									class:
										"text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex-1",
								},
								"Entitate selectată",
							),
							t.span(
								{
									class:
										"inline-block bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-[10px] font-semibold rounded-full px-2 py-0.5",
								},
								`v${f.version}`,
							),
						),
						// Stratul căruia îi aparține elementul (21.09): cu straturi suprapuse și reordonabile,
						// „ce am selectat?" nu se mai vede din context; numele e cel afișat, din lista hărții.
						() => {
							const slug = STATE.selected_feature_layer || f.layer_slug || ""
							const name = STATE.map_detail?.layers.find((l) => l.slug === slug)?.name || slug
							return name
								? t.div(
										{
											class: "text-xs text-slate-500 dark:text-slate-400 mb-2",
											"data-layer": slug,
										},
										t.span({ class: "font-semibold" }, "Strat: "),
										name,
									)
								: t.span({ style: "display:none" })
						},
						// Properties
						t.div(
							{ class: "mb-3" },
							t.div(
								{
									class:
										"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
								},
								"Proprietăți",
							),
							t.div(
								{
									class: "rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700",
								},
								PropertiesTable(f.properties ?? {}, true),
							),
						),
						// 1.5: coordonatele elementului — punctul, sau centrul geometriei (Stereo 70 + WGS84)
						(() => {
							if (f.geometry?.type === "Point") {
								const [lng, lat] = f.geometry.coordinates
								return CoordsBlock(lng, lat, "Coordonate")
							}
							const c = feature_center()
							return c ? CoordsBlock(c.lng, c.lat, "Centrul geometriei") : ""
						})(),
						// Metadata
						t.div(
							{ class: "mb-3" },
							t.div(
								{
									class:
										"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
								},
								"Metadate",
							),
							t.div(
								{
									class: "rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700",
								},
								PropertiesTable(f.metadata ?? {}),
							),
						),
						// „API-uri definibile" (F7-T4): interogarea sistemelor externe cu datele elementului.
						api_links_block(f),
						// Poze de teren (F9-T3) — doar intern
						PhotosSection(),
						// Editare (F13-T4) — doar intern, editor+ pe org-ul hărții (refuzul real e pe server).
						FeatureEditSection(),
					)
				},
			),
			// Documents section — leagă GIS-ul de documente în ambele direcții (DOAR intern: caps.documents).
			t.div(
				{
					class: "mb-3",
					style: () => {
						const has = STATE.feature_detail && !STATE.feature_loading && CTX.caps.documents
						return `display:${has ? "block" : "none"}`
					},
				},
				t.div(
					{
						class:
							"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5",
					},
					"Documente",
				),
				() => {
					if (STATE.feature_documents_loading) {
						return t.p(
							{ class: "text-xs text-slate-400 dark:text-slate-500 italic" },
							"Se încarcă documentele…",
						)
					}
					if (STATE.feature_documents.length === 0) {
						return t.p(
							{ class: "text-xs text-slate-400 dark:text-slate-500" },
							"Niciun document legat de entitate.",
						)
					}
					return t.div(
						{ class: "flex flex-col gap-1" },
						...STATE.feature_documents.map((d) =>
							t.div(
								{ class: "flex items-center gap-1.5 text-xs" },
								d.registry_code
									? t.span(
											{
												class:
													"px-1.5 py-0.5 rounded bg-cyan-50 dark:bg-cyan-950 text-cyan-800 dark:text-cyan-300 border border-cyan-200 text-[10px] font-bold font-mono" +
													(d.status === "anulat" ? " line-through opacity-60" : ""),
											},
											d.registry_code,
										)
									: t.span(),
								t.a(
									{
										class:
											"flex-1 truncate text-slate-700 dark:text-slate-200 hover:text-cyan-800 hover:underline" +
											(d.status === "anulat"
												? " line-through text-slate-400 dark:text-slate-500"
												: ""),
										href: `/api/documents/${d.id}/file`,
										title: d.name || d.filename,
									},
									d.name || d.filename,
								),
							),
						),
					)
				},
				// Generarea pornește DE AICI: parcela e deja selectată, alegi doar actul.
				// Șabloanele sunt FILTRATE per strat (F13): allowed_templates explicit, altfel
				// predefinire după rol, altfel NICIUNUL (fail-closed) — nu tot peste tot.
				() => {
					const allowed = allowed_templates_for_selected()
					const list = STATE.templates.filter((tpl) => allowed === null || allowed.has(tpl.slug))
					if (list.length === 0) {
						return t.div(
							{ class: "text-xs text-slate-400 dark:text-slate-500 italic mt-2" },
							"Niciun act configurat pentru acest strat.",
						)
					}
					return t.div(
						{ class: "flex flex-col gap-1.5 mt-2" },
						...list.map((tpl) =>
							t.a(
								{
									class:
										"flex items-center gap-1.5 rounded-lg bg-cyan-700 hover:bg-cyan-800 text-white " +
										"px-3 py-2 text-xs font-semibold transition",
									href: `#/documents?entity=${STATE.selected_feature_id}&template=${encodeURIComponent(tpl.slug)}`,
								},
								icon("file-plus"),
								` Generează: ${tpl.name || tpl.slug}`,
							),
						),
					)
				},
			),
			// History section
			t.div(
				{
					style: () => {
						const has = STATE.feature_detail && !STATE.feature_loading
						return `display:${has ? "block" : "none"}`
					},
				},
				t.div(
					{
						class:
							"text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1.5",
					},
					"Istoric",
				),
				t.div(
					{
						class: "flex items-center gap-1.5",
						style: () => `display:${STATE.feature_history_loading ? "flex" : "none"}`,
					},
					Spinner("w-3 h-3"),
					t.span(
						{ class: "text-xs text-slate-400 dark:text-slate-500 italic" },
						"Se încarcă istoricul...",
					),
				),
				t.div({ style: () => `display:${!STATE.feature_history_loading ? "block" : "none"}` }, () =>
					HistoryList(STATE.feature_history),
				),
			),
		),
	)
}

/** Centrul geometriei feature-ului selectat (pentru coordonata sesizării). */
function feature_center(): { lng: number; lat: number } | null {
	const g = STATE.feature_detail?.geometry
	if (!g || !("coordinates" in g)) {
		return null
	}
	const b = new maplibregl.LngLatBounds()
	extend_bounds(b, (g as { coordinates: unknown }).coordinates)
	if (b.isEmpty()) {
		return null
	}
	const c = b.getCenter()
	return { lng: c.lng, lat: c.lat }
}

/** Buton „Sesizare despre acest element" (public). Ascuns intern (caps.ticket=false) sau fără primărie. */
function FeatureTicketButton() {
	return t.button(
		{
			class:
				"w-full mb-3 bg-cyan-700 text-white rounded-lg py-2 text-xs font-semibold " +
				"hover:bg-cyan-800 transition cursor-pointer",
			style: () =>
				`display:${
					CTX.caps.ticket &&
					STATE.ticket_available &&
					STATE.feature_detail &&
					STATE.selected_feature_id
						? "block"
						: "none"
				}`,
			onclick: () => {
				const c = feature_center()
				// Stratul vine din feature-ul ÎNCĂRCAT (sursă unică), corect indiferent de calea de selecție.
				const layer = STATE.feature_detail?.layer_slug ?? ""
				open_ticket({
					feature_ref: layer ? `${layer}:${STATE.selected_feature_id}` : undefined,
					lng: c?.lng,
					lat: c?.lat,
					source: "pin",
				})
			},
		},
		"✎ Sesizare despre acest element",
	)
}

function fmt_cell(v: unknown): string {
	if (v == null) {
		return ""
	}
	return typeof v === "object" ? JSON.stringify(v) : String(v)
}

/** fmt_cell + cazul special `photos` (F9-T3): numărul de poze, nu JSON-ul tehnic cu chei de storage. */
function fmt_prop(key: string, v: unknown): string {
	if (key === PHOTOS_KEY) {
		const n = parse_photos(v).length
		return `📷 ${n} ${n === 1 ? "poză" : "poze"}`
	}
	return fmt_cell(v)
}

function TablePanel() {
	return t.div(
		{
			id: "table-panel",
			// Stă în coloana hărții (sub hartă) — nu acoperă sidebar-ul de straturi.
			class:
				"flex-shrink-0 bg-white dark:bg-slate-900 border-t-2 border-slate-200 dark:border-slate-700 flex-col z-10",
			style: () => `height:42dvh; display:${STATE.table_layer_slug ? "flex" : "none"};`,
		},
		// Panel header
		t.div(
			{
				class:
					"flex items-center gap-3 px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 flex-shrink-0",
			},
			t.div(
				{ class: "flex items-center gap-2 flex-1 min-w-0" },
				t.span(
					{ class: "font-semibold text-slate-800 dark:text-slate-200 text-sm truncate" },
					() => STATE.table_layer_name || "",
				),
				t.span(
					{ class: "text-xs text-slate-500 dark:text-slate-400 flex-shrink-0" },
					() =>
						`${STATE.table_features.length} entități${STATE.table_truncated ? ` (primele ${TABLE_LIMIT})` : ""}`,
				),
			),
			// Loading spinner in header
			t.div(
				{
					class: "flex items-center gap-1.5",
					style: () => `display:${STATE.table_loading ? "flex" : "none"}`,
				},
				Spinner("w-3.5 h-3.5"),
				t.span({ class: "text-xs text-slate-400 dark:text-slate-500 italic" }, "Se încarcă..."),
			),
			t.button(
				{
					class:
						"flex-shrink-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition",
					onclick: close_table,
				},
				"închide",
			),
		),
		// Panel body
		t.div(
			{ class: "flex-1 overflow-auto min-h-0" },
			MsgError(() => STATE.table_error),
			() => {
				if (STATE.table_loading) {
					return t.div()
				}
				const cols = STATE.table_columns
				const feats = STATE.table_features
				return t.table(
					{ class: "w-full border-collapse text-xs" },
					t.thead(
						t.tr(
							{ class: "sticky top-0 z-10 bg-slate-100 dark:bg-slate-800" },
							t.th(
								{
									class:
										"text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 font-medium whitespace-nowrap",
								},
								"",
							),
							...cols.map((c) =>
								t.th(
									{
										class:
											"text-left px-3 py-2 border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-semibold whitespace-nowrap",
									},
									c,
								),
							),
						),
					),
					t.tbody(
						...feats.map((f, i) =>
							t.tr(
								{
									class:
										i % 2 === 0
											? "bg-white dark:bg-slate-900 cursor-pointer hover:bg-cyan-50 dark:hover:bg-cyan-950"
											: "bg-slate-50 dark:bg-slate-950 cursor-pointer hover:bg-cyan-50 dark:hover:bg-cyan-950",
									onclick: () => go_to_feature(f.id),
								},
								t.td(
									{
										class:
											"px-2 py-1 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap",
									},
									t.button(
										{
											class:
												"rounded border border-cyan-200 bg-cyan-50 dark:bg-cyan-950 px-2 py-0.5 text-xs text-cyan-700 dark:text-cyan-300 font-medium hover:bg-cyan-100 transition",
											onclick: (e: Event) => {
												e.stopPropagation()
												go_to_feature(f.id)
											},
										},
										"go to",
									),
								),
								...cols.map((c) =>
									t.td(
										{
											class:
												"px-3 py-1 border-b border-slate-100 dark:border-slate-800 whitespace-nowrap max-w-[280px] overflow-hidden text-ellipsis text-slate-700 dark:text-slate-200",
											title: fmt_prop(c, f.properties?.[c]),
										},
										fmt_prop(c, f.properties?.[c]),
									),
								),
							),
						),
					),
				)
			},
		),
	)
}

/** Titlu intern: eticheta „PROIECT" + numele hărții curente (schimbarea se face din meniul de user). */
function InternalMapTitle() {
	return t.div(
		{ class: "mb-3" },
		t.div(
			{
				class:
					"block text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
			},
			"Proiect (hartă)",
		),
		t.div(
			{ class: "text-base font-bold text-slate-800 dark:text-slate-200 truncate" },
			() => (STATE.map_detail?.config.name as string) || STATE.selected_map_slug || "—",
		),
	)
}

/** Titlu de hartă publică (înlocuiește MapSelector-ul de workspace, care e doar intern). */
function PublicMapTitle() {
	return t.div(
		{ class: "mb-3" },
		t.div(
			{
				class:
					"block text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-1",
			},
			() => STATE.org_name || "Hartă publică",
		),
		t.div(
			{ class: "text-base font-bold text-slate-800 dark:text-slate-200 truncate" },
			() => (STATE.map_detail?.config.name as string) ?? "",
		),
	)
}

// ─────────────────────────────────────────────────────────────
// Căutare UNIFICATĂ (F13 redesign): un singur câmp → caută în ATRIBUTE (search_features) și/sau
// pe ADRESĂ (geocode), cu comutator + opțiuni avansate. Rezultatele grupate în dropdown.
// ─────────────────────────────────────────────────────────────
function UnifiedSearch() {
	const mode_btn = (m: "both" | "attr" | "addr", label: string) =>
		t.button(
			{
				class: () =>
					"px-2 py-0.5 rounded-md text-[11px] font-semibold transition " +
					(STATE.search_mode === m
						? "bg-cyan-600 text-white"
						: "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"),
				onclick: () => (STATE.search_mode = m),
			},
			label,
		)
	const run_all = () => {
		const q = STATE.search_q.trim()
		if (!q) {
			return
		}
		if (STATE.search_mode !== "addr") {
			void run_search()
		}
		if (STATE.search_mode !== "attr") {
			STATE.addr_q = q
			void run_addr_search()
		}
	}
	return t.div(
		{ class: "flex flex-col gap-2" },
		// Comutator de mod
		t.div(
			{ class: "flex items-center gap-1" },
			mode_btn("both", "Ambele"),
			mode_btn("attr", "Atribute"),
			mode_btn("addr", "Adresă"),
			t.div({ class: "flex-1" }),
			t.button(
				{
					class: () =>
						"text-[11px] font-semibold transition " +
						(STATE.search_advanced
							? "text-cyan-700 dark:text-cyan-300"
							: "text-slate-400 dark:text-slate-500 hover:text-cyan-700"),
					title: "Opțiuni avansate",
					onclick: () => {
						STATE.search_advanced = !STATE.search_advanced
						if (!STATE.search_advanced && STATE.identify_mode) {
							set_identify_mode(false) // butonul modului dispare → și modul
						}
					},
				},
				"Avansat ▾",
			),
		),
		// Câmpul unic
		t.div(
			{ class: "flex gap-1.5" },
			t.input({
				type: "text",
				placeholder: "adresă, nr. cadastral sau atribut…",
				class:
					"flex-1 min-w-0 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-800 dark:text-slate-200 outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 transition",
				value: () => STATE.search_q,
				oninput: (e: Event) => {
					STATE.search_q = (e.target as HTMLInputElement).value
					if (!STATE.search_q.trim()) {
						STATE.search_results = []
						clear_addr_search()
					}
				},
				onkeydown: (e: KeyboardEvent) => {
					if (e.key === "Enter") {
						run_all()
					}
				},
			}),
			t.button(
				{
					class:
						"flex-shrink-0 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white px-3 py-2 text-sm font-semibold transition",
					onclick: run_all,
				},
				icon("magnifying-glass"),
			),
		),
		// Opțiuni avansate (colapsabile): identify pe punct.
		() =>
			STATE.search_advanced
				? t.div(
						{
							class:
								"rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 p-2 flex flex-col gap-1.5",
						},
						t.button(
							{
								class: () =>
									"flex items-center gap-2 rounded-md px-2 py-1 text-xs font-medium transition " +
									(STATE.identify_mode
										? "bg-amber-500 text-white"
										: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-amber-400"),
								onclick: () => set_identify_mode(!STATE.identify_mode),
							},
							icon("crosshair"),
							"Interogare pe punct (click pe hartă)",
						),
						// 3.4: „pe o rază de X m" — se aplică interogării pe punct și căutării
						t.div(
							{ class: "flex items-center gap-1.5 flex-wrap text-xs" },
							t.span({ class: "text-slate-500 dark:text-slate-400" }, "Pe o rază de"),
							RadiusInput(),
							t.span({ class: "text-slate-500 dark:text-slate-400" }, "m"),
							...RADIUS_PRESETS.map((m) =>
								t.button(
									{
										class: () =>
											"px-1.5 py-0.5 rounded-md text-[11px] font-semibold transition " +
											(STATE.radius_m === m
												? "bg-amber-500 text-white"
												: "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-amber-400"),
										onclick: () => set_radius(STATE.radius_m === m ? 0 : m),
									},
									String(m),
								),
							),
						),
						t.div({ class: "text-[10px] text-slate-400 dark:text-slate-500 leading-snug" }, () =>
							STATE.radius_m > 0
								? `Interogarea pe punct și căutarea întorc obiectele aflate la cel mult ${STATE.radius_m} m ` +
									"(căutarea: în jurul centrului hărții; distanțele apar în rezultate). Maxim 5000 m."
								: "0 = doar obiectele care ating punctul (comportamentul obișnuit).",
						),
						CTX.caps.as_of ? AsOfInput() : t.span({ style: "display:none" }),
					)
				: t.span({ style: "display:none" }),
		// Rezultate ATRIBUTE
		() => {
			if (STATE.search_mode === "addr") {
				return t.span({ style: "display:none" })
			}
			if (STATE.search_loading) {
				return t.div({ class: "text-xs text-slate-400 italic" }, "Caut în atribute…")
			}
			if (STATE.search_error) {
				return t.div({ class: "text-xs text-red-500" }, STATE.search_error)
			}
			if (STATE.search_results.length === 0) {
				return t.span({ style: "display:none" })
			}
			return t.div(
				{ class: "flex flex-col" },
				t.div(
					{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mt-1" },
					"Atribute",
				),
				t.div(
					{
						class:
							"max-h-40 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800",
					},
					...STATE.search_results.map((m) =>
						t.button(
							{
								class:
									"block w-full text-left px-3 py-1.5 text-xs hover:bg-cyan-50 dark:hover:bg-cyan-950 transition cursor-pointer",
								onclick: () => select_search_match(m),
							},
							t.span(
								{ class: "font-mono text-slate-700 dark:text-slate-200" },
								`${m.key}: ${fmt_prop(m.key, m.value)}`,
							),
							t.span({ class: "text-slate-400 dark:text-slate-500 ml-2" }, m.layer_name),
							typeof m.distance_m === "number"
								? t.span(
										{ class: "ml-2 text-amber-700 dark:text-amber-300 font-semibold" },
										m.distance_m === 0 ? "aici" : `~${format_distance(m.distance_m)}`,
									)
								: "",
						),
					),
				),
			)
		},
		// Rezultate ADRESĂ
		() => {
			if (STATE.search_mode === "attr") {
				return t.span({ style: "display:none" })
			}
			if (STATE.addr_loading) {
				return t.div({ class: "text-xs text-slate-400 italic" }, "Caut adresa…")
			}
			if (STATE.addr_error) {
				return t.div({ class: "text-xs text-red-500" }, STATE.addr_error)
			}
			if (STATE.addr_results.length === 0) {
				return t.span({ style: "display:none" })
			}
			return t.div(
				{ class: "flex flex-col" },
				t.div(
					{ class: "text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mt-1" },
					"Adrese",
				),
				t.div(
					{
						class:
							"max-h-40 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800",
					},
					...STATE.addr_results.map((h) =>
						t.button(
							{
								class:
									"block w-full text-left px-3 py-1.5 text-xs hover:bg-cyan-50 dark:hover:bg-cyan-950 transition cursor-pointer",
								title: h.label,
								onclick: () => select_addr_hit(h),
							},
							t.div(
								{ class: "text-slate-700 dark:text-slate-200 truncate" },
								[h.street, h.number].filter(Boolean).join(" ") || h.label.split(",")[0],
							),
							t.div(
								{ class: "text-slate-400 dark:text-slate-500 truncate" },
								[h.locality, h.county].filter(Boolean).join(", "),
							),
						),
					),
				),
			)
		},
	)
}

// Definiția unui panel din rail: iconiță + titlu + conținut.
/**
 * Legendă pe hartă (F13): overlay flotant cu straturile VIZIBILE (simbol + nume). Toggle prin
 * `STATE.legend_open`. Se actualizează reactiv la vizibilitate/schimbare de hartă.
 */
function MapLegend() {
	return t.div(
		{
			class: () =>
				"absolute top-16 right-3 z-20 w-56 max-h-[60vh] overflow-y-auto rounded-xl border " +
				"border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 backdrop-blur shadow-lg p-2.5 " +
				(STATE.legend_open ? "block" : "hidden"),
		},
		t.div(
			{ class: "flex items-center justify-between mb-1.5" },
			t.span(
				{
					class: "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400",
				},
				"Legendă",
			),
			t.button(
				{
					class:
						"p-2 -m-2 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 text-xs",
					title: "Închide legenda",
					onclick: () => (STATE.legend_open = false),
				},
				"✕",
			),
		),
		() => {
			const d = STATE.map_detail
			const visible = d ? d.layers.filter((l) => STATE.layer_visible[l.slug] !== false) : []
			const rows: HTMLElement[] = visible.flatMap((l, i) => {
				const idx = d!.layers.indexOf(l)
				const color = layer_color(l, idx < 0 ? i : idx)
				const role = l.role && ROLE_META[l.role] ? l.role : ""
				const es_l = effective_style(l, idx < 0 ? i : idx)
				const head = t.div(
					{ class: "flex items-center gap-2" },
					role
						? RoleSwatch(role)
						: LayerSwatch(
								color,
								es_l.stroke,
								l.geometry_type,
								l.icon_url,
								es_l.opacity === 0 ? 0 : 0.7,
								es_l.hatch,
							),
					t.span(
						{ class: "text-xs text-slate-700 dark:text-slate-200 truncate", title: l.name },
						l.name,
					),
				)
				// 09.10: stilul pe valoare — câte o intrare per categorie, sub strat (legenda planșei);
				// 12.10: categoriile ASCUNSE (debifate de proiectant) nu se desenează, deci lipsesc din legendă
				const cats = es_l.categories
					? [
							...es_l.categories.values
								.filter((c) => !is_hidden_symbol(c))
								.map((c) =>
									t.div(
										{ class: "flex items-center gap-2 pl-4" },
										LayerSwatch(c.color, c.stroke, l.geometry_type, "", c.opacity, c.hatch),
										t.span(
											{
												class: "text-[11px] text-slate-600 dark:text-slate-300 truncate",
												title: c.label,
											},
											c.label,
										),
									),
								),
							...(es_l.categories.has_else && !is_hidden_symbol(es_l.categories.else)
								? [
										t.div(
											{ class: "flex items-center gap-2 pl-4" },
											LayerSwatch(
												es_l.categories.else.color,
												es_l.categories.else.stroke,
												l.geometry_type,
												"",
												es_l.categories.else.opacity,
												es_l.categories.else.hatch,
											),
											t.span(
												{ class: "text-[11px] text-slate-400 dark:text-slate-500 truncate" },
												"altele",
											),
										),
									]
								: []),
						]
					: []
				return [head, ...cats]
			})
			// 2.2: rândurile derivate vizibile — „<strat> — <filtru>", culoarea derivată
			for (const { layer, idx, fi, f } of visible_filter_rows()) {
				const color = filter_color(layer_color(layer, idx), fi)
				rows.push(
					t.div(
						{ class: "flex items-center gap-2" },
						LayerSwatch(color, shade_color(color, 0.7), layer.geometry_type),
						t.span(
							{
								class: "text-xs text-slate-700 dark:text-slate-200 truncate",
								title: saved_filter_label(f),
							},
							`${layer.name} — ${f.name || f.slug}`,
						),
					),
				)
			}
			// 1.2: zonificarea actuală — PUG (decupat) + PUZ (derogare)
			if (STATE.zonificare_visible) {
				rows.push(
					t.div(
						{ class: "flex items-center gap-2" },
						LayerSwatch("#94a3b8", ZONIFICARE_PUG_LINE, "Polygon"),
						t.span(
							{ class: "text-xs text-slate-700 dark:text-slate-200 truncate" },
							"Zonificare actuală — PUG (decupat)",
						),
					),
					t.div(
						{ class: "flex items-center gap-2" },
						RoleSwatch("derogare"),
						t.span(
							{ class: "text-xs text-slate-700 dark:text-slate-200 truncate" },
							`Zonificare actuală — PUZ/derogare (${Math.max(0, STATE.zonificare_derogari)})`,
						),
					),
				)
			}
			// Stratul virtual „acte" (N3): aceleași două intrări ca pe pagina tipărită — legenda de pe
			// ecran nu are voie să mintă față de hârtie.
			if (STATE.acte_visible) {
				for (const [color, label] of ACTE_LEGEND) {
					rows.push(
						t.div(
							{ class: "flex items-center gap-2" },
							LayerSwatch(color, "#ffffff", "Point"),
							t.span({ class: "text-xs text-slate-700 dark:text-slate-200 truncate" }, label),
						),
					)
				}
			}
			if (rows.length === 0) {
				return t.div(
					{ class: "text-xs text-slate-400 dark:text-slate-500 italic" },
					"Niciun strat vizibil.",
				)
			}
			return t.div({ class: "flex flex-col gap-1" }, ...rows)
		},
	)
}

/**
 * TIPĂRIRE / SALVARE PDF (REQ-FMT-005): deschide un tab nou cu pagina de tipărire (lib/print —
 * A4 peisaj: antet, imaginea vederii curente, legenda straturilor vizibile, scară + nord, atribuire).
 * Fereastra se deschide SINCRON în handler-ul de click (altfel o blochează browserul), apoi se umple
 * după captura canvas-ului. Captura se ia în evenimentul `render` după `triggerRepaint` — singurul
 * moment în care buffer-ul WebGL e garantat plin fără `preserveDrawingBuffer` (care costă pe mobil).
 * Read-only, client-only: nimic pe server, deci nici drept contractual separat; în embed-uri urmează
 * instrumentul `legend`.
 */
async function print_current_view(): Promise<void> {
	if (!MAP) {
		return
	}
	const win = window.open("", "_blank")
	if (!win) {
		alert("Browserul a blocat fereastra de tipărire. Permite ferestrele pop-up pentru acest site.")
		return
	}
	win.document.write(
		"<!doctype html><title>Se pregătește harta…</title><p style='font-family:sans-serif;padding:24px'>Se pregătește harta pentru tipărire…</p>",
	)
	win.document.close()
	const map = MAP
	const image_data_url = await new Promise<string>((resolve) => {
		// fallback: dacă harta e demontată înainte de cadru, `render` nu mai vine — nu lăsăm tabul agățat
		const timer = setTimeout(() => resolve(map.getCanvas().toDataURL("image/png")), 1500)
		map.once("render", () => {
			clearTimeout(timer)
			resolve(map.getCanvas().toDataURL("image/png"))
		})
		map.triggerRepaint()
	})
	if (win.closed) {
		return // utilizatorul a închis tabul cât timp se făcea captura
	}
	const canvas = map.getCanvas()
	// scara: metri reali pe pixel CSS, măsurați GEODEZIC pe orizontala din centrul vederii
	const cw = canvas.clientWidth || 1
	const ch = canvas.clientHeight || 1
	const a = map.unproject([cw / 2 - 50, ch / 2])
	const b = map.unproject([cw / 2 + 50, ch / 2])
	const meters_per_px = distance_m([a.lng, a.lat], [b.lng, b.lat]) / 100
	const d = STATE.map_detail
	const visible = d ? d.layers.filter((l) => STATE.layer_visible[l.slug] !== false) : []
	// Fiecare strat urmat de categoriile lui, INDENTATE (`sub`) — aceeași structură ca legenda de pe ecran
	// (03.11, testarea pe staging: tipărirea punea toate straturile întâi și categoriile la coadă).
	const groups = await Promise.all(
		visible.map(async (l): Promise<PrintLegendItem[]> => {
			const idx = d ? d.layers.indexOf(l) : 0
			const color = layer_color(l, idx < 0 ? 0 : idx)
			const role = l.role && ROLE_META[l.role] ? (l.role as "utr" | "derogare") : ""
			const kind = legend_kind(l.geometry_type)
			// 1.4: iconița stratului — încorporată ca data URL (documentul rămâne self-contained);
			// la eșec, URL-ul absolut (tabul e about:blank — o cale relativă nu s-ar rezolva).
			if (!role && kind === "point" && l.icon_url) {
				const data = await icon_data_url(l.icon_url)
				return [{ name: l.name, svg: swatch_img(data || `${location.origin}${l.icon_url}`, color) }]
			}
			const es_p = effective_style(l, idx < 0 ? 0 : idx)
			const head: PrintLegendItem = {
				name: l.name,
				svg: role
					? swatch_svg(role)
					: swatch_svg(kind, color, es_p.stroke, es_p.opacity === 0 ? 0 : 0.7, es_p.hatch),
			}
			if (!es_p.categories || role) {
				return [head]
			}
			// 09.10: stilul pe valoare — câte o intrare per categorie, cu simbolul ei (ca pe ecran);
			// 12.10: categoriile debifate de proiectant nu se desenează, deci nu apar pe hârtie
			const cats: PrintLegendItem[] = es_p.categories.values
				.filter((c) => !is_hidden_symbol(c))
				.map((c) => ({
					name: c.label,
					svg: swatch_svg(kind, c.color, c.stroke, c.opacity, c.hatch),
					sub: true,
				}))
			if (es_p.categories.has_else && !is_hidden_symbol(es_p.categories.else)) {
				const e = es_p.categories.else
				cats.push({
					name: "altele",
					svg: swatch_svg(kind, e.color, e.stroke, e.opacity, e.hatch),
					sub: true,
				})
			}
			return [head, ...cats]
		}),
	)
	const legend: PrintLegendItem[] = groups.flat()
	// 2.2: rândurile derivate vizibile („<strat> — <filtru>"), cu culoarea derivată din a părintelui.
	for (const { layer, idx, fi, f } of visible_filter_rows()) {
		const color = filter_color(layer_color(layer, idx), fi)
		legend.push({
			name: `${layer.name} — ${f.name || f.slug}`,
			svg: swatch_svg(legend_kind(layer.geometry_type), color, shade_color(color, 0.7)),
		})
	}
	// 1.2: zonificarea actuală (PUG decupat + PUZ/derogare) — e în captură când e aprinsă.
	if (STATE.zonificare_visible) {
		legend.push({
			name: "Zonificare actuală — PUG (decupat)",
			svg: swatch_svg("polygon", "#94a3b8", ZONIFICARE_PUG_LINE),
		})
		legend.push({ name: "Zonificare actuală — PUZ/derogare", svg: swatch_svg("derogare") })
	}
	// Ce e ÎN captură dar nu e strat al hărții: stratul virtual „Acte" (F5-T9 → N3, două intrări:
	// emise/anulate — culoarea e starea) și schițele de măsurare/desen. Markerele DOM (adresă, pin de
	// sesizare) NU sunt pe canvas → nu apar (documentat).
	if (STATE.acte_visible) {
		for (const [color, label] of ACTE_LEGEND) {
			legend.push({ name: label, svg: swatch_svg("point", color, "#ffffff") }) // ca LayerSwatch de pe ecran
		}
	}
	if (drawn_features.length > 0 || measure_points.length > 0) {
		legend.push({ name: "Desen / măsurători", svg: swatch_svg("line", "#f59e0b") })
	}
	// Straturile vizibile pe care serverul le-a semnalat ca parțiale — ajung pe hârtie ca avertisment.
	const truncated_layers = visible
		.filter((l) => STATE.layer_truncated[l.slug] === true)
		.map((l) => l.name)
	const html = build_print_html({
		title: "",
		map_name: String((d?.config as { name?: string } | undefined)?.name ?? d?.slug ?? "Hartă"),
		// intern: numele primăriei din membership-ul hărții (STATE.org_name e setat doar pe public)
		org_name:
			STATE.org_name ||
			session.val.memberships.find((mb) => mb.org_slug === d?.org_slug)?.org_name ||
			"",
		// intern: stema e în atributele hărții (public: STATE.org_stema din PublicMapView)
		stema_url: STATE.org_stema || String(d?.attributes?.stema_url ?? ""),
		date_label: new Date().toLocaleString("ro-RO", { dateStyle: "long", timeStyle: "short" }),
		image_data_url,
		image_aspect: cw / ch,
		image_width_px: cw,
		legend,
		scale: nice_scale(meters_per_px, 160),
		bearing_deg: map.getBearing(),
		attribution: basemap_attribution(), // 1.3: fundalul ACTIV (configurat sau fix)
		mode_3d: STATE.mode_3d,
		truncated_layers,
	})
	win.document.open()
	win.document.write(html)
	win.document.close()
}

function toggle_sidebar_collapsed(): void {
	STATE.sidebar_collapsed = !STATE.sidebar_collapsed
	if (typeof localStorage !== "undefined") {
		localStorage.setItem("urbanism_sidebar_collapsed", STATE.sidebar_collapsed ? "1" : "0")
	}
}

/**
 * Iconiță de strat pentru rail-ul COLLAPSED — swatch + click=toggle vizibilitate + tooltip nume.
 * (În expanded se folosește LayerItem, cu nume + count + controale.)
 */
function LayerIconRail(layer: LayerSummary, idx: number) {
	const slug = layer.slug
	const color = layer_color(layer, idx)
	const stroke = effective_style(layer, idx).stroke
	const map_slug = STATE.selected_map_slug
	const role = layer.role && ROLE_META[layer.role] ? layer.role : ""
	return t.button(
		{
			class: () =>
				"flex items-center justify-center w-9 h-9 rounded-lg border transition flex-shrink-0 " +
				(STATE.layer_visible[slug] !== false
					? "border-cyan-300 dark:border-cyan-700 bg-cyan-50/60 dark:bg-cyan-950/40 hover:bg-cyan-100 dark:hover:bg-cyan-900"
					: "border-transparent opacity-35 hover:opacity-70"),
			title: layer.name + (role ? ` · ${ROLE_META[role].label}` : ""),
			onclick: () => {
				explicit_visibility.add(slug)
				STATE.layer_visible[slug] = STATE.layer_visible[slug] === false
				sync_layer_visibility(map_slug)
			},
		},
		role ? RoleSwatch(role) : LayerSwatch(color, stroke, layer.geometry_type, layer.icon_url),
	)
}

/**
 * Sidebar = DRAWER de straturi colapsabil (pattern daisyUI icon-drawer, F13):
 *   - COLLAPSED (~56px): straturile ca ICONIȚE (click=toggle vizibilitate, tooltip=nume) + buton de extindere.
 *   - EXPANDED (~320px): titlu + căutare unificată + lista completă de straturi cu nume/count/controale.
 * Legenda straturilor vizibile se afișează separat, ca overlay peste hartă (MapLegend).
 */
function Sidebar() {
	// ── COLLAPSED: coloană de iconițe de strat ──
	const collapsed = t.div(
		{
			class: () =>
				"flex-col items-center gap-1.5 py-3 w-14 bg-slate-100 dark:bg-slate-950 " +
				"border-r border-slate-200 dark:border-slate-700 overflow-y-auto " +
				(STATE.sidebar_collapsed ? "flex" : "hidden"),
		},
		t.button(
			{
				class:
					"w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 dark:text-slate-400 " +
					"hover:bg-slate-200 dark:hover:bg-slate-800 transition mb-1",
				title: "Extinde panoul de straturi",
				onclick: toggle_sidebar_collapsed,
			},
			icon("caret-right"),
		),
		t.div({ class: "flex flex-col items-center gap-1.5" }, () => {
			const d = STATE.map_detail
			return d
				? t.div({ class: "flex flex-col gap-1.5" }, ...d.layers.map((l, i) => LayerIconRail(l, i)))
				: t.div()
		}),
	)

	// ── EXPANDED: panoul complet ──
	const search_block = CTX.caps.search
		? t.div(
				{ class: "mb-3" },
				t.button(
					{
						class: () =>
							"flex items-center gap-2 w-full rounded-lg border px-3 py-2 text-sm transition " +
							(STATE.search_open
								? "border-cyan-400 text-cyan-700 dark:text-cyan-300 bg-cyan-50/60 dark:bg-cyan-950/40"
								: "border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-cyan-400"),
						onclick: () => {
							STATE.search_open = !STATE.search_open
							if (!STATE.search_open && STATE.identify_mode) {
								set_identify_mode(false) // panoul se închide → modul de interogare se închide
							}
						},
					},
					icon("magnifying-glass"),
					t.span("Caută adresă / atribut"),
				),
				() =>
					STATE.search_open
						? t.div({ class: "mt-2" }, UnifiedSearch())
						: t.span({ style: "display:none" }),
			)
		: t.span({ style: "display:none" })

	const expanded = t.div(
		{
			class: () =>
				"w-[320px] max-w-[85vw] bg-slate-50 dark:bg-slate-950 border-r border-slate-200 " +
				"dark:border-slate-700 flex-col overflow-hidden " +
				(STATE.sidebar_collapsed ? "hidden" : "flex"),
		},
		// Antet: titlul hărții + buton de colapsare.
		t.div(
			{
				class:
					"flex-shrink-0 px-3 pt-3 pb-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 " +
					"flex items-start justify-between gap-2",
			},
			t.div(
				{ class: "flex-1 min-w-0" },
				CTX.caps.workspace ? InternalMapTitle() : PublicMapTitle(),
			),
			t.button(
				{
					class:
						"flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 " +
						"dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition",
					title: "Restrânge la iconițe",
					onclick: toggle_sidebar_collapsed,
				},
				icon("caret-left"),
			),
		),
		// Corp: căutare + identify + straturi.
		t.div(
			{ class: "flex-1 overflow-y-auto px-3 pt-3 pb-4" },
			search_block,
			IdentifyPanel(),
			PublicDocsSection(),
			LayerList(),
		),
	)

	return t.div(
		{
			id: "sidebar",
			class: "absolute inset-y-0 left-0 md:static flex z-30",
			style: () => `display:${STATE.sidebar_open ? "flex" : "none"}`,
		},
		collapsed,
		expanded,
	)
}

// ─────────────────────────────────────────────────────────────
// Documentația publică (F6, REQ-PEX-001/002): planșele PUG + regulamentul scanat, bifate PUBLICE
// de primărie în #/documents → listate în sidebar-ul /embed cu descărcare anonimă prin token.
// ─────────────────────────────────────────────────────────────

interface PublicDoc {
	id: string
	name: string
	filename: string
	size: number
	mime: string
	created_at: string
}

function fmt_doc_size(bytes: number): string {
	if (bytes >= 1024 * 1024) {
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
	}
	return bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`
}

/** Secțiunea „Documentația PUG" din sidebar — card colapsabil DEASUPRA straturilor (un rând
 *  când e închis, cu numărul de fișiere). Nod STABIL, ascuns când nu există documente (F8). */
function PublicDocsSection() {
	const body = () =>
		t.div(
			{ class: "flex flex-col gap-0.5 px-1.5 pb-1.5" },
			...STATE.public_docs.map((d) =>
				t.a(
					{
						class:
							"flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-slate-700 dark:text-slate-200 " +
							"hover:bg-cyan-50 dark:hover:bg-cyan-950 hover:text-cyan-800 transition",
						href: `/api/public/documents/${encodeURIComponent(d.id)}/file?k=${encodeURIComponent(CTX.token)}`,
						title: `${d.filename} · ${fmt_doc_size(d.size)}`,
					},
					icon(
						d.mime === "application/pdf" ? "file-pdf" : "file",
						"text-slate-400 dark:text-slate-500",
					),
					t.span({ class: "truncate flex-1" }, d.name || d.filename),
					t.span(
						{ class: "text-[10px] text-slate-400 dark:text-slate-500 flex-shrink-0" },
						fmt_doc_size(d.size),
					),
				),
			),
		)
	return t.div(
		{
			class:
				"mb-3 rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50/40 dark:bg-cyan-950/30 overflow-hidden",
			style: () => `display:${CTX.is_public && STATE.public_docs.length > 0 ? "block" : "none"}`,
		},
		t.details(
			{ open: true }, // deschis implicit — descoperibilitatea a fost exact problema
			t.summary(
				{
					class:
						"cursor-pointer select-none px-3 py-2 text-xs font-bold text-cyan-800 dark:text-cyan-300 " +
						"flex items-center gap-1.5 hover:bg-cyan-50 dark:hover:bg-cyan-950 transition",
				},
				icon("files"),
				"Documentația PUG",
				t.span({ class: "font-normal text-cyan-600" }, () =>
					STATE.public_docs.length > 0 ? `(${STATE.public_docs.length})` : "",
				),
			),
			body,
		),
	)
}

// ─────────────────────────────────────────────────────────────
// Bootstrap: read URL query params, init map, load data
// ─────────────────────────────────────────────────────────────

function get_query_params(): { map: string; feature: string; lng: string; lat: string } {
	// Query din hash (#/map?map=…&feature=…&lng=&lat=), nu din location.search.
	const p = get_hash_query_params()
	return {
		map: p.get("map") ?? "",
		feature: p.get("feature") ?? "",
		lng: p.get("lng") ?? "",
		lat: p.get("lat") ?? "",
	}
}

// Componentă de rută SPA: întoarce arborele paginii. Shell-ul îl atașează în #app
/** Empty-state pentru `#/map` fără spațiu de lucru: mesaj pe tipul de utilizator, nu harta
 *  goală a României. Conținutul e o funcție REACTIVĂ (sesiunea se hidratează async la load
 *  rece — un staff ar vedea altfel mesajul de cetățean); nu întoarce niciodată null (F8). */
function MapEmptyState(): HTMLElement {
	const cta =
		"inline-block mt-5 px-5 py-2.5 rounded-lg bg-cyan-700 text-white font-semibold hover:bg-cyan-800 transition"
	return t.div(
		{
			class:
				"min-h-[calc(100dvh-3rem)] flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4",
		},
		t.div({ class: "max-w-md text-center" }, () => {
			if (!session_loaded.val) {
				return t.div({ class: "text-slate-400 dark:text-slate-500 animate-pulse" }, "Se încarcă…")
			}
			const s = session.val
			const staff =
				s.authenticated &&
				(s.user?.platform_role === "super" ||
					s.user?.platform_role === "support" ||
					s.memberships.length > 0)
			if (staff) {
				return t.div(
					t.div({ class: "text-4xl" }, "🗺️"),
					t.h1(
						{ class: "mt-4 text-xl font-bold text-slate-800 dark:text-slate-200" },
						"Alege un spațiu de lucru",
					),
					t.p(
						{ class: "mt-2 text-slate-600 dark:text-slate-300" },
						"Harta se deschide pe spațiul de lucru al primăriei tale — alege-l din lista de mai jos sau din meniul de cont.",
					),
					t.a({ href: "#/harti", class: cta }, "Spațiile mele de lucru"),
				)
			}
			if (s.authenticated) {
				return t.div(
					t.div({ class: "text-4xl" }, "🗺️"),
					t.h1(
						{ class: "mt-4 text-xl font-bold text-slate-800 dark:text-slate-200" },
						"Harta publică a primăriei tale",
					),
					t.p(
						{ class: "mt-2 text-slate-600 dark:text-slate-300" },
						"Hărțile publicate se deschid prin linkul distribuit de fiecare primărie (pe site-ul ei sau în detaliul unei solicitări).",
					),
					t.a({ href: "#/solicitari", class: cta }, "Solicitările mele"),
				)
			}
			return t.div(
				t.div({ class: "text-4xl" }, "🗺️"),
				t.h1(
					{ class: "mt-4 text-xl font-bold text-slate-800 dark:text-slate-200" },
					"Harta se deschide printr-un spațiu de lucru",
				),
				t.p(
					{ class: "mt-2 text-slate-600 dark:text-slate-300" },
					"Funcționarii se autentifică pentru harta primăriei lor; hărțile publice se deschid prin linkul distribuit de fiecare primărie.",
				),
				t.a({ href: "#/autentificare", class: cta }, "Autentificare"),
			)
		}),
	)
}

// DUPĂ ce funcția returnează, deci MapLibre se inițializează într-un requestAnimationFrame
// (container atașat + dimensionat). La re-montare distrugem instanța anterioară.
export function MapPage(): HTMLElement {
	// Fără spațiu de lucru selectat (doar intern), pagina NU mai arată harta goală a României
	// (fundătură derutantă — constatare 25.08): empty-state diferențiat pe tipul de utilizator.
	// Re-montarea la schimbarea workspace-ului / a sesiunii readuce harta normală.
	if (!CTX.is_public && !workspace.val.slug) {
		return MapEmptyState()
	}
	if (MAP) {
		MAP.remove()
		MAP = null
		// Instanța veche a murit cu tot cu surse/layere/handler-e — altfel
		// add_layer_to_map ar sări peste re-adăugare la re-montare (hartă goală).
		added_layers.clear()
		added_sources.clear()
		bound_click_layers.clear()
		geojson_cache.clear()
		// Harta nouă pornește plată — vederea 3D e per sesiune de hartă.
		STATE.mode_3d = false
		STATE.terrain_on = false // sursa DEM a murit cu instanța veche de MAP
		STATE.sun_active = false // lumina moare cu instanța veche; data/ora alese se păstrează
		STATE.sun_readout = ""
		utr_ctx_3d = null // contextul UTR e al hărții vechi (alt strat, alt RLU)
		// …și fără măsurători: `MAP.remove()` a distrus straturile, dar starea e la nivel de modul —
		// fără reset, harta nouă ar afișa rezultatul vechi fără desenul care-l justifică.
		measure_points = []
		drawn_features = []
		STATE.measure_mode = ""
		STATE.measure_readout = ""
		// …și fără coordonate/rază: cursorul și cercul erau pe harta veche (sursa a murit cu MAP).
		STATE.coord_lng = Number.NaN
		STATE.coord_lat = Number.NaN
		STATE.identify_result = null
		// …și fără regim: rezultatul e al hărții vechi (alt teritoriu, alt RLU).
		STATE.regim_mode = false
		STATE.regim_loading = false
		STATE.regim = null
		STATE.regim_error = ""
		// …și fără overlay-ul de acte: sursa lui a murit cu instanța MapLibre.
		STATE.acte_visible = false
		// …și fără zonificarea actuală / rândurile derivate: ale hărții vechi (sursele au murit cu MAP).
		STATE.zonificare_visible = false
		STATE.zonificare_loading = false
		STATE.zonificare_error = ""
		STATE.zonificare_derogari = -1
		STATE.zonificare_utr = ""
		STATE.filter_visible = {}
		// …și fără markerul de adresă (a murit cu harta veche; referința ar rămâne stale).
		addr_marker = null
		STATE.addr_q = ""
		STATE.addr_results = []
		STATE.addr_error = ""
		// …și fără filtrul de straturi: e al listei hărții vechi (altă hartă = alte nume).
		STATE.layer_q = ""
		// …și fără editare (F13-T4): instanța Terra Draw a murit cu MAP.remove(); starea de mod
		// e la nivel de modul — fără reset, harta nouă ar porni „în editare" fără editor.
		DRAW = null
		edit_pending_geometry = null
		edit_form = {}
		edit_geom_rewrap = ""
		STATE.edit_mode = false
		STATE.edit_layer_slug = "" // altă hartă = alte straturi
		layer_schema_cache.clear() // schemele erau ale hărții vechi (cheiat pe hartă, dar nu la infinit)
		feature_edit_schema = {}
		STATE.edit_draw_active = ""
		STATE.edit_schema = []
		STATE.edit_pending = false
		STATE.edit_geom_feature = ""
		STATE.edit_geom_dirty = false
		STATE.edit_saving = false
		STATE.edit_error = ""
		STATE.edit_notice = ""
		STATE.feature_edit_open = false
		STATE.feature_edit_error = ""
	}

	const toggle_btn_cls =
		"flex items-center gap-1 rounded-lg bg-white/95 dark:bg-slate-900/95 shadow border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 " +
		// shrink-0: în banda cu scroll orizontal de pe mobil butoanele nu au voie să se strângă;
		// max-md:py-2 = țintă touch mai înaltă doar pe telefon.
		"shrink-0 max-md:py-2 " +
		"text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition cursor-pointer"
	const basemap_btn = (kind: "harta" | "satelit", icon_name: string, label: string) =>
		t.button(
			{
				// F7-T3: pe un embed cu `basemap` oprit, comutarea fundalului dispare (class, nod stabil).
				// 1.3: când harta are fundaluri CONFIGURATE, perechea fixă dispare (le înlocuiesc).
				class: () =>
					toggle_btn_cls +
					(STATE.basemap === kind ? " !bg-cyan-700 !text-white !border-cyan-700" : "") +
					(CTX.caps.basemap && custom_basemaps().length === 0 ? "" : " hidden"),
				// title = numele accesibil pe mobil, unde eticheta text e ascunsă (hidden sm:inline)
				title: `Fundal: ${label}`,
				onclick: () => set_basemap(kind),
			},
			icon(icon_name),
			t.span({ class: "hidden sm:inline" }, label),
		)
	// Buton de măsurare: re-apăsarea pe modul activ îl oprește (toggle).
	const measure_btn = (
		mode: "distance" | "area" | "draw",
		icon_name: string,
		label: string,
		title: string,
	) =>
		t.button(
			{
				// F7-T3: pe un embed cu `measure` oprit, trusa de măsurare/desen dispare (class, nod stabil).
				class: () =>
					toggle_btn_cls +
					(STATE.measure_mode === mode ? " !bg-pink-600 !text-white !border-pink-600" : "") +
					(CTX.caps.measure ? "" : " hidden"),
				title,
				onclick: () => set_measure_mode(STATE.measure_mode === mode ? "" : mode),
			},
			icon(icon_name),
			t.span({ class: "hidden sm:inline" }, label),
		)
	// Controale plutitoare: stânga-sus — sidebar + basemap (vizibile și pe mobil).
	// Mobil (<md): UN singur rând cu scroll orizontal (altfel cele ~13 butoane se împachetau pe
	// 3-4 rânduri peste panourile de la top-14); right-12 lasă loc NavigationControl-ului din
	// dreapta-sus. Desktop (md+): comportamentul istoric (wrap, fără limită la dreapta).
	// Banda are lățimea CONȚINUTULUI (shrink-to-fit + max-w), nu full-width: zona goală de după
	// ultimul buton nici nu face parte din bandă → tap-urile trec natural la hartă (constatarea
	// 24.08, fără hack-ul pointer-events-none — care pe iOS bloca scroll-ul cu degetul: Safari
	// nu derulează un container scos din hit-test, iar butonul de editare devenea de neatins,
	// bug 26.08). Când butoanele depășesc max-w, banda E plină → overflow-x-auto preia.
	// max-w lasă loc NavigationControl-ului din dreapta-sus la TOATE lățimile — un telefon în
	// LANDSCAPE (~932px) e deja `md:` și rândul cu etichete ajungea sub butonul de zoom (26.08).
	const map_overlay = t.div(
		{
			class:
				"absolute top-3 left-3 z-20 flex items-center gap-2 " +
				"max-w-[calc(100%-3.75rem)] " +
				"overflow-x-auto md:overflow-visible md:flex-wrap [scrollbar-width:none]",
		},
		t.button(
			{
				class: toggle_btn_cls,
				title: "Arată/ascunde panoul de straturi",
				onclick: toggle_sidebar_open,
			},
			() => icon(STATE.sidebar_open ? "sidebar-simple" : "list"),
			t.span({ class: "hidden sm:inline" }, "Straturi"),
		),
		basemap_btn("harta", "map-trifold", "Hartă"),
		basemap_btn("satelit", "globe-hemisphere-west", "Satelit"),
		// 1.3: butoanele fundalurilor configurate (ordinea din listă; primul = implicit) — re-randate
		// când se schimbă harta; `contents` ca butoanele să stea în același rând flex.
		t.div({ class: "contents" }, () => {
			const list = custom_basemaps()
			if (list.length === 0 || !CTX.caps.basemap) {
				return t.span({ class: "hidden" })
			}
			return t.div(
				{ class: "contents" },
				...list.map((b, i) =>
					t.button(
						{
							class: () =>
								toggle_btn_cls +
								(STATE.basemap === `bm:${i}` ? " !bg-cyan-700 !text-white !border-cyan-700" : ""),
							title: `Fundal: ${b.name}`,
							onclick: () => set_basemap(`bm:${i}`),
						},
						icon(b.type === "wms" ? "stack" : "map-trifold"),
						t.span({ class: "hidden sm:inline" }, b.name),
					),
				),
			)
		}),
		// Legendă pe hartă (F13) — toggle overlay-ul cu straturile vizibile.
		// F7-T3: ascunsă pe embed-urile cu `legend` oprit (class, nod stabil).
		t.button(
			{
				class: () =>
					toggle_btn_cls +
					(STATE.legend_open ? " !bg-cyan-700 !text-white !border-cyan-700" : "") +
					(CTX.caps.legend ? "" : " hidden"),
				title: "Legendă: straturile vizibile cu simbolurile lor",
				onclick: () => (STATE.legend_open = !STATE.legend_open),
			},
			icon("list-bullets"),
			t.span({ class: "hidden sm:inline" }, "Legendă"),
		),
		// Tipărire / salvare PDF (REQ-FMT-005) — urmează instrumentul `legend` în embed-uri.
		t.button(
			{
				class: () => toggle_btn_cls + (CTX.caps.legend ? "" : " hidden"),
				title: "Tipărește sau salvează ca PDF vederea curentă (cu legendă și scară)",
				onclick: () => void print_current_view(),
			},
			icon("printer"),
			t.span({ class: "hidden sm:inline" }, "Tipărește"),
		),
		// Gating pe contract: fără `model_3d` pe org-ul hărții, toată trusa 3D dispare
		// (ascunsă prin class — nod stabil, reactiv la schimbarea workspace-ului).
		t.button(
			{
				class: () =>
					toggle_btn_cls +
					(STATE.mode_3d ? " !bg-cyan-700 !text-white !border-cyan-700" : "") +
					(map_3d_available() ? "" : " hidden"),
				title:
					"Vedere 3D: extrudează clădirile după înălțime/regim (implicit ~parter unde atributul lipsește)",
				onclick: () => set_mode_3d(!STATE.mode_3d),
			},
			icon("cube"),
			t.span({ class: "hidden sm:inline" }, "3D"),
		),
		// Teren 3D (F10-T3): vizibil DOAR în modul 3D și doar dacă instanța are sursă DEM
		// (TERRAIN_TILES_URL). Nod stabil, ascuns prin class (nu detașa noduri reactive).
		t.button(
			{
				class: () =>
					toggle_btn_cls +
					(STATE.terrain_on ? " !bg-cyan-700 !text-white !border-cyan-700" : "") +
					(STATE.mode_3d && map_3d_available() && STATE.map_detail?.terrain?.url ? "" : " hidden"),
				title: "Relief: drapează harta pe modelul digital al terenului (sursa DEM a instanței)",
				onclick: () => set_terrain(!STATE.terrain_on),
			},
			icon("mountains"),
			t.span({ class: "hidden sm:inline" }, "Teren"),
		),
		// Însorire (F10-T4): vizibil doar în modul 3D — direcția soarelui mișcă umbrirea fațadelor.
		t.button(
			{
				class: () =>
					toggle_btn_cls +
					(STATE.sun_active ? " !bg-amber-600 !text-white !border-amber-600" : "") +
					(STATE.mode_3d && map_3d_available() ? "" : " hidden"),
				title:
					"Însorire: alege data și ora — direcția și înălțimea soarelui schimbă umbrirea fațadelor (simulare vizuală)",
				onclick: () => set_sun(!STATE.sun_active),
			},
			icon("sun"),
			t.span({ class: "hidden sm:inline" }, "Soare"),
		),
		// Măsurare (REQ-PEX-004): disponibilă și intern, și public — e read-only, nu scrie nimic.
		measure_btn(
			"distance",
			"ruler",
			"Distanță",
			"Măsoară o distanță: click pe hartă pentru fiecare punct, dublu-click = gata",
		),
		measure_btn(
			"area",
			"polygon",
			"Suprafață",
			"Măsoară o suprafață: click pe hartă pentru fiecare colț, dublu-click = gata",
		),
		// Desen liber temporar (REQ-PEX-004, „funcții de desenare") — client-only, ca măsurarea.
		measure_btn(
			"draw",
			"pencil-simple-line",
			"Desen",
			"Desen temporar pe hartă: click = punct, dublu-click = forma gata (rămâi în mod); " +
				"un click înapoi pe primul punct înainte de dublu-click închide o zonă. Nimic nu se salvează.",
		),
		// Mod urbanism (F5-T2): click pe parcelă → regim UTR. Public din F6-T2 (/api/public/regim).
		CTX.caps.regim
			? t.button(
					{
						class: () =>
							toggle_btn_cls +
							(STATE.regim_mode ? " !bg-emerald-700 !text-white !border-emerald-700" : "") +
							// Faza 2: modulul urb_regim per contract (reactiv — sesiunea/harta vin async)
							(regim_available() ? "" : " !hidden"),
						title:
							"Regim urbanistic: click pe orice punct al hărții → UTR + indicatori (POT/CUT/Rh) + fișa RLU",
						onclick: () => set_regim_mode(!STATE.regim_mode),
					},
					icon("book-open-text"),
					t.span({ class: "hidden sm:inline" }, "Regim"),
				)
			: "",
		// Editare pe hartă (F13-T4): DOAR intern + editor pe org-ul hărții. Nod stabil,
		// ascuns prin class — sesiunea se hidratează async, iar gating-ul e reactiv pe ea.
		CTX.is_public
			? ""
			: t.button(
					{
						class: () =>
							toggle_btn_cls +
							(STATE.edit_mode ? " !bg-indigo-600 !text-white !border-indigo-600" : "") +
							(can_edit_map() ? "" : " hidden"),
						title:
							"Editare: desenează elemente noi pe un strat sau modifică-le pe cele existente (doar editorii hărții; totul e versionat)",
						onclick: () => set_edit_mode(!STATE.edit_mode),
					},
					icon("pencil-simple"),
					t.span({ class: "hidden sm:inline" }, "Editare"),
				),
	)

	// Rezultatul măsurătorii + instrucțiunea. Nod STABIL, ascuns prin class (F8: nu detașa noduri
	// care poartă legături reactive) — apare doar când măsori sau când ai un rezultat pe hartă.
	const measure_panel = t.div(
		{
			class: () =>
				"absolute top-14 left-3 z-20 rounded-lg bg-white/95 dark:bg-slate-900/95 shadow border border-slate-200 dark:border-slate-700 " +
				"px-3 py-2 text-xs text-slate-700 dark:text-slate-200 " +
				(STATE.measure_mode || STATE.measure_readout ? "" : "hidden"),
		},
		t.div({ class: "font-semibold text-pink-700" }, () => STATE.measure_readout || "—"),
		t.div({ class: "text-[11px] text-slate-500 dark:text-slate-400 mt-0.5" }, () =>
			STATE.measure_mode === "draw"
				? "Click = punct · dublu-click = forma gata · click pe primul punct închide zona · Esc = ieși"
				: STATE.measure_mode === "area"
					? "Click = colț · dublu-click = gata · Esc = renunță"
					: STATE.measure_mode
						? "Click = punct · dublu-click = gata · Esc = renunță"
						: "Terminat",
		),
		t.button(
			{
				class:
					"mt-1.5 py-2 -my-2 text-[11px] font-semibold text-slate-600 dark:text-slate-300 hover:text-pink-700 cursor-pointer",
				onclick: () => {
					clear_measure()
					finish_measure()
				},
			},
			"Șterge desenul",
		),
	)

	// Panoul de însorire (F10-T4): dată + slider de oră + citirea azimut/elevație. Nod STABIL,
	// ascuns prin class (ca measure_panel) — inputurile își împing valorile în STATE prin
	// oninput, iar apply_sun() recalculează lumina la fiecare atingere.
	const sun_date_input = t.input({
		type: "date",
		class: "border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs",
		value: STATE.sun_date,
		oninput: (e: Event) => {
			STATE.sun_date = (e.target as HTMLInputElement).value
			apply_sun()
		},
	})
	const sun_hour_input = t.input({
		type: "range",
		min: "0",
		max: "24",
		step: "0.25",
		class: "w-full accent-amber-600",
		value: String(STATE.sun_hour),
		oninput: (e: Event) => {
			STATE.sun_hour = parseFloat((e.target as HTMLInputElement).value)
			apply_sun()
		},
	})
	const sun_panel = t.div(
		{
			class: () =>
				"absolute top-14 right-3 z-20 rounded-lg bg-white/95 dark:bg-slate-900/95 shadow border border-slate-200 dark:border-slate-700 " +
				"px-3 py-2 text-xs text-slate-700 dark:text-slate-200 w-64 " +
				(STATE.sun_active ? "" : "hidden"),
		},
		t.div(
			{ class: "flex items-center justify-between gap-2 mb-1" },
			t.span({ class: "font-semibold text-amber-700 dark:text-amber-300" }, "Însorire"),
			sun_date_input,
		),
		sun_hour_input,
		t.div(
			{ class: "flex items-center justify-between mt-0.5" },
			t.span({ class: "font-semibold" }, () => {
				const h = Math.floor(STATE.sun_hour)
				const m = Math.round((STATE.sun_hour - h) * 60)
				return `ora ${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
			}),
			t.span({ class: "text-slate-500 dark:text-slate-400" }, () => STATE.sun_readout),
		),
		t.div(
			{ class: "text-[11px] text-slate-400 dark:text-slate-500 mt-1" },
			"Simulare vizuală a direcției soarelui (umbrirea fațadelor) — nu calcul de umbre purtate.",
		),
	)

	// Panoul de editare (F13-T4): consola modului „Editare" — strat țintă + desen de forme noi
	// + fluxul de salvare a geometriei editate. Nod STABIL, ascuns prin class (ca measure_panel);
	// ocupă același colț top-14 (modurile se exclud, măsurarea e curățată la intrare).
	const edit_btn_cls =
		"rounded-md px-2 py-1 text-xs font-semibold border transition cursor-pointer "
	// 4.5: „Lipire la obiecte" (toggle) + „și la straturile vizibile" — construit per apel (apare
	// în ambele fluxuri ale panoului, care se re-randează reactiv).
	const snapping_controls = () =>
		t.div(
			{ class: "flex items-center gap-2 flex-wrap mb-1.5" },
			t.button(
				{
					class: () =>
						edit_btn_cls +
						"flex items-center gap-1 " +
						(STATE.edit_snapping
							? "bg-amber-500 text-white border-amber-500"
							: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-amber-400"),
					title:
						"Lipire la obiecte: la desen și la mutarea vârfurilor, cursorul se lipește de vârfurile și muchiile obiectelor din apropiere (12 px)",
					onclick: () => set_edit_snapping(!STATE.edit_snapping),
				},
				icon("magnet"),
				() => (STATE.edit_snapping ? "Lipire: pornită" : "Lipire: oprită"),
			),
			t.label(
				{
					class: () =>
						"flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 cursor-pointer " +
						(STATE.edit_snapping ? "" : "opacity-50"),
					title: "Lipește și de obiectele celorlalte straturi vizibile, nu doar de stratul editat",
				},
				t.input({
					type: "checkbox",
					checked: () => STATE.edit_snap_all,
					onchange: (e: Event) => {
						STATE.edit_snap_all = (e.target as HTMLInputElement).checked
					},
				}),
				"și la straturile vizibile",
			),
		)
	const edit_panel = t.div(
		{
			id: "edit-panel",
			class: () =>
				"absolute top-14 left-3 z-20 w-[19rem] max-w-[88vw] rounded-lg bg-white/95 dark:bg-slate-900/95 shadow-lg border border-slate-200 dark:border-slate-700 " +
				"px-3 py-2.5 text-xs text-slate-700 dark:text-slate-200 " +
				(STATE.edit_mode ? "" : "hidden"),
		},
		t.div(
			{ class: "flex items-center justify-between mb-1.5" },
			t.span(
				{
					class:
						"font-bold uppercase tracking-wider text-[10px] text-indigo-700 dark:text-indigo-300",
				},
				"Editare hartă",
			),
			t.button(
				{
					class: "p-2 -m-2 text-slate-400 hover:text-slate-700 font-bold leading-none",
					title: "Închide editarea",
					onclick: () => set_edit_mode(false),
				},
				"✕",
			),
		),
		// Conținut reactiv: fluxul de geometrie existentă SAU fluxul de element nou.
		() => {
			// ── Editarea geometriei unui element existent ──
			if (STATE.edit_geom_feature) {
				return t.div(
					t.div(
						{ class: "text-[11px] text-slate-500 dark:text-slate-400 mb-2" },
						"Editezi geometria elementului selectat: trage de puncte, ",
						"punctele de mijloc adaugă vertecși, click pe un vertex + Delete îl șterge.",
					),
					snapping_controls(),
					t.div(
						{ class: "flex items-center gap-2" },
						t.button(
							{
								class: () =>
									edit_btn_cls +
									(STATE.edit_geom_dirty && !STATE.edit_saving
										? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
										: "bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700 cursor-not-allowed"),
								disabled: () => !STATE.edit_geom_dirty || STATE.edit_saving,
								onclick: () => void save_geometry_edit(),
							},
							() => (STATE.edit_saving ? "Se salvează…" : "Salvează geometria"),
						),
						t.button(
							{
								class:
									edit_btn_cls +
									"bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-slate-500",
								onclick: () => cancel_edit_session(),
							},
							"Renunță",
						),
					),
				)
			}
			// ── Element nou: strat țintă + desen ──
			const layers = STATE.map_detail?.layers ?? []
			const modes = draw_modes_for_geometry(edit_layer_geometry())
			const mode_meta: Record<string, { icon: string; label: string }> = {
				point: { icon: "map-pin", label: "Punct" },
				linestring: { icon: "line-segments", label: "Linie" },
				polygon: { icon: "polygon", label: "Poligon" },
			}
			return t.div(
				t.div(
					{
						class: () =>
							"text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1" +
							(STATE.edit_new_layer_open ? " hidden" : ""),
					},
					"Element nou pe stratul",
				),
				t.select(
					{
						// cât formularul de strat nou e deschis, fluxul de „element nou" se ascunde —
						// un singur lucru pe ecran (constatare 27.08)
						class: () =>
							"w-full border border-slate-300 dark:border-slate-600 rounded-md px-1.5 py-1 text-xs bg-white dark:bg-slate-900 mb-2" +
							(STATE.edit_new_layer_open ? " hidden" : ""),
						onchange: (e: Event) => void select_edit_layer((e.target as HTMLSelectElement).value),
					},
					...layers.map((l) =>
						t.option(
							{ value: l.slug, selected: l.slug === STATE.edit_layer_slug },
							l.name || l.slug,
						),
					),
				),
				// F9-T5: strat nou de colectare, creat direct din teren (nume + geometrie + câmpuri)
				t.button(
					{
						class: () =>
							"text-[11px] font-semibold text-cyan-700 dark:text-cyan-300 hover:underline cursor-pointer mb-2 " +
							(STATE.edit_new_layer_open || !teren_available() ? "hidden" : ""),
						onclick: () => toggle_new_layer_form(true),
					},
					"+ Strat nou de colectare",
				),
				t.div(
					{
						class: () =>
							"mb-2 rounded-lg border border-cyan-300 dark:border-cyan-800 p-2 " +
							(STATE.edit_new_layer_open ? "" : "hidden"),
					},
					// Corpul se REconstruiește la deschidere/schimbarea nr. de rânduri; valorile stau
					// în STATE/modul, deci se restaurează. Numele stratului NU e citit aici (doar în
					// preview-ul de slug, nested) — tastarea nu re-randează formularul (fără pierdere
					// de focus).
					() => {
						if (!STATE.edit_new_layer_open) {
							return t.span({ style: "display:none" })
						}
						const geoms = [
							["Point", "Punct"],
							["LineString", "Linie"],
							["Polygon", "Poligon"],
						] as const
						const types = [
							["string", "text"],
							["number", "număr"],
							["boolean", "da / nu"],
						] as const
						const field_cls =
							"border border-slate-300 dark:border-slate-600 rounded-md px-1.5 py-1 text-xs bg-white dark:bg-slate-900"
						return t.div(
							t.div(
								{
									class: "text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1",
								},
								"Strat nou de colectare",
							),
							t.input({
								class: field_cls + " w-full mb-0.5",
								placeholder: "Numele stratului (ex. Stâlpi iluminat)",
								// valoarea din MODUL — citirea STATE aici ar re-randa formularul la
								// fiecare literă și ar pierde focusul (constatare 27.08)
								value: new_layer_name,
								oninput: (e: Event) => {
									new_layer_name = (e.target as HTMLInputElement).value
									STATE.edit_new_layer_name = new_layer_name // doar preview-ul de slug
								},
							}),
							t.div({ class: "text-[10px] text-slate-400 mb-1.5" }, () =>
								STATE.edit_new_layer_name.trim()
									? `slug: ${slugify(STATE.edit_new_layer_name)}`
									: " ",
							),
							t.div(
								{ class: "flex items-center gap-1.5 mb-1.5" },
								...geoms.map(([g, label]) =>
									t.button(
										{
											class: () =>
												edit_btn_cls +
												(STATE.edit_new_layer_geom === g
													? "bg-cyan-700 text-white border-cyan-700"
													: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-cyan-500"),
											onclick: () => (STATE.edit_new_layer_geom = g),
										},
										label,
									),
								),
							),
							t.div(
								{
									class: "text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1",
								},
								"Câmpuri (opțional)",
							),
							...Array.from({ length: STATE.edit_new_layer_rows }, (_, i) =>
								t.div(
									{ class: "flex items-center gap-1.5 mb-1" },
									t.input({
										class: field_cls + " flex-1 min-w-0",
										placeholder: `câmp ${i + 1} (ex. stare)`,
										value: new_layer_fields[i]?.name ?? "",
										oninput: (e: Event) => {
											new_layer_fields[i] = {
												name: (e.target as HTMLInputElement).value,
												type: new_layer_fields[i]?.type ?? "string",
											}
										},
									}),
									t.select(
										{
											class: field_cls,
											onchange: (e: Event) => {
												new_layer_fields[i] = {
													name: new_layer_fields[i]?.name ?? "",
													type: (e.target as HTMLSelectElement).value,
												}
											},
										},
										...types.map(([v, label]) =>
											t.option(
												{
													value: v,
													selected: (new_layer_fields[i]?.type ?? "string") === v,
												},
												label,
											),
										),
									),
								),
							),
							t.button(
								{
									class: () =>
										"text-[11px] text-slate-500 dark:text-slate-400 hover:underline cursor-pointer mb-2 " +
										(STATE.edit_new_layer_rows >= 6 ? "hidden" : ""),
									onclick: () => {
										new_layer_fields.push({ name: "", type: "string" })
										STATE.edit_new_layer_rows++
									},
								},
								"+ câmp",
							),
							() =>
								STATE.edit_new_layer_error
									? t.div({ class: "text-[11px] text-rose-600 mb-1.5" }, STATE.edit_new_layer_error)
									: t.span({ style: "display:none" }),
							t.div(
								{ class: "flex items-center gap-1.5" },
								t.button(
									{
										class:
											edit_btn_cls + "bg-cyan-700 text-white border-cyan-700 hover:bg-cyan-800",
										disabled: () => STATE.edit_new_layer_busy,
										onclick: () => void create_field_layer(),
									},
									() => (STATE.edit_new_layer_busy ? "Se creează…" : "Creează stratul"),
								),
								t.button(
									{
										class:
											edit_btn_cls +
											"bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-slate-500",
										// dezactivat cât crearea e în zbor — altfel „Renunță" reseta busy
										// și permitea un al doilea submit paralel (strat dublat)
										disabled: () => STATE.edit_new_layer_busy,
										onclick: () => toggle_new_layer_form(false),
									},
									"Renunță",
								),
							),
						)
					},
				),
				t.div(
					{
						class: () =>
							"flex items-center gap-1.5 mb-1.5 flex-wrap" +
							(STATE.edit_new_layer_open ? " hidden" : ""),
					},
					...modes.map((m) =>
						t.button(
							{
								class: () =>
									edit_btn_cls +
									"flex items-center gap-1 " +
									(STATE.edit_draw_active === m
										? "bg-indigo-600 text-white border-indigo-600"
										: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-indigo-400"),
								onclick: () => start_edit_draw(m),
							},
							icon(mode_meta[m].icon),
							mode_meta[m].label,
						),
					),
					// F9-T2: captură GPS — doar pe straturi din familia punct, doar cu geolocation
					...(modes.includes("point") && typeof navigator !== "undefined" && !!navigator.geolocation
						? [
								t.button(
									{
										class: () =>
											edit_btn_cls +
											"flex items-center gap-1 " +
											// Faza 2: modulul de teren (app_mobila) per contract
											(teren_available() ? "" : "!hidden ") +
											(STATE.edit_gps_pending
												? "bg-cyan-700 text-white border-cyan-700"
												: "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-cyan-500"),
										disabled: () => STATE.edit_gps_pending,
										onclick: () => capture_gps_point(),
									},
									() => (STATE.edit_gps_pending ? "📍 Se localizează…" : "📍 Locația mea"),
								),
							]
						: []),
				),
				t.div({ class: () => (STATE.edit_new_layer_open ? "hidden" : "") }, snapping_controls()),
				t.div(
					{
						class: () =>
							"text-[11px] text-slate-500 dark:text-slate-400" +
							(STATE.edit_new_layer_open ? " hidden" : ""),
					},
					() =>
						STATE.edit_pending
							? "Forma e gata — completează atributele și salvează."
							: STATE.edit_draw_active
								? "Click = punct · dublu-click sau Enter = forma gata · Esc = renunță"
								: "Alege o formă de desenat, sau selectează un element de pe hartă ca să-l modifici (panoul din dreapta).",
				),
				// Formularul de atribute al schiței terminate (după attribute_schema stratului).
				() => {
					if (!STATE.edit_pending || STATE.edit_new_layer_open) {
						return t.span({ style: "display:none" })
					}
					return t.div(
						{ class: "mt-2 border-t border-slate-200 dark:border-slate-700 pt-2" },
						// F9-T2: precizia raportată de GPS — informativ + avertisment peste 30 m
						() =>
							STATE.edit_gps_accuracy > 0
								? t.div(
										{
											class:
												"text-[11px] mb-1.5 " +
												(STATE.edit_gps_accuracy > 30
													? "text-amber-600 dark:text-amber-400"
													: "text-slate-500 dark:text-slate-400"),
										},
										`Precizie GPS: ±${STATE.edit_gps_accuracy} m` +
											(STATE.edit_gps_accuracy > 30 ? " — semnal slab, reîncercați afară" : ""),
									)
								: t.span({ style: "display:none" }),
						STATE.edit_schema.length === 0
							? t.div(
									{ class: "text-[11px] text-slate-400 italic mb-1.5" },
									STATE.edit_schema_loading
										? "Se încarcă schema de atribute…"
										: "Stratul nu are schemă de atribute — elementul se salvează fără atribute.",
								)
							: t.div(
									{ class: "max-h-48 overflow-y-auto space-y-1.5 mb-2 pr-1" },
									...STATE.edit_schema.map(({ key, type, values }) =>
										t.label(
											{ class: "block" },
											t.span(
												{ class: "text-[10px] font-mono text-slate-500 dark:text-slate-400" },
												key,
											),
											values
												? // 1.6: atribut cu listă de valori → select (opțiune goală = nesetat)
													t.select(
														{
															class:
																"w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900",
															onchange: (e: Event) => {
																edit_form[key] = (e.target as HTMLSelectElement).value
															},
														},
														t.option({ value: "", selected: !edit_form[key] }, "— nesetat —"),
														...values.map((v) =>
															t.option(
																{
																	value: String(v),
																	selected: String(v) === (edit_form[key] ?? ""),
																},
																String(v),
															),
														),
													)
												: t.input({
														class:
															"w-full border border-slate-300 dark:border-slate-600 rounded px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900",
														placeholder: schema_type_label(type),
														value: edit_form[key] ?? "",
														oninput: (e: Event) => {
															edit_form[key] = (e.target as HTMLInputElement).value
														},
													}),
										),
									),
								),
						t.div(
							{ class: "flex items-center gap-2" },
							t.button(
								{
									class: () =>
										edit_btn_cls +
										(STATE.edit_saving
											? "bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 cursor-not-allowed"
											: "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"),
									disabled: () => STATE.edit_saving,
									onclick: () => void save_new_feature(),
								},
								() => (STATE.edit_saving ? "Se salvează…" : "Salvează elementul"),
							),
							t.button(
								{
									class:
										edit_btn_cls +
										"bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 hover:border-slate-500",
									onclick: () => {
										DRAW?.clear()
										edit_pending_geometry = null
										edit_form = {}
										STATE.edit_pending = false
										STATE.edit_error = ""
									},
								},
								"Renunță",
							),
						),
					)
				},
			)
		},
		// Eroare/confirmare — sub ambele fluxuri.
		t.div(
			{
				class: () =>
					"mt-1.5 text-[11px] font-semibold text-red-600 " + (STATE.edit_error ? "" : "hidden"),
			},
			() => STATE.edit_error,
		),
		t.div(
			{
				class: () =>
					"mt-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 " +
					(STATE.edit_notice && !STATE.edit_error ? "" : "hidden"),
			},
			() => STATE.edit_notice,
		),
	)

	const map_container = t.div({ id: "map" })

	// Coloana centrală: harta + controalele plutitoare + bara de sesizare (public) + tabelul.
	const map_column = t.div(
		{ class: "flex-1 flex flex-col min-w-0 relative" },
		map_container,
		map_overlay,
		MapLegend(), // legenda straturilor vizibile (F13) — overlay flotant, toggle din toolbar
		// Notița tranzitorie (show_map_notice) — nod STABIL, comutat prin clasă; click = o închide.
		t.div(
			{
				id: "map-notice",
				role: "status",
				class: () =>
					"absolute bottom-14 left-1/2 -translate-x-1/2 z-30 max-w-[min(92%,34rem)] rounded-lg border " +
					"border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 px-4 py-2.5 text-xs " +
					"font-medium text-amber-900 dark:text-amber-200 shadow-lg cursor-pointer " +
					(STATE.map_notice ? "" : "hidden"),
				title: "Click pentru a închide",
				onclick: () => {
					STATE.map_notice = ""
				},
			},
			() => STATE.map_notice,
		),
		measure_panel, // rezultatul măsurătorii (ascuns când nu măsori)
		edit_panel, // consola modului de editare (F13-T4, doar intern editor+)
		sun_panel, // simularea de însorire (doar în 3D, F10-T4)
		CoordBar(), // 1.5: coordonate Stereo 70 + WGS84 la cursor (desktop)
		TicketBar(), // ascunsă intern (caps.ticket=false) sau când harta n-are primărie
		ConsultareBar(), // bannerul consultării publice PUG (doar public, F6-T6)
		PinConfirmBar(), // bara de confirmare a locației pinului (public)
		TablePanel(),
	)
	// Scrim mobil sub sidebar (z-20 < z-30): pe telefon sidebar-ul e overlay peste hartă —
	// fără scrim, harta rămânea interactivă (pan „prin" panou) și nu exista închidere evidentă.
	// Desktop: inert (md:hidden). Nod STABIL comutat prin class-fn (regula F8).
	const sidebar_scrim = t.div({
		class: () =>
			"absolute inset-0 z-20 bg-black/30 md:hidden" + (STATE.sidebar_open ? "" : " hidden"),
		onclick: () => {
			STATE.sidebar_open = false
		},
	})
	const map_area = t.div(
		{ id: "map-area" },
		sidebar_scrim,
		Sidebar(),
		map_column,
		FeaturePanel(),
		RegimPanel(),
	)

	// #map-page: intern = sub AppHeader (calc(100vh-3rem) în map.css); public = 100vh + antet propriu.
	const root = CTX.is_public
		? t.div({ id: "map-page", class: "public" }, PublicHeader(), map_area)
		: t.div({ id: "map-page" }, map_area)

	requestAnimationFrame(() => {
		// Initialise MapLibre
		MAP = new maplibregl.Map({
			container: map_container,
			style: {
				version: 8,
				// glyphs (fonturi) — OBLIGATORIU pentru `symbol`/`text-field`. demotiles servește
				// PBF valid (openmaptiles întorcea HTML → "Unimplemented type: 4"). Are "Noto Sans Regular".
				glyphs: "https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf",
				sources: {
					satelit: {
						type: "raster",
						tiles: [
							"https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
						],
						tileSize: 256,
						attribution: "Imagini: Esri, Maxar, Earthstar Geographics",
						maxzoom: 19,
					},
					osm: {
						type: "raster",
						tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
						tileSize: 256,
						attribution:
							"&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors",
						maxzoom: 19,
					},
				},
				layers: [
					{
						id: "osm-tiles",
						type: "raster",
						source: "osm",
						minzoom: 0,
						maxzoom: 22,
						layout: { visibility: STATE.basemap === "harta" ? "visible" : "none" },
					},
					{
						id: "satelit-tiles",
						type: "raster",
						source: "satelit",
						minzoom: 0,
						maxzoom: 22,
						layout: { visibility: STATE.basemap === "satelit" ? "visible" : "none" },
					},
				],
			},
			center: [25, 46],
			zoom: 6,
			// F7-T3: embed cu bbox_lock → navigarea (pan + zoom-out) rămâne în extentul configurat.
			...(CTX.is_public && CTX.bbox_lock
				? {
						maxBounds: [
							[CTX.bbox_lock[0], CTX.bbox_lock[1]],
							[CTX.bbox_lock[2], CTX.bbox_lock[3]],
						] as [[number, number], [number, number]],
					}
				: {}),
		})

		// Erorile MapLibre erau până acum COMPLET tăcute: `Evented.fire` fără ascultător pe „error"
		// scrie doar în consolă, iar clasele de defect care ne pot ascunde un strat (o ancoră de
		// ordonare inexistentă, o sursă care nu se încarcă) se manifestă EXACT ca un ErrorEvent.
		// Prefix distinctiv, ca să se poată cere primăriei „ce scrie în consolă".
		// (`capture_exception` e server-side — lib/observe nu se importă în client.)
		MAP.on("error", (e: unknown) => {
			const err = (e as { error?: unknown })?.error ?? e
			console.error("[maplibre]", err)
		})

		// Basemap dark (F13-T1): rasterul OSM nu se poate restiliza → filtru de culoare pe strat
		// (desaturare + întunecare). Reactiv pe temă; satelitul rămâne neatins (imaginile n-au temă).
		const apply_basemap_theme = () => {
			if (!MAP || !MAP.getLayer("osm-tiles")) {
				return
			}
			const dark = is_dark.val
			try {
				// Dark „citibil": desaturare moderată + inversare ușoară a luminii (fundal mai închis,
				// dar drumurile/etichetele rămân vizibile — nu noroi). Valori blânde, nu agresive.
				MAP.setPaintProperty("osm-tiles", "raster-saturation", dark ? -0.6 : 0)
				MAP.setPaintProperty("osm-tiles", "raster-brightness-min", dark ? 0.12 : 0)
				MAP.setPaintProperty("osm-tiles", "raster-brightness-max", dark ? 0.72 : 1)
				MAP.setPaintProperty("osm-tiles", "raster-contrast", dark ? -0.12 : 0)
			} catch {
				/* stilul încă se încarcă — se reaplică la load */
			}
		}
		MAP.on("load", apply_basemap_theme)
		van.derive(apply_basemap_theme)
		// Metrică „harta interactivă" (F13): timpul de la montare până la primul `idle` cu straturi
		// (bazemap + date randate) → OpenObserve prin /api/client-log. O singură dată per montare.
		const t_mount = performance.now()
		MAP.once("idle", () => {
			mark_perf("map_interactive", performance.now() - t_mount)
		})
		MAP.addControl(new maplibregl.NavigationControl(), "top-right")
		MAP.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-right")
		// Indicator de scară la zoom (REQ-BD-018) — dreapta-jos, lângă atribuire (stânga-jos e
		// ocupată de TicketBar în modul public). Se actualizează singur la fiecare zoom/pan.
		MAP.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: "metric" }), "bottom-right")

		// 1.5: bara de coordonate urmărește cursorul (rAF); pe dispozitive fără hover (touch) arată
		// centrul hărții după fiecare mișcare — nu există cursor de urmărit.
		MAP.on("mousemove", (e) => schedule_coord_update(e.lngLat.lng, e.lngLat.lat))
		const no_hover = window.matchMedia("(hover: none)").matches
		MAP.on("moveend", () => {
			if (no_hover && MAP) {
				const c = MAP.getCenter()
				schedule_coord_update(c.lng, c.lat)
			}
		})

		// Click global. Prioritate: măsurare → pin de sesizare (public) → regim (urbanism) →
		// interogare punct (identify). Toate sunt moduri EXPLICITE: cât unul e activ, clickul e al lui.
		MAP.on("click", (e) => {
			if (STATE.measure_mode) {
				add_measure_point(e.lngLat.lng, e.lngLat.lat)
				return
			}
			if (STATE.pin_active) {
				set_pin_mode(false)
				// Plasează un pin tras-abil + bară de confirmare (nu deschide formularul încă).
				place_pending_pin(e.lngLat.lng, e.lngLat.lat, "pin")
				return
			}
			if (STATE.regim_mode) {
				void run_regim(e.lngLat.lng, e.lngLat.lat)
				return
			}
			if (STATE.identify_mode) {
				run_identify({ lng: e.lngLat.lng, lat: e.lngLat.lat })
			}
		})

		// Dublu-click = gata (doubleClickZoom e dezactivat cât măsori, ca să nu sară harta).
		MAP.on("dblclick", () => {
			if (STATE.measure_mode === "draw") {
				// Desen liber: dublu-click termină FORMA, dar rămâi în mod (poți desena următoarea).
				finalize_drawn_shape()
				return
			}
			if (STATE.measure_mode) {
				finish_measure()
			}
		})

		// Esc = renunță la modul activ (măsurare/desen sau regim). Un singur listener,
		// pe viața paginii — harta se montează o dată.
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && STATE.measure_mode === "draw") {
				// Desen: Esc aruncă DOAR schița curentă și iese din mod — formele terminate rămân
				// (le șterge butonul „Șterge desenul"); altfel un Esc reflex ar pierde tot desenul.
				measure_points = []
				finish_measure()
				render_measure()
			} else if (e.key === "Escape" && STATE.measure_mode) {
				clear_measure()
				finish_measure()
			} else if (e.key === "Escape" && STATE.regim_mode) {
				set_regim_mode(false)
			}
		})

		// Bootstrap public: o singură hartă din token, fără workspace/query-params.
		if (CTX.is_public) {
			// Deep-link opțional din linkul unei sesizări: ?feature=<id> (element) SAU ?lng=&lat= (coordonată).
			const q = new URLSearchParams(location.search)
			const dl_feature = q.get("feature") ?? ""
			const dl_lng = Number(q.get("lng"))
			const dl_lat = Number(q.get("lat"))
			const has_loc =
				!!q.get("lng") && !!q.get("lat") && Number.isFinite(dl_lng) && Number.isFinite(dl_lat)
			load_map("", { fit: !has_loc && !dl_feature }).then(() => {
				if (STATE.map_detail) {
					STATE.selected_map_slug = STATE.map_detail.slug
					sync_layer_visibility(STATE.map_detail.slug)
				}
				if (dl_feature) {
					// Selectează elementul referențiat → panoul de detaliu + zbor la geometria lui.
					STATE.selected_feature_id = dl_feature
					load_feature(dl_feature).then(() => {
						if (STATE.feature_detail) {
							fly_to_geometry(STATE.feature_detail.geometry)
						}
					})
				} else if (has_loc && MAP) {
					// Zboară la locația sesizării + marchează-o cu un pin de reper (roșu).
					MAP.flyTo({ center: [dl_lng, dl_lat], zoom: 17 })
					new maplibregl.Marker({ color: "#dc2626" }).setLngLat([dl_lng, dl_lat]).addTo(MAP)
				}
			})
			return
		}

		// Load initial data after DOM is constructed
		const params = get_query_params()
		url_sync_ready = true // deep-link-ul e citit → derive-ul poate sincroniza STATE→URL de-acum

		load_maps().then(() => {
			// `?map=` din link CÂȘTIGĂ în fața workspace-ului din localStorage (un deep-link
			// #/map?map=X&feature=Y deschis pe alt workspace ignora tăcut `feature` — linkurile din
			// raportul topologic/solicitări); set_workspace se sincronizează mai jos. Apoi workspace-ul,
			// apoi prima hartă.
			// …DAR un `?map=` VECHI nu bate workspace-ul nou ales din selector: derive-ul de
			// sincronizare URL scrie `#/map?map=<harta afișată>`, selectorul din header cheamă doar
			// set_workspace(Z) și shell-ul re-montează ruta — la re-montare `params.map` e încă harta
			// VECHE; fără gardă am chema set_workspace(veche) și selectorul sărea înapoi. Euristica:
			// `?map=` e exact harta pe care o AFIȘAM (selected_map_slug supraviețuiește re-montării)
			// și workspace-ul a plecat în altă parte ⇒ parametrul e al nostru, nu un deep-link →
			// câștigă workspace-ul. Un deep-link real spre ALTĂ hartă (afișam X, vine `?map=B`) nu se
			// potrivește și rămâne prioritar; `#/map` fără parametri = workspace-ul.
			const stale_map_param =
				params.map !== "" &&
				params.map === STATE.selected_map_slug &&
				workspace.val.slug !== "" &&
				workspace.val.slug !== params.map
			const target_slug =
				(!stale_map_param && params.map) ||
				workspace.val.slug ||
				params.map ||
				(STATE.maps.length > 0 ? STATE.maps[0].slug : "")
			if (target_slug) {
				STATE.selected_map_slug = target_slug
				// Sincronizează workspace-ul global (meniul de user + selectorul îl citesc) — intern.
				if (!CTX.is_public && workspace.val.slug !== target_slug) {
					const m = STATE.maps.find((x) => x.slug === target_slug)
					set_workspace(target_slug, m?.name ?? target_slug)
				}
				// Deep link-ul ?feature= e valid doar pentru harta țintă (?map= dacă există);
				// altfel selecția veche (altă hartă / re-montare) se curăță.
				const on_target = !params.map || params.map === target_slug
				const feature_param = params.feature && on_target ? params.feature : ""
				// Deep-link la coordonată (din linkul „Locație" al unei sesizări), valid pt. harta țintă.
				const dl_lng = Number(params.lng)
				const dl_lat = Number(params.lat)
				const has_loc =
					on_target &&
					params.lng !== "" &&
					params.lat !== "" &&
					Number.isFinite(dl_lng) &&
					Number.isFinite(dl_lat)
				if (!feature_param && STATE.selected_feature_id) {
					STATE.selected_feature_id = ""
					STATE.feature_detail = null
				}
				// Zborul la parcelă NU așteaptă încărcarea layerelor (poate dura zeci de
				// secunde): feature-ul vine dintr-un singur GET și camera pleacă imediat;
				// layerele apar pe măsură ce se încarcă, iar autozoom-ul pe extent e sărit.
				if (feature_param) {
					STATE.selected_feature_id = feature_param
					load_feature(feature_param).then(() => {
						if (STATE.feature_detail) {
							fly_to_geometry(STATE.feature_detail.geometry)
						}
					})
				}
				load_map(target_slug, { fit: !feature_param && !has_loc }).then(() => {
					// After map layers are loaded, sync visibility
					if (STATE.selected_map_slug) {
						sync_layer_visibility(STATE.selected_map_slug)
					}
					// Deep-link la coordonată: zboară acolo + pin de reper (roșu).
					if (has_loc && !feature_param && MAP) {
						MAP.flyTo({ center: [dl_lng, dl_lat], zoom: 17 })
						new maplibregl.Marker({ color: "#dc2626" }).setLngLat([dl_lng, dl_lat]).addTo(MAP)
					}
				})
			}
		})
	})

	return root
}

// ─────────────────────────────────────────────────────────────
// Modul PUBLIC (embed): aceleași componente, sursă /api/public/* + caps reduse (fără edit/documente)
// ─────────────────────────────────────────────────────────────

/**
 * Meniul de user pentru viewer-ul public — ACELAȘI dropdown ca AppHeader-ul intern (avatar cu inițiale
 * + nume + ▼ → „Profilul meu" + „Ieșire"), alimentat din `session.client`. Adaptat pentru embed:
 * „Profilul meu" deschide app-ul în tab nou (embed-ul n-are router SPA). Instanțiat O SINGURĂ dată și
 * comutat prin `class` (niciodată detașat) — altfel legăturile reactive îngheață (vezi UserMenu în app).
 */
function PublicUserMenu() {
	const open = van.state(false)
	const close = () => (open.val = false)
	van.derive(() => {
		if (!session.val.authenticated) {
			close()
		}
	})
	document.addEventListener("click", (e) => {
		if (open.val && !root.contains(e.target as Node)) {
			close()
		}
	})
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape") {
			close()
		}
	})
	const item =
		"block w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
	const trigger = t.button(
		{
			class:
				"flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 " +
				"hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer flex-shrink-0",
			"aria-haspopup": "menu",
			"aria-expanded": () => String(open.val),
			onclick: (e: Event) => {
				e.stopPropagation()
				open.val = !open.val
			},
		},
		t.span(
			{
				class:
					"w-7 h-7 rounded-full bg-cyan-700 text-white flex items-center justify-center " +
					"text-[11px] font-bold flex-shrink-0",
			},
			() => user_initials(session.val.user),
		),
		t.span(
			{
				class:
					"hidden md:inline text-xs font-medium text-slate-700 dark:text-slate-200 max-w-[10rem] truncate",
			},
			() => user_label(session.val.user),
		),
		t.span({ class: "text-slate-400 dark:text-slate-500 text-[10px]" }, "▼"),
	)
	const menu = t.div(
		{
			class: () =>
				(open.val ? "block" : "hidden") +
				" absolute right-0 top-full mt-1 w-56 rounded-lg border border-slate-200 dark:border-slate-700 " +
				"bg-white dark:bg-slate-900 shadow-lg py-1 z-50",
			role: "menu",
		},
		t.div(
			{ class: "px-3 py-2 border-b border-slate-100 dark:border-slate-800" },
			t.div({ class: "text-sm font-semibold text-slate-800 dark:text-slate-200 truncate" }, () =>
				user_label(session.val.user),
			),
			t.div(
				{ class: "text-xs text-slate-500 dark:text-slate-400 truncate" },
				() => session.val.user?.email ?? "",
			),
		),
		// Embed: deschide app-ul în tab nou (nu există router SPA aici).
		t.a(
			{
				href: `${location.origin}/#/profil`,
				target: "_blank",
				rel: "noopener",
				class: item,
				role: "menuitem",
				onclick: close,
			},
			"Profilul meu",
		),
		t.button(
			{
				class: item + " text-slate-600 dark:text-slate-300",
				role: "menuitem",
				onclick: async () => {
					close()
					await logout() // sesiunea devine anonimă → meniul se ascunde (gating pe session)
				},
			},
			"Ieșire",
		),
	)
	const root = t.div({ class: "relative flex-shrink-0" }, trigger, menu)
	return root
}

/** Antet public (embed): brand (logo + „Urbanism") + hartă/primărie + CTA sesizare + meniu user. */
function PublicHeader() {
	// Meniul de user e instanțiat O SINGURĂ dată și comutat prin `class` (F8-safe, ca în AppHeader).
	const user_menu = PublicUserMenu()
	return t.div(
		{
			class:
				"flex-shrink-0 flex items-center gap-3 px-4 h-12 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm z-40",
		},
		// Brand (F6-T1): stema + numele PRIMĂRIEI când harta are stemă (attributes.stema_url),
		// altfel brandul platformei. onerror ascunde imaginea stricată (URL mort) — rămâne numele.
		t.div(
			{ class: "flex items-center gap-1.5 flex-shrink-0" },
			t.img({
				alt: "",
				class: "w-6 h-6 object-contain",
				src: () => STATE.org_stema || "/public/logo-192.png",
				onerror: (e: Event) => {
					;(e.target as HTMLImageElement).style.display = "none"
				},
			}),
			t.span(
				{
					class:
						"text-cyan-700 dark:text-cyan-300 font-extrabold text-base tracking-tight truncate max-w-24 sm:max-w-48",
				},
				() => (STATE.org_stema && STATE.org_name ? STATE.org_name : "Urbanism"),
			),
			t.span(
				{
					class:
						"hidden sm:inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-950 text-amber-800 dark:text-amber-300 " +
						"px-1.5 py-0.5 text-[9px] font-bold tracking-wide",
				},
				`v${pkg.version}`,
			),
		),
		// Contextul hărții: numele hărții + primăria (omisă când e deja în brand — nu o dublăm).
		t.div(
			{ class: "min-w-0 flex-1 border-l border-slate-200 dark:border-slate-700 pl-3" },
			t.div(
				{ class: "text-sm font-bold text-slate-800 dark:text-slate-200 truncate leading-tight" },
				() => (STATE.map_detail?.config.name as string) ?? "Hartă publică",
			),
			t.div(
				{ class: "text-[11px] text-slate-500 dark:text-slate-400 truncate leading-tight" },
				() => (STATE.org_stema ? "" : STATE.org_name || ""),
			),
		),
		// GDPR: portalul public e locul unde cetățeanul lasă date personale (sesizări) → linkul
		// către politica de confidențialitate e mereu vizibil în antet (audit 09.09, §D).
		t.a(
			{
				href: "/confidentialitate",
				target: "_blank",
				rel: "noopener",
				class:
					"flex-shrink-0 text-xs text-slate-500 dark:text-slate-400 hover:text-cyan-700 transition",
				title: "Politica de confidențialitate",
			},
			"Confidențialitate",
		),
		// Legal (LEGAL_CHECKLIST.md D2): termenii + declarația de accesibilitate (OUG 112/2018 art. 6).
		// Pe mobil rămân accesibile din pagina de confidențialitate (paginile legale se leagă între ele).
		t.a(
			{
				href: "/termeni",
				target: "_blank",
				rel: "noopener",
				class:
					"hidden sm:inline flex-shrink-0 text-xs text-slate-500 dark:text-slate-400 hover:text-cyan-700 transition",
				title: "Termeni și condiții",
			},
			"Termeni",
		),
		t.a(
			{
				href: "/accesibilitate",
				target: "_blank",
				rel: "noopener",
				class:
					"hidden sm:inline flex-shrink-0 text-xs text-slate-500 dark:text-slate-400 hover:text-cyan-700 transition",
				title: "Declarația de accesibilitate",
			},
			"Accesibilitate",
		),
		// Instalarea pe dispozitiv: pe ANDROID → aplicația oficială din Google Play (TWA,
		// publicată 31.08); altfel → promptul PWA, vizibil doar când browserul îl oferă
		// (beforeinstallprompt). Nu depinde de autentificare. Ambele montate STATIC (regula Van).
		t.a(
			{
				href: PLAY_STORE_URL,
				target: "_blank",
				rel: "noopener",
				class:
					"flex-shrink-0 text-xs font-semibold text-slate-500 dark:text-slate-400 " +
					"hover:text-cyan-700 transition" +
					(is_android() && !is_standalone() ? "" : " hidden"),
				title: "Descarcă aplicația „Urbanism by VEGO” din Google Play",
			},
			"📱 Aplicația",
		),
		t.button(
			{
				class: () =>
					"flex-shrink-0 text-xs font-semibold text-slate-500 dark:text-slate-400 " +
					"hover:text-cyan-700 transition cursor-pointer" +
					(!is_android() && install_available.val ? "" : " hidden"),
				title: "Instalează aplicația pe acest dispozitiv",
				onclick: prompt_install,
			},
			"⤓ Instalează",
		),
		// Ajutor (F6-T7): ghidul cetățeanului din /docs — deep-link prin ?s= (hash-routerul ocupă #).
		t.a(
			{
				href: `${location.origin}/#/docs?s=cetatean`,
				target: "_blank",
				rel: "noopener",
				class:
					// mobil: ascuns (ghidul rămâne accesibil din formularul de solicitare) — titlul
					// hărții are nevoie de fiecare pixel la 390px
					"hidden sm:inline flex-shrink-0 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-cyan-700 transition",
				title: "Ghid de utilizare a portalului public (se deschide în filă nouă)",
			},
			"Ajutor",
		),
		// CTA de sesizare (dacă harta e a unei primării).
		() =>
			STATE.ticket_available
				? t.button(
						{
							class:
								"flex-shrink-0 bg-cyan-700 hover:bg-cyan-800 text-white rounded-lg px-3 py-1.5 " +
								"text-xs font-semibold transition cursor-pointer",
							onclick: () => open_ticket({}),
							title: "Fă o solicitare",
						},
						"✎ ",
						// mobil: doar creionul (butonul plin strivea titlul hărții la 390px)
						t.span({ class: "hidden sm:inline" }, "Fă o solicitare"),
					)
				: t.span({ style: "display:none" }),
		// Meniu user — vizibil DOAR dacă vizitatorul e deja autentificat (comutat prin class, nu detașat).
		t.div({ class: () => (session.val.authenticated ? "flex-shrink-0" : "hidden") }, user_menu),
	)
}

/** Bara plutitoare de sesizare (public): CTA general + pin + GPS. Ascunsă intern / fără primărie. */
function TicketBar() {
	const btn_cls =
		"bg-cyan-700 hover:bg-cyan-800 text-white rounded-lg px-3.5 py-2 text-xs font-semibold shadow " +
		"cursor-pointer transition"
	const pin_btn = t.button(
		{
			class: () =>
				(STATE.pin_active ? "bg-amber-600 hover:bg-amber-700" : "bg-cyan-700 hover:bg-cyan-800") +
				" text-white rounded-lg px-3.5 py-2 text-xs font-semibold shadow cursor-pointer transition",
			onclick: () => set_pin_mode(!STATE.pin_active),
		},
		() => (STATE.pin_active ? "📍 Atinge harta…" : "📍 Sesizare cu punct"),
	)
	const kids: HTMLElement[] = [
		t.button({ class: btn_cls, onclick: () => open_ticket({}) }, "✎ Fă o solicitare"),
		pin_btn,
	]
	if (typeof navigator !== "undefined" && navigator.geolocation) {
		const gps_btn = t.button(
			{
				class: btn_cls,
				onclick: () => {
					// GPS pornit FĂRĂ mod pin activ = flux nou de sesizare → fără preset moștenit;
					// pornit ÎN TIMPUL unui pin de consultare = doar altă sursă de locație (preset păstrat).
					if (!STATE.pin_active) {
						pin_extra = {}
					}
					gps_btn.disabled = true
					gps_btn.textContent = "📍 Se localizează…"
					navigator.geolocation.getCurrentPosition(
						(pos) => {
							gps_btn.disabled = false
							gps_btn.textContent = "📍 La locația mea"
							set_pin_mode(false)
							// Zboară la locație + plasează pinul tras-abil (persoana poate ajusta înainte de trimitere).
							MAP?.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 17 })
							place_pending_pin(pos.coords.longitude, pos.coords.latitude, "gps")
						},
						() => {
							gps_btn.disabled = false
							gps_btn.textContent = "📍 Locația indisponibilă"
						},
						{ enableHighAccuracy: true, timeout: 10000 },
					)
				},
			},
			"📍 La locația mea",
		) as HTMLButtonElement
		kids.push(gps_btn)
	}
	return t.div(
		{
			// bottom cu safe-area (indicatorul home iOS); right-24 pe mobil ține butoanele departe
			// de ScaleControl/atribuirea din dreapta-jos (desktop: fără limită, ca înainte).
			class:
				"absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-3 right-24 md:right-auto " +
				"z-20 flex gap-2 flex-wrap",
			// Ascunsă cât timp un pin e în așteptare de confirmare (atunci apare PinConfirmBar).
			style: () =>
				`display:${CTX.caps.ticket && STATE.ticket_available && !STATE.pending_pin ? "flex" : "none"}`,
		},
		...kids,
	)
}

// ─────────────────────────────────────────────────────────────
// Consultare publică PUG preliminar (F6-T6, REQ-PEX-008): straturile cu `consultare: true` apar în
// portalul public cu un banner + formular de observații (reuse solicitări, tip_act pre-setat).
// Fail-closed by design: în modul public `map_detail.layers` conține DOAR straturile PUBLICATE —
// un strat în consultare dar nepublicat nu ajunge niciodată în banner.
// ─────────────────────────────────────────────────────────────

/** Consultarea stratului e activă? (flag + termenul, comparat pe data LOCALĂ; gol = fără termen). */
function consultare_active(l: LayerSummary): boolean {
	if (l.consultare !== true) {
		return false
	}
	const term = l.consultare_pana_la ?? ""
	if (!term) {
		return true
	}
	const now = new Date()
	const today =
		`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-` +
		String(now.getDate()).padStart(2, "0")
	return today <= term
}

/** „YYYY-MM-DD" → „DD.MM.YYYY" (formatul uzual RO); input neașteptat trece nemodificat. */
function format_date_ro(iso: string): string {
	const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/)
	return m ? `${m[3]}.${m[2]}.${m[1]}` : iso
}

/** Bannerul consultării publice (doar în /embed) — nod STABIL, ascuns prin display (F8).
 *  Pot rula CONCOMITENT mai multe consultări → fiecare rând are propriile acțiuni, iar observația
 *  pleacă legată de consultarea țintă: subiect pre-completat (editabil) + categoria
 *  `consultare:<slug>` (mașinal-filtrabilă). Varianta „cu locație" trece prin pinul tras-abil
 *  existent (pin_extra poartă preset-ul până la confirmare). */
function ConsultareBar() {
	const observatie_preset = (l: LayerSummary) => ({
		tip_act: "observatie_consultare",
		subject: `Observație — ${l.name || l.slug}`,
		category: `consultare:${l.slug}`,
	})
	const body = () => {
		const active = (STATE.map_detail?.layers ?? []).filter(consultare_active)
		return t.div(
			{ class: "flex flex-col gap-2" },
			t.div(
				{
					class:
						"text-[11px] font-bold uppercase tracking-wide text-violet-700 dark:text-violet-300",
				},
				"Consultare publică — PUG preliminar",
			),
			...active.map((l) =>
				t.div(
					{ class: "flex flex-wrap items-center gap-x-3 gap-y-1" },
					t.div(
						{ class: "text-xs text-violet-900 dark:text-violet-200" },
						t.span({ class: "font-semibold" }, l.name || l.slug),
						l.consultare_pana_la
							? ` — până la ${format_date_ro(l.consultare_pana_la)}`
							: " — în consultare",
					),
					STATE.ticket_available
						? t.div(
								{ class: "flex items-center gap-1.5" },
								t.button(
									{
										class:
											"bg-violet-700 hover:bg-violet-800 text-white rounded-lg px-2.5 py-1 " +
											"text-[11px] font-semibold cursor-pointer transition",
										title:
											"Trimite o observație la această consultare (necesită cont; formular pre-completat)",
										onclick: () => open_ticket(observatie_preset(l)),
									},
									"✍ Trimite o observație",
								),
								t.button(
									{
										class:
											"border border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900 rounded-lg " +
											"px-2.5 py-1 text-[11px] font-semibold cursor-pointer transition",
										title:
											"Alege întâi locația pe hartă (pin tras-abil) — observația pleacă cu coordonata",
										onclick: () => {
											set_pin_mode(true) // întâi comutarea (resetează pin_extra)…
											pin_extra = observatie_preset(l) // …apoi preset-ul consultării
										},
									},
									"📍 cu locație pe hartă",
								),
							)
						: "",
				),
			),
		)
	}
	// Card expandat (cu buton de minimizare) + cip compact — noduri STABILE comutate prin class
	// (F8). Feedback testare 24.08: bannerul acoperea permanent centrul hărții pe telefon.
	const expanded = t.div(
		{
			class: () =>
				"relative rounded-xl bg-violet-50/95 dark:bg-violet-950/95 border border-violet-300 dark:border-violet-800 shadow px-4 py-2.5 pr-8" +
				(STATE.consultare_collapsed ? " hidden" : ""),
		},
		t.button(
			{
				class:
					"absolute top-1.5 right-2.5 p-2 -m-2 text-violet-400 dark:text-violet-500 hover:text-violet-700 " +
					"dark:hover:text-violet-300 text-xs font-bold leading-none cursor-pointer",
				title: "Minimizează bannerul (rămâne un cip pe hartă)",
				onclick: () => {
					STATE.consultare_collapsed = true
				},
			},
			"–",
		),
		body,
	)
	const chip = t.button(
		{
			class: () =>
				"rounded-full bg-violet-50/95 dark:bg-violet-950/95 border border-violet-300 dark:border-violet-800 shadow " +
				"px-3 py-1.5 text-[11px] font-bold text-violet-700 dark:text-violet-300 cursor-pointer whitespace-nowrap" +
				(STATE.consultare_collapsed ? "" : " hidden"),
			title: "Consultare publică în desfășurare — apasă pentru detalii și observații",
			onclick: () => {
				STATE.consultare_collapsed = false
			},
		},
		"📢 Consultare publică",
	)
	return t.div(
		{
			class: "absolute top-14 left-1/2 -translate-x-1/2 z-20 max-w-[92vw] md:max-w-md",
			// Auto-ascundere cât e activ ORICE mod care folosește harta — refolosim sursa unică
			// map_click_consumed() (acoperă și identify; o listă scrisă de mână aici ar drifta,
			// constatare review 24.08) + faza de confirmare a pinului (pin_active se stinge
			// ÎNAINTE de place_pending_pin, altfel bannerul ar reapărea fix peste pinul tras-abil).
			style: () =>
				`display:${
					CTX.is_public &&
					(STATE.map_detail?.layers ?? []).some(consultare_active) &&
					!map_click_consumed() &&
					!STATE.pending_pin
						? "block"
						: "none"
				}`,
		},
		expanded,
		chip,
	)
}

/** Bara de confirmare a locației: apare când un pin tras-abil e plasat (pin/GPS). */
function PinConfirmBar() {
	return t.div(
		{
			class:
				"absolute bottom-[max(1rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2 " +
				"bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 px-4 py-3 max-w-[92vw]",
			style: () => `display:${STATE.pending_pin ? "flex" : "none"}`,
		},
		t.div(
			{ class: "text-xs text-slate-600 dark:text-slate-300 text-center" },
			"📍 Trage pinul ca să ajustezi locația exactă, apoi confirmă.",
		),
		t.div(
			{ class: "flex gap-2" },
			t.button(
				{
					class:
						"bg-cyan-700 hover:bg-cyan-800 text-white rounded-lg px-4 py-1.5 text-xs font-semibold " +
						"cursor-pointer transition",
					onclick: confirm_pin,
				},
				"Confirmă și continuă",
			),
			t.button(
				{
					class:
						"border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg px-3 py-1.5 " +
						"text-xs font-medium cursor-pointer transition",
					onclick: cancel_pin,
				},
				"Anulează",
			),
		),
	)
}

/**
 * Montează harta în MOD PUBLIC (embed): ACELEAȘI componente ca back-office-ul, cu sursa de date pe
 * /api/public/* (token) și capabilities reduse — lipsesc DOAR editarea și generarea de documente.
 * Restul (straturi vizibil/tabel, toolbar, căutare, identify, detaliu, istoric, as-of) e identic.
 */
export function mount_public_map(
	token: string,
	view?: {
		tools?: Partial<Record<string, boolean>>
		bbox_lock?: [number, number, number, number] | null
	},
): HTMLElement {
	CTX.is_public = true
	CTX.token = token
	// regim: true (F6-T2) — sursa comută pe /api/public/regim, fail-closed pe server (strat UTR
	// publicat + allow-list F10); pe hărți fără regim public butonul răspunde cu mesajul serverului.
	// F7-T3: instrumentele embed-ului (absent = pornit — permisiv pe răspunsuri vechi; serverul e
	// poarta reală) se aplică AICI, înainte de montare — CTX.caps nu e reactiv, deci trebuie să fie
	// complet când se construiește toolbar-ul.
	const tool_ok = (name: string) => view?.tools?.[name] !== false
	CTX.caps = {
		documents: false,
		workspace: false,
		as_of: true,
		search: tool_ok("search"),
		ticket: tool_ok("sesizari"),
		regim: tool_ok("regim"),
		measure: tool_ok("measure"),
		legend: tool_ok("legend"),
		basemap: tool_ok("basemap"),
	}
	CTX.bbox_lock = view?.bbox_lock ?? null
	// Auth OPȚIONAL (best-effort): hidratează sesiunea din cookie. SameSite=Lax → în iframe cross-origin
	// NU se trimite cookie-ul (rămâne anonim — corect GDPR); la deschidere directă apare meniul de user.
	void load_session()
	return MapPage()
}
